import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, QueryList, TemplateRef, ViewChildren } from '@angular/core';
import Chart, { ChartDataSets } from 'chart.js';
import { MatDialog } from '@angular/material/dialog';
import moment from 'moment-timezone';
import { Timezone } from 'src/app/shared/model/timezone';
import { resolveDeviceTimezone } from 'src/app/shared/helper/device-timezone';
import { ActivatedRoute, Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, Observable, Subscription } from 'rxjs';
import { UserConstant } from 'src/app/shared/constants/user-constant';
import { FormatterHelper } from 'src/app/shared/helper/formatter-helper';
import { LineChartColors } from 'src/app/shared/model/chart';
import {
  AnalyticsMetric,
  AnalyticsMetricConfig,
  AnalyticsTableColumn,
  AnalyticsValueFormat,
  TOP_DEVICES_METRIC_CONFIG,
  TOP_DEVICES_METRICS
} from 'src/app/shared/model/top-devices-analytics';
import { ApService } from 'src/app/shared/services/ap.service';
import { VceVhubService } from 'src/app/shared/services/vce-vhub.service';
import {
  AddPeerModel,
  AssignedIpLine,
  BackupRow,
  CLIENT_DEVICE_TYPE_OPTIONS,
  ConfirmDeleteModel,
  ConnectionRow,
  DISTRIBUTION_MODE_OPTIONS,
  DistributionMode,
  DPC_IP_ASSIGNMENT_OPTIONS,
  DpcIpAssignment,
  DpcModel,
  DpcServiceRow,
  FEC_LANE_MODE_OPTIONS,
  InterfaceRow,
  OverlayPlusLane,
  OverlayPlusLegacyEntry,
  OverlayPlusLink,
  PATH_MANAGEMENT_MODE_OPTIONS,
  PathManagementMode,
  PeerDetailModel,
  PeerFecLaneModel,
  PeerLinkModel,
  PeerTableRow,
  QfecLaneModel,
  RestoreImportModel,
  TRANSPORT_PROTOCOL_OPTIONS,
  TransportProtocol,
  SummaryDetailRow
} from 'src/app/shared/model/vce-vhub';

type ApplyProcessStepKey = 'collect' | 'validate' | 'authorize' | 'vceProvision' | 'assertOk' | 'saveDevice';
type ApplyProcessStepStatus = 'pending' | 'active' | 'completed' | 'failed' | 'waiting';

interface ApplyProcessStep {
  key: ApplyProcessStepKey;
  title: string;
  description: string;
  status: ApplyProcessStepStatus;
  error?: string;
}

interface ApplyProcessContext {
  type: 'addPeer' | 'editPeer';
  vhubMac: string;
  peerMac: string;
  vceMac?: string;
  team?: string;
  dpc?: string;
  peerSavedOnVhub?: boolean;
  submitPayload: any;
  peerPayload?: any;
  existingVceProvision?: any;
  updateExistingVceProvision?: boolean;
  result?: any;
  responses?: { [key: string]: any };
}

@Component({
  selector: 'app-device-vce-vhub-overlay-plus',
  templateUrl: './device-vce-vhub-overlay-plus.component.html',
  styleUrls: ['./device-vce-vhub-overlay-plus.component.scss']
})
export class DeviceVceVhubOverlayPlusComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChildren('laneAnalyticsCanvas') laneAnalyticsCanvases: QueryList<ElementRef<HTMLCanvasElement>>;
  @ViewChildren('topDevicesCanvas') topDevicesCanvases: QueryList<ElementRef<HTMLCanvasElement>>;

  vHubMode = false;
  vCEMode = false;
  @Input() role: any;
  selectedOverlayPlusTab = 0;
  selectedVceOverlayPlusTab = 0;
  selectedLaneAnalyticsRange = 0;
  laneAnalyticsTimezones = Timezone;
  selectedLaneAnalyticsTimezone = resolveDeviceTimezone(null);
  laneAnalyticsTimezoneSearch = '';

  onLaneAnalyticsTimezoneChange(timezone: string) {
    if (!moment.tz.zone(timezone)) return;
    this.selectedLaneAnalyticsTimezone = timezone;
    this.buildLaneAnalyticsCharts();
  }

  selectedDeviceAnalyticsView: 'device' | 'topDevices' = 'device';
  selectedLaneAnalyticsDevice = '';
  laneAnalyticsDeviceSearchText = '';
  laneAnalyticsDevicePickerOpen = false;
  laneAnalyticsDeviceOptions: Array<{ value: string, label: string }> = [];
  laneAnalyticsRanges = [
    { label: 'Last 4 Hrs', key: 'last4Hrs', range: 'last4hrs' },
    { label: 'Last 24 Hrs', key: 'last24Hrs', range: 'last24hrs' },
    { label: 'Last 7 Days', key: 'last7Days', range: 'last7days' },
    { label: 'This Month', key: 'thisMonth', range: 'thismonth' },
    { label: 'Last Month', key: 'lastMonth', range: 'lastmonth' }
  ];
  laneAnalyticsMetrics = [
    { label: 'TX Packets', valueKey: 'txPackets', apiKeys: ['tx_packets', 'TX Packets'], axis: 'packets' },
    { label: 'TX Bytes', valueKey: 'txBytes', apiKeys: ['tx_bytes', 'TX Bytes'], axis: 'bytes' },
    { label: 'RX Packets', valueKey: 'rxPackets', apiKeys: ['rx_packets', 'RX Packets'], axis: 'packets' },
    { label: 'RX Bytes', valueKey: 'rxBytes', apiKeys: ['rx_bytes', 'RX Bytes'], axis: 'bytes' },
    { label: 'TX Mbps', valueKey: 'txMbps', apiKeys: ['tx_bytes', 'TX Bytes'], axis: 'Mbps', rateFromBytes: true },
    { label: 'RX Mbps', valueKey: 'rxMbps', apiKeys: ['rx_bytes', 'RX Bytes'], axis: 'Mbps', rateFromBytes: true },
    { label: 'Mbps', valueKey: 'totalMbps', apiKeys: [], axis: 'Mbps', combinedTraffic: true, rateFromBytes: true },
    { label: 'Bytes', valueKey: 'totalBytes', apiKeys: [], axis: 'bytes', combinedTraffic: true },
    { label: 'Loss %', valueKey: 'lossPct', apiKeys: ['loss_pct', 'Average Loss %'], axis: '%' },
    { label: 'Avg Latency', valueKey: 'decodeLatencyAvg', apiKeys: ['decode_latency_avg_ms', 'Average Decode Latency'], axis: 'ms' },
    { label: 'Latency', valueKey: 'decodeLatencyP90', apiKeys: ['decode_latency_p90_ms', 'Max Decode Latency P90'], axis: 'ms' },
    { label: 'Jitter', valueKey: 'jitter', apiKeys: ['jitter_ms', 'Average Jitter'], axis: 'ms' },
    { label: 'MOS', valueKey: 'mos', apiKeys: ['mos', 'Average MOS'], axis: 'MOS' }
  ];
  laneAnalyticsData: any = null;
  laneAnalyticsLoading = false;
  laneAnalyticsError = '';
  laneAnalyticsChartLabels: any[] = [];
  laneAnalyticsChartDataByMetric: { [key: string]: ChartDataSets[] } = {};
  laneAnalyticsGraphs: any[] = [];
  laneAnalyticsRows: any[] = [];
  laneAnalyticsTableLinkRowsByPeer: { [peer: string]: any[] } = {};
  private laneAnalyticsSubscription: Subscription | null = null;
  laneAnalyticsDeviceMacs: string[] = [];
  laneAnalyticsDevicesLoading = false;
  laneAnalyticsDevicesError = '';
  private laneAnalyticsDevicesSubscription: Subscription | null = null;
  private analyticsQuerySubscription: Subscription | null = null;
  selectedTopDevicesRange = 0;
  topDevicesRanges = [
    { label: 'Last 4 Hrs', range: 'last4hrs' },
    { label: 'Last 24 Hrs', range: 'last24hrs' },
    { label: 'Last 7 Days', range: 'last7days' },
    { label: 'This Month', range: 'thismonth' },
    { label: 'Last Month', range: 'lastmonth' },
    { label: 'Custom', range: '' }
  ];
  topDevicesFrom = '';
  topDevicesTo = '';
  topDevicesTopN = 5;
  selectedTopDevicesMetric: AnalyticsMetric = 'bandwidth';
  topDevicesMetricOptions = TOP_DEVICES_METRICS;
  topDevicesSort = TOP_DEVICES_METRIC_CONFIG.bandwidth.defaultSort;
  topDevicesOrder: 'asc' | 'desc' = 'desc';
  topDevicesSearchText = '';
  topDevicesAppliedSearch = '';
  topDevicesVceSearchText = '';
  topDevicesVcePickerOpen = false;
  topDevicesPageSize = 10;
  topDevicesSkip = 0;
  topDevicesData: any = null;
  topDevicesLoading = false;
  topDevicesError = '';
  topDevicesGraphs: any[] = [];
  private topDevicesSubscription: Subscription | null = null;
  private laneAnalyticsChartInstances: Chart[] = [];
  private topDevicesChartInstances: Chart[] = [];
  private chartCanvasSubscriptions: Subscription[] = [];
  private chartRenderTimer: any = null;
  laneAnalyticsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      yAxes: [{
        display: true,
        scaleLabel: { display: true, labelString: 'Mbps' },
        ticks: {
          beginAtZero: true
        }
      }]
    }
  };
  laneAnalyticsChartColors = LineChartColors.map(color => ({
    ...color, lineTension: 0.35, cubicInterpolationMode: 'monotone'
  }));
  laneAnalyticsChartPlugins = [];

  openLaneAnalyticsChart(graph: any, template: TemplateRef<any>) {
    const labels = graph.fullLabels || graph.labels || [];
    const indices = this.getLaneAnalyticsDisplayIndices(labels.length, true);
    this.dialog.open(template, {
      width: '95vw', maxWidth: '1500px', maxHeight: '95vh',
      data: {
        ...graph,
        labels: indices.map(index => labels[index]),
        data: (graph.fullData || graph.data || []).map((series, index) => ({
          ...this.laneAnalyticsChartColors[index % this.laneAnalyticsChartColors.length],
          ...series,
          hidden: !!graph.data?.[index]?.hidden,
          data: indices.map(pointIndex => (series.data || [])[pointIndex])
        }))
      },
      ariaLabel: graph.title + ' chart'
    });
  }

  topDevicesRankingChartColors = [
    { borderColor: '#c8631f', backgroundColor: 'rgba(228, 126, 48, 0.78)', borderWidth: 1 },
    { borderColor: '#e8914f', backgroundColor: 'rgba(232, 145, 79, 0.68)', borderWidth: 1 },
    { borderColor: '#a94a12', backgroundColor: 'rgba(169, 74, 18, 0.68)', borderWidth: 1 },
    { borderColor: '#f0aa70', backgroundColor: 'rgba(240, 170, 112, 0.68)', borderWidth: 1 },
    { borderColor: '#7e3710', backgroundColor: 'rgba(126, 55, 16, 0.62)', borderWidth: 1 },
    { borderColor: '#f3bd8f', backgroundColor: 'rgba(243, 189, 143, 0.68)', borderWidth: 1 }
  ];
  topDevicesRankingChartPlugins = [{
    afterDatasetsDraw: (chart: any) => {
      const context = chart?.ctx;
      if (!context) {
        return;
      }

      context.save();
      context.fillStyle = '#374151';
      context.font = '600 11px Arial, sans-serif';
      context.textAlign = 'left';
      context.textBaseline = 'middle';

      (chart.data?.datasets || []).forEach((dataset: any, datasetIndex: number) => {
        const metadata = chart.getDatasetMeta(datasetIndex);
        if (metadata?.hidden) {
          return;
        }

        (metadata?.data || []).forEach((bar: any, valueIndex: number) => {
          const value = this.toNullableTopDevicesNumber(dataset.data?.[valueIndex]);
          const model = bar?._model;
          if (value == null || !model) {
            return;
          }

          const label = this.formatTopDevicesChartValue(value, dataset.valueUnit || '');
          context.fillText(label, model.x + 8, model.y);
        });
      });

      context.restore();
    }
  }];
  laneAnalyticsChartLegend = true;
  laneAnalyticsChartType = 'line';
  showPsk = false;
  overlayPlusTeam: any = null;
  interfaceRow: InterfaceRow | null = null;
  interfaceRows: InterfaceRow[] = [];
  connectionRows: ConnectionRow[] = [];
  dpcServiceRows: DpcServiceRow[] = [];
  tableRows: PeerTableRow[] = [];
  currentDevice: any = null;
  currentDeviceMac = '';
  currentDeviceLocation = '';
  currentDeviceRegion = '';
  dpcDialogMode: 'add' | 'edit' = 'add';
  dpcDialogTemplateRef: TemplateRef<any> | null = null;
  confirmDeleteDialogTemplateRef: TemplateRef<any> | null = null;
  peerDialogTemplateRef: TemplateRef<any> | null = null;
  editingDpcIndex = -1;
  editingPeerId = '';
  editingPeerData: any = null;
  addPeerProvisioningReady = false;
  addPeerUiBlock = '';
  addPeerCliScript = '';
  peerProvisioningTitle = '';
  peerProvisioningUiBlock = '';
  peerProvisioningCliScript = '';
  copiedProvisioningTarget = '';
  confirmDeleteModel: ConfirmDeleteModel = {
    type: '',
    title: '',
    message: '',
    targetName: '',
    row: null,
    index: -1
  };
  applyConfirmModel = {
    title: 'Configuration Saved',
    message: 'Configuration Saved - Would You Like to Apply and Activate on the Device?',
    savedText: 'No = Config saved and you can apply it later',
    applyText: 'Yes = Immediately send to device and apply'
  };
  pendingApplyActivation: ApplyProcessContext | null = null;
  applyProcessSteps: ApplyProcessStep[] = this.getDefaultApplyProcessSteps();
  applyProcessContext: ApplyProcessContext | null = null;
  applyProcessBusy = false;
  applyProcessComplete = false;
  newDpcModel: DpcModel = {
    name: 'dpc1',
    listenAddr: '0.0.0.0:3121',
    forwardAddr: '127.0.0.1:4021',
    serverUrl: 'vhub5-east-ca.rabbitrun.ca:3122',
    dpcIp: '10.0.43.254/24',
    tunnelIp: 'auto',
    mtu: 1442,
    maxPacketSize: 1365,
    fecRatios: '1:3,2:4,2:1,10:5,20:10,3:1,30:10,5:1,6:1',
    fecTimeout: 100,
    sdwanLink: 'link6',
    qosRate: '800mbit',
    authRequired: true,
    enabled: true
  };
  clientDeviceTypeOptions = CLIENT_DEVICE_TYPE_OPTIONS;
  dpcIpAssignmentOptions = DPC_IP_ASSIGNMENT_OPTIONS;
  pathManagementModeOptions = PATH_MANAGEMENT_MODE_OPTIONS;
  distributionModeOptions = DISTRIBUTION_MODE_OPTIONS;
  transportProtocolOptions = TRANSPORT_PROTOCOL_OPTIONS;
  fecLaneModeOptions = FEC_LANE_MODE_OPTIONS;
  addPeerPlaceholders = {
    clientMac: 'fc:5e:3c:4c:75:26',
    clientHostname: 'RRT300-Site1',
    dpcIp: '10.0.43.5/24',
    wanId: '100001',
    wanSubnet: '192.168.100.0/24',
    dmzIp: '169.69.68.80/32',
    dmzSubnets: '169.69.68.84/30',
    link1: 'link1',
    link2: 'link2',
    link3: '[not set]',
    link4: '[not set]',
    routeTable1: 'wan',
    routeTable2: '4glte',
    routeTable3: '',
    routeTable4: '',
    weightTx1: '100',
    weightTx2: '0',
    weightRx1: '100',
    weightRx2: '0',
    weightTx3: '0',
    weightTx4: '0',
    weightRx3: '0',
    weightRx4: '0',
    failoverTimeout1: '3200',
    failoverTimeout2: '90000',
    failoverTimeout3: '15000',
    failoverTimeout4: '15000',
    flapcheck1: '30000',
    flapcheck2: '0',
    flapcheck3: '0',
    flapcheck4: '0',
    maxPacketSize: '1420',
    rseqHoldMs: '40',
    rseqThinkAcks: '6',
    maxblocks: '256',
    fecGroupHold: '0',
    fecGroupHoldDepth: '',
    defaultLaneOrigDupes: '10:0'
  };
  addPeerModel: AddPeerModel = {
    clientDeviceType: this.clientDeviceTypeOptions[0].value,
    clientMac: '',
    clientHostname: '',
    dpcService: '',
    dpcIpAssignment: DpcIpAssignment.AutoPool,
    dpcIp: '',
    psk: '',
    wanId: '',
    wanSubnet: '',
    dmzIp: '',
    dmzSubnets: '',
    mode: PathManagementMode.RouteTables,
    link1: '',
    link2: '',
    link3: '',
    link4: '',
    routeTable1: '',
    routeTable2: '',
    routeTable3: '',
    routeTable4: '',
    weightTx1: 100,
    weightTx2: 0,
    weightRx1: 100,
    weightRx2: 0,
    weightTx3: null,
    weightTx4: null,
    weightRx3: null,
    weightRx4: null,
    failoverTimeout1: 3200,
    failoverTimeout2: 45000,
    failoverTimeout3: null,
    failoverTimeout4: null,
    flapcheck1: 30000,
    flapcheck2: 0,
    flapcheck3: null,
    flapcheck4: null,
    transport1: TransportProtocol.Udp,
    transport2: TransportProtocol.Udp,
    transport3: TransportProtocol.Udp,
    transport4: TransportProtocol.Udp,
    distribution: DistributionMode.PerPacket,
    maxPacketSize: 1420,
    rseqHoldMs: 40,
    rseqThinkAcks: 6,
    maxblocks: 256,
    fecGroupHold: '0',
    fecGroupHoldDepth: '',
    defaultLaneOrigDupes: '10:0'
  };
  qfecLanes: QfecLaneModel[] = [];
  linkSelectOptions = ['link1', 'link2', 'linkB1', 'MyFi2G', 'MyFi5G'];
  openLinkPickerKey = '';
  customLinkValues: { [key: string]: string } = {};
  showPeerPsk = false;
  peerPskLoading = false;
  peerDetailModel: PeerDetailModel = {
    clientDeviceType: '',
    provisioned: '',
    mac: '',
    hostname: '',
    dpcAllowed: '',
    encrypt: '',
    psk: '',
    dpcIp: '',
    wanId: '',
    wanSubnet: '',
    dmzIps: '',
    dmzSubnets: '',
    mode: '',
    distribution: '',
    maxPacketSize: null,
    rseqHoldMs: null,
    rseqThinkAcks: null,
    fecMaxBlocks: null,
    fecGroupHold: null,
    fecGroupHoldDepth: '',
    defaultFec: '',
    links: [
      {
        label: 'Link 1 (Primary)', name: '[not set]', transport: '', path: '',
        tx: null, rx: null, timeout: null, flapcheck: null
      },
      {
        label: 'Link 2 (Backup)', name: '[not set]', transport: '', path: '',
        tx: null, rx: null, timeout: null, flapcheck: null
      },
      { label: 'Link 3 (Optional)', name: '[not set]', transport: '', path: '', tx: null, rx: null, timeout: null, flapcheck: null },
      { label: 'Link 4 (Optional)', name: '[not set]', transport: '', path: '', tx: null, rx: null, timeout: null, flapcheck: null }
    ]
  };
  peerFecLanes: PeerFecLaneModel[] = [];
  vceAddProvisioningBlock = [
    '# RRT-Midlay Provisioning',
    'REMOTE_ADDR=vhub.example.com:3122',
    'DPC_GATEWAY=10.0.43.254',
    'DPC_IP=10.0.43.5/24',
    'ENCRYPT=on',
    'PSK=...',
    'MTU=1460',
    'KEEPALIVE=10',
    'FEC=auto'
  ].join('\n');
  vceAddProvisioningError = '';
  vceEditModel: any = {
    title: 'team1',
    readOnly: {},
    mode: '',
    distribution: '',
    maxPacketSize: null,
    rseqHoldMs: null,
    rseqThinkAcks: null,
    fecMaxBlocks: null,
    fecGroupHold: null,
    fecGroupHoldDepth: '',
    defaultFec: '',
    links: [],
    fecLanes: []
  };
  backupRows: BackupRow[] = [
    { file: 'authorized_peers-backup-20260527-185119.json', timestamp: '2026-05-27 18:51:19 UTC' },
    { file: 'authorized_peers-backup-20260527-183305.json', timestamp: '2026-05-27 18:33:05 UTC' },
    { file: 'authorized_peers-backup-20260527-183119.json', timestamp: '2026-05-27 18:31:19 UTC' },
    { file: 'authorized_peers-backup-20260526-172609.json', timestamp: '2026-05-26 17:26:09 UTC' }
  ];
  restoreImportModel: RestoreImportModel = {
    importMode: 'Merge -- add / update peers',
    pasteJson: [
      '{',
      '  "peers": [',
      '    { "...": "..." }',
      '  ]',
      '}'
    ].join('\n'),
    newRemoteAddr: 'vhub5-east-ca.rabbitrun.ca:3122',
    authorizedPeersJson: [
      '{',
      '  "peers": [',
      '    {',
      '      "mac": "f8:5e:3c:0e:f9:96",',
      '      "hostname": "team1",',
      '      "client_model": "RRT200",',
      '      "dpc_allowed": "dpc5",',
      '      "encrypt": "on",',
      '      "psk": "...",',
      '      "dpc_ip": "auto",',
      '      "wan_id": "100001",',
      '      "wan_subnet": "192.168.105.0/24",',
      '      "remote_addr": "vhub1-east-usa.rabbitrun.ca:3121",',
      '      "dpc_gateway": "10.0.141.254",',
      '      "mtu": "1420",',
      '      "keepalive": "10",',
      '      "vce_provision": {',
      '        "link_names": "link1,link2",',
      '        "qfec": "15:1"',
      '      }',
      '    }',
      '  ]',
      '}'
    ].join('\n')
  };
  clientDeviceLookupRows: any[] = [];
  clientDeviceLookupLoaded = false;
  addPeerSelectionLoading = false;
  addPeerSelectionError = '';
  clientMacLookupOpen = false;
  clientMacLookupIndex = -1;
  clientMacSearchText = '';
  readonly clientMacSearchDebounceMs = 400;
  private clientMacSearchDebounceTimer: any = null;
  private clientDeviceLookupRequestId = 0;
  addPeerSubmitted = false;
  addPeerSaveLoading = false;
  dpcSubmitted = false;
  dpcCrudProcess: 'add' | 'edit' | 'delete' | '' = '';
  peerCrudProcess: 'add' | 'edit' | 'delete' | '' = '';
  overlayPlusRefreshing = false;
  overlayPlusCrudLoading = false;
  private associatedVhubPeers: any[] = [];
  private associatedVhubMac = '';

  @Input() set device(obj: any | any[]) {
    const device = Array.isArray(obj) ? obj[0] : obj;

    if (!device) {
      this.resetView();
      return;
    }

    if (!this.currentDevice || this.currentDeviceMac.toLowerCase() !== String(device?.mac || '').toLowerCase()) {
      this.selectedLaneAnalyticsTimezone = resolveDeviceTimezone(device?.statistics?.timezone);
      this.laneAnalyticsTimezoneSearch = '';
      this.associatedVhubPeers = [];
      this.associatedVhubMac = '';
    }
    this.currentDeviceMac = device?.mac || '';
    this.currentDevice = device;
    this.currentDeviceLocation = this.getDevicePrimaryLocation(device);
    this.currentDeviceRegion = this.getDeviceRegionKey(device);
    this.vHubMode = device.templateConfig?.team?.config?.team1?.mode === 'VHUB';
    this.vCEMode = device.templateConfig?.team?.config?.team1?.mode === 'VCE';
    this.sortClientDeviceLookupRowsByLocation();
    this.buildOverlayPlusView(device);
    this.refreshLaneAnalyticsDeviceOptions();
    this.clientDeviceLookupLoaded = false;
    if (this.vHubMode) {
      this.loadClientDeviceLookup();
    } else {
      this.clearClientDeviceLookup();
      if (this.vCEMode) this.loadAssociatedVhubPeers();
    }
  }

  private loadAssociatedVhubPeers() {
    const mac = this.currentDeviceMac.toLowerCase();
    if (!mac || this.associatedVhubMac === mac) return;
    this.associatedVhubMac = mac;
    this.vceVhubService.getAssociatedMidlayVhubPeers(mac).subscribe(res => {
      if (this.currentDeviceMac.toLowerCase() !== mac || !this.vCEMode) return;
      this.associatedVhubPeers = Array.isArray(res?.peers) ? res.peers : [];
      this.buildOverlayPlusView(this.currentDevice);
    }, () => {
      if (this.currentDeviceMac.toLowerCase() === mac) this.associatedVhubMac = '';
    });
  }

  constructor(
    public dialog: MatDialog,
    private vceVhubService: VceVhubService,
    private apService: ApService,
    private toastr: ToastrService,
    private route?: ActivatedRoute,
    private router?: Router
  ) {
  }

  ngOnInit(): void {
    this.loadClientDeviceLookup();
    this.analyticsQuerySubscription = this.route?.queryParamMap?.subscribe(params => {
      const view = params.get('analyticsView');
      const deviceMac = params.get('analyticsDevice');
      if (view === 'top-devices' || view === 'device') {
        this.selectedDeviceAnalyticsView = view === 'top-devices' ? 'topDevices' : 'device';
      }
      if (deviceMac) {
        this.selectedLaneAnalyticsDevice = this.normalizeLaneAnalyticsDeviceOptionValue(deviceMac);
        this.laneAnalyticsDeviceSearchText = this.getSelectedLaneAnalyticsDeviceLabel();
      }
    }) || null;
  }

  ngAfterViewInit(): void {
    if (this.laneAnalyticsCanvases) {
      this.chartCanvasSubscriptions.push(
        this.laneAnalyticsCanvases.changes.subscribe(() => this.renderLaneAnalyticsCharts())
      );
    }
    if (this.topDevicesCanvases) {
      this.chartCanvasSubscriptions.push(
        this.topDevicesCanvases.changes.subscribe(() => this.renderTopDevicesCharts())
      );
    }
    this.scheduleAnalyticsChartRender();
  }

  ngOnDestroy(): void {
    this.clearClientMacSearchDebounce();
    this.cancelLaneAnalyticsRequest();
    this.cancelLaneAnalyticsDevicesRequest();
    this.cancelTopDevicesRequest();
    this.analyticsQuerySubscription?.unsubscribe();
    this.chartCanvasSubscriptions.forEach(subscription => subscription.unsubscribe());
    this.chartCanvasSubscriptions = [];
    this.clearScheduledAnalyticsChartRender();
    this.destroyChartInstances(this.laneAnalyticsChartInstances);
    this.destroyChartInstances(this.topDevicesChartInstances);
  }

  resetView() {
    this.clearClientMacSearchDebounce();
    this.clientDeviceLookupRequestId += 1;
    this.clientMacSearchText = '';
    this.clientMacLookupOpen = false;
    this.clientMacLookupIndex = -1;
    this.overlayPlusTeam = null;
    this.interfaceRow = null;
    this.interfaceRows = [];
    this.connectionRows = [];
    this.dpcServiceRows = [];
    this.tableRows = [];
    this.currentDevice = null;
    this.currentDeviceMac = '';
    this.currentDeviceLocation = '';
    this.currentDeviceRegion = '';
    this.addPeerSelectionLoading = false;
    this.addPeerSelectionError = '';
    this.selectedOverlayPlusTab = 0;
    this.selectedVceOverlayPlusTab = 0;
    this.selectedDeviceAnalyticsView = 'device';
    this.selectedLaneAnalyticsDevice = '';
    this.laneAnalyticsDeviceSearchText = '';
    this.laneAnalyticsDevicePickerOpen = false;
    this.dpcDialogTemplateRef = null;
    this.confirmDeleteDialogTemplateRef = null;
    this.peerDialogTemplateRef = null;
    this.addPeerProvisioningReady = false;
    this.addPeerSaveLoading = false;
    this.dpcCrudProcess = '';
    this.peerCrudProcess = '';
    this.overlayPlusCrudLoading = false;
    this.addPeerUiBlock = '';
    this.addPeerCliScript = '';
    this.peerProvisioningTitle = '';
    this.peerProvisioningUiBlock = '';
    this.peerProvisioningCliScript = '';
    this.laneAnalyticsData = null;
    this.laneAnalyticsLoading = false;
    this.laneAnalyticsError = '';
    this.laneAnalyticsChartLabels = [];
    this.laneAnalyticsChartDataByMetric = {};
    this.laneAnalyticsGraphs = [];
    this.laneAnalyticsRows = [];
    this.laneAnalyticsTableLinkRowsByPeer = {};
    this.laneAnalyticsDeviceOptions = [];
    this.laneAnalyticsDeviceMacs = [];
    this.laneAnalyticsDevicesError = '';
    this.topDevicesData = null;
    this.topDevicesGraphs = [];
    this.topDevicesError = '';
    this.topDevicesSkip = 0;
    this.topDevicesSearchText = '';
    this.topDevicesAppliedSearch = '';
    this.topDevicesVceSearchText = '';
    this.topDevicesVcePickerOpen = false;
  }

  loadClientDeviceLookup(searchText = '', force = false) {
    if (!this.vHubMode || (!force && this.clientDeviceLookupLoaded) || !this.currentDeviceMac) {
      return;
    }

    const requestId = ++this.clientDeviceLookupRequestId;
    this.clientDeviceLookupLoaded = true;
    this.addPeerSelectionLoading = true;
    this.addPeerSelectionError = '';
    this.vceVhubService.getMidlaySelectionOptions(undefined, this.currentDeviceMac, searchText).subscribe(res => {
      if (requestId !== this.clientDeviceLookupRequestId) {
        return;
      }
      this.clientDeviceLookupRows = Array.isArray(res?.vces) ? res.vces : [];
      this.addPeerSelectionLoading = false;
      this.sortClientDeviceLookupRowsByLocation();
      this.refreshLaneAnalyticsDeviceOptions();
      if (this.topDevicesData) {
        this.buildTopDevicesCharts();
      }
      this.clientMacLookupOpen = !!this.clientDeviceLookupRows.length;
      this.onClientMacLookupChange();
    }, error => {
      if (requestId !== this.clientDeviceLookupRequestId) {
        return;
      }
      this.clientDeviceLookupLoaded = false;
      this.addPeerSelectionLoading = false;
      this.addPeerSelectionError = this.getApplyErrorMessage(error);
    });
  }

  private clearClientDeviceLookup() {
    this.clearClientMacSearchDebounce();
    this.clientDeviceLookupRequestId += 1;
    this.clientDeviceLookupRows = [];
    this.clientDeviceLookupLoaded = false;
    this.addPeerSelectionLoading = false;
    this.addPeerSelectionError = '';
    this.clientMacLookupOpen = false;
    this.clientMacLookupIndex = -1;
  }

  refreshOverlayPlusTable() {
    if (this.overlayPlusRefreshing || !this.currentDevice) {
      return;
    }

    const deviceId = this.currentDevice?._id || this.currentDevice?.id;
    this.overlayPlusRefreshing = true;

    if (this.vHubMode && this.currentDeviceMac) {
      this.loadLiveVhubState();
      return;
    }

    if (deviceId) {
      this.apService.getdeviceById(deviceId).subscribe(res => {
        this.applyRefreshedOverlayPlusDevice(this.getDeviceFromRefreshResponse(res));
      }, () => {
        this.overlayPlusRefreshing = false;
      });
      return;
    }

    this.refreshOverlayPlusTableFromListing();
  }

  refreshOverlayPlusTableFromListing() {
    const orgId = this.currentDevice?.orgId || this.getCurrentUserOrganizationId();
    const mac = String(this.currentDeviceMac || '').toLowerCase();
    if (!orgId || !mac) {
      this.overlayPlusRefreshing = false;
      return;
    }

    this.apService.getListing({
      params: {
        id: orgId,
        type: 'overlay'
      }
    }).subscribe(res => {
      const devices = Array.isArray(res?.data) ? res.data : [];
      const device = devices.find((row: any) => String(row?.mac || '').toLowerCase() === mac);
      this.applyRefreshedOverlayPlusDevice(device);
    }, () => {
      this.overlayPlusRefreshing = false;
    });
  }

  getDeviceFromRefreshResponse(res: any) {
    if (Array.isArray(res?.data)) {
      return res.data[0];
    }

    return res?.data || res;
  }

  loadLiveVhubState() {
    if (!this.vHubMode || !this.currentDeviceMac) {
      return;
    }

    this.vceVhubService.getMidlayVhubState(this.currentDeviceMac).subscribe(res => {
      this.applyRefreshedVhubState(res);
    }, () => {
      this.overlayPlusRefreshing = false;
    });
  }

  applyRefreshedVhubState(res: any) {
    const currentMidlay = this.currentDevice?.statistics?.rrt_midlay || {};
    const state = res?.state || {};
    const configuredPeers = state?.peers?.peers || state?.peers || [];
    const configuredDpcs = state?.dpc?.dpcs || state?.dpc || [];
    const currentPeers = this.getPeers(currentMidlay);
    const currentPeersByMac = new Map<string, any>();
    currentPeers.forEach(peer => {
      currentPeersByMac.set(String(peer?.mac || '').toLowerCase(), peer);
    });
    const peers = (Array.isArray(configuredPeers) ? configuredPeers : []).map(peer => ({
      ...(currentPeersByMac.get(String(peer?.mac || '').toLowerCase()) || {}),
      ...peer
    }));
    const dpcs = Array.isArray(configuredDpcs) && configuredDpcs.length
      ? configuredDpcs
      : this.getDpcs(currentMidlay);
    const device = {
      ...this.currentDevice,
      statistics: {
        ...this.currentDevice?.statistics,
        rrt_midlay: { dpcs, peers }
      }
    };

    this.applyRefreshedOverlayPlusDevice(device);
  }

  applyRefreshedOverlayPlusDevice(device: any) {
    if (!device) {
      this.overlayPlusRefreshing = false;
      return;
    }

    this.currentDevice = device;
    this.currentDeviceMac = device?.mac || this.currentDeviceMac;
    this.currentDeviceLocation = this.getDevicePrimaryLocation(device);
    this.currentDeviceRegion = this.getDeviceRegionKey(device);
    this.vHubMode = device.templateConfig?.team?.config?.team1?.mode === 'VHUB';
    this.vCEMode = device.templateConfig?.team?.config?.team1?.mode === 'VCE';
    this.sortClientDeviceLookupRowsByLocation();
    this.buildOverlayPlusView(device);
    this.refreshLaneAnalyticsDeviceOptions();
    this.overlayPlusRefreshing = false;
    if (this.selectedOverlayPlusTab === 2) {
      this.loadLaneAnalyticsDevices();
      if (this.selectedDeviceAnalyticsView === 'topDevices') {
        this.loadTopDevices();
      } else {
        this.loadLaneAnalytics();
      }
    }
  }

  onOverlayPlusTabChange(index: number) {
    this.selectedOverlayPlusTab = index;
    if (index === 2) {
      this.loadLaneAnalyticsDevices();
      if (this.selectedDeviceAnalyticsView === 'topDevices') {
        this.loadTopDevices();
      } else {
        this.loadLaneAnalytics();
      }
    }
  }

  onDeviceAnalyticsViewChange(view: 'device' | 'topDevices') {
    this.selectedDeviceAnalyticsView = view;
    this.updateAnalyticsQueryParams(view === 'topDevices' ? 'top-devices' : 'device');
    if (view === 'topDevices') {
      if (!this.topDevicesData) {
        this.loadTopDevices();
      }
      return;
    }
    this.loadLaneAnalyticsDevices();
    if (this.isLaneAnalyticsDeviceSelected() && !this.laneAnalyticsData) {
      this.loadLaneAnalytics();
    }
  }

  onVceOverlayPlusTabChange(index: number) {
    this.selectedVceOverlayPlusTab = index;
    if (index === 1) {
      this.loadLaneAnalytics();
    }
  }

  onLaneAnalyticsRangeChange(index: number) {
    this.selectedLaneAnalyticsRange = index;
    this.loadLaneAnalytics();
  }

  onLaneAnalyticsDeviceChange(value: string) {
    const selectedDevice = this.normalizeLaneAnalyticsDeviceOptionValue(value);
    if (selectedDevice === this.getNormalizedLaneAnalyticsDeviceFilter() && this.laneAnalyticsData) {
      this.laneAnalyticsDevicePickerOpen = false;
      return;
    }

    this.cancelLaneAnalyticsRequest();
    this.selectedLaneAnalyticsDevice = selectedDevice;
    this.laneAnalyticsDeviceSearchText = this.getSelectedLaneAnalyticsDeviceLabel();
    this.laneAnalyticsDevicePickerOpen = false;
    this.clearLaneAnalyticsResults();
    this.refreshLaneAnalyticsDeviceOptions();
    this.updateAnalyticsQueryParams('device', selectedDevice || null);
    this.loadLaneAnalytics();
  }

  onLaneAnalyticsDeviceSearchFocus() {
    this.laneAnalyticsDevicePickerOpen = true;
  }

  onLaneAnalyticsDeviceSearchBlur() {
    setTimeout(() => {
      this.laneAnalyticsDevicePickerOpen = false;
      if (this.isLaneAnalyticsDeviceSelected()) {
        this.laneAnalyticsDeviceSearchText = this.getSelectedLaneAnalyticsDeviceLabel();
      }
    }, 150);
  }

  onLaneAnalyticsDeviceSearchInput() {
    this.laneAnalyticsDevicePickerOpen = true;
    if (this.isLaneAnalyticsDeviceSelected() &&
      this.laneAnalyticsDeviceSearchText !== this.getSelectedLaneAnalyticsDeviceLabel()) {
      this.cancelLaneAnalyticsRequest();
      this.selectedLaneAnalyticsDevice = '';
      this.clearLaneAnalyticsResults();
    }
  }

  selectLaneAnalyticsDeviceOption(option: { value: string, label: string }) {
    this.onLaneAnalyticsDeviceChange(option?.value || '');
  }

  loadLaneAnalytics() {
    const deviceId = this.currentDevice?._id || this.currentDevice?.id;
    const range = this.getSelectedLaneAnalyticsRange();
    const type = this.getLaneAnalyticsType();
    if (!deviceId || !range || (this.vHubMode && !this.isLaneAnalyticsDeviceSelected())) {
      this.clearLaneAnalyticsResults();
      return;
    }

    this.cancelLaneAnalyticsRequest();
    this.laneAnalyticsLoading = true;
    this.laneAnalyticsError = '';
    const mac = this.vHubMode ? this.getNormalizedLaneAnalyticsDeviceFilter() : undefined;
    this.laneAnalyticsSubscription = this.vceVhubService.getMidlayLaneAnalytics(deviceId, range, type, mac).subscribe(res => {
      this.laneAnalyticsData = res?.data || res || null;
      this.buildLaneAnalyticsCharts();
      this.laneAnalyticsLoading = false;
      this.laneAnalyticsSubscription = null;
    }, () => {
      this.clearLaneAnalyticsResults(false);
      this.laneAnalyticsError = 'Unable to load device analytics.';
      this.laneAnalyticsLoading = false;
      this.laneAnalyticsSubscription = null;
    });
  }

  retryLaneAnalytics() {
    this.loadLaneAnalytics();
  }

  cancelLaneAnalyticsRequest() {
    if (this.laneAnalyticsSubscription) {
      this.laneAnalyticsSubscription.unsubscribe();
      this.laneAnalyticsSubscription = null;
    }
    this.laneAnalyticsLoading = false;
  }

  loadLaneAnalyticsDevices() {
    const deviceId = this.currentDevice?._id || this.currentDevice?.id;
    if (!this.vHubMode || !deviceId || this.laneAnalyticsDevicesLoading) {
      return;
    }
    this.cancelLaneAnalyticsDevicesRequest();
    this.laneAnalyticsDevicesLoading = true;
    this.laneAnalyticsDevicesError = '';
    this.laneAnalyticsDevicesSubscription = this.vceVhubService.getMidlayAnalyticsDevices(deviceId).subscribe(macs => {
      this.laneAnalyticsDeviceMacs = (Array.isArray(macs) ? macs : [])
        .map(mac => this.normalizeLaneAnalyticsDeviceOptionValue(mac))
        .filter((mac, index, items) => !!mac && items.indexOf(mac) === index);
      this.refreshLaneAnalyticsDeviceOptions();
      this.laneAnalyticsDevicesLoading = false;
      this.laneAnalyticsDevicesSubscription = null;
    }, () => {
      this.laneAnalyticsDevicesError = 'Unable to load the device list.';
      this.laneAnalyticsDevicesLoading = false;
      this.laneAnalyticsDevicesSubscription = null;
    });
  }

  cancelLaneAnalyticsDevicesRequest() {
    this.laneAnalyticsDevicesSubscription?.unsubscribe();
    this.laneAnalyticsDevicesSubscription = null;
    this.laneAnalyticsDevicesLoading = false;
  }

  onTopDevicesRangeChange(index: number) {
    this.selectedTopDevicesRange = index;
    this.topDevicesSkip = 0;
    this.loadTopDevices();
  }

  onTopDevicesFilterChange() {
    this.topDevicesSkip = 0;
    this.loadTopDevices();
  }

  onTopDevicesMetricChange() {
    const config = this.getTopDevicesMetricConfig();
    this.topDevicesSort = config.defaultSort;
    this.topDevicesOrder = config.defaultOrder;
    this.topDevicesSkip = 0;
    this.loadTopDevices();
  }

  selectTopDevicesMetric(metric: AnalyticsMetric) {
    if (metric === this.selectedTopDevicesMetric) {
      return;
    }
    this.selectedTopDevicesMetric = metric;
    this.onTopDevicesMetricChange();
  }

  getTopDevicesMetricConfig(): AnalyticsMetricConfig {
    return TOP_DEVICES_METRIC_CONFIG[this.selectedTopDevicesMetric]
      || TOP_DEVICES_METRIC_CONFIG.bandwidth;
  }

  get topDevicesSortOptions() {
    return this.getTopDevicesMetricConfig().sortOptions;
  }

  getTopDevicesTableColumns(): AnalyticsTableColumn[] {
    return this.getTopDevicesMetricConfig().tableColumns;
  }

  onTopDevicesVceSearchFocus() {
    this.topDevicesVcePickerOpen = true;
  }

  onTopDevicesVceSearchBlur() {
    setTimeout(() => {
      this.topDevicesVcePickerOpen = false;
      this.topDevicesVceSearchText = this.getSelectedTopDevicesVce()?.label || '';
    }, 150);
  }

  onTopDevicesVceSearchInput() {
    this.topDevicesVcePickerOpen = true;
  }

  selectTopDevicesRegisteredVce(device?: { mac: string, name: string, label: string }) {
    const mac = String(device?.mac || '').trim();
    if (mac === this.topDevicesAppliedSearch) {
      this.topDevicesVceSearchText = device?.label || '';
      this.topDevicesVcePickerOpen = false;
      return;
    }

    this.topDevicesSearchText = mac;
    this.topDevicesAppliedSearch = mac;
    this.topDevicesVceSearchText = device?.label || '';
    this.topDevicesVcePickerOpen = false;
    this.onTopDevicesFilterChange();
  }

  getTopDevicesRegisteredVces() {
    return this.tableRows
      .filter(row => row?.showPeerSummary && row?.peerData && !row.peerData.local)
      .map(row => row.peerData)
      .reduce((devices, peer) => {
        const mac = String(peer?.mac || '').trim();
        const normalizedMac = this.normalizeLaneAnalyticsDeviceOptionValue(mac);
        if (!normalizedMac || devices.some(device =>
          this.normalizeLaneAnalyticsDeviceOptionValue(device.mac) === normalizedMac)) {
          return devices;
        }

        const name = this.getFirstValue(peer?.hostname, peer?.name, peer?.server_name);
        return [
          ...devices,
          {
            mac,
            name: name || mac,
            label: name ? `${name} (${mac})` : mac
          }
        ];
      }, [] as Array<{ mac: string, name: string, label: string }>)
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  getFilteredTopDevicesRegisteredVces() {
    const selected = this.getSelectedTopDevicesVce();
    const input = String(this.topDevicesVceSearchText || '').trim().toLowerCase();
    const search = selected && input === selected.label.toLowerCase() ? '' : input;
    return this.getTopDevicesRegisteredVces().filter(device => !search ||
      device.label.toLowerCase().indexOf(search) > -1 ||
      device.mac.toLowerCase().indexOf(search) > -1);
  }

  getSelectedTopDevicesVce() {
    const selectedMac = this.normalizeLaneAnalyticsDeviceOptionValue(this.topDevicesAppliedSearch);
    return this.getTopDevicesRegisteredVces().find(device =>
      this.normalizeLaneAnalyticsDeviceOptionValue(device.mac) === selectedMac) || null;
  }

  trackByTopDevicesRegisteredVce(index: number, device: any) {
    return device?.mac || index;
  }

  loadTopDevices() {
    const deviceId = this.currentDevice?._id || this.currentDevice?.id;
    const range = this.topDevicesRanges[this.selectedTopDevicesRange]?.range;
    const customRangeReady = !!this.topDevicesFrom && !!this.topDevicesTo;
    if (!this.vHubMode || !deviceId || (!range && !customRangeReady)) {
      return;
    }
    this.cancelTopDevicesRequest();
    this.topDevicesLoading = true;
    this.topDevicesError = '';
    this.topDevicesData = null;
    this.topDevicesGraphs = [];
    this.topDevicesSubscription = this.vceVhubService.getMidlayTopDevices(deviceId, {
      metric: this.selectedTopDevicesMetric,
      range,
      from: range ? '' : this.toTopDevicesIsoDate(this.topDevicesFrom),
      to: range ? '' : this.toTopDevicesIsoDate(this.topDevicesTo),
      top: this.topDevicesTopN,
      sort: this.topDevicesSort,
      order: this.topDevicesOrder,
      search: this.topDevicesAppliedSearch,
      limit: this.topDevicesPageSize,
      skip: this.topDevicesSkip
    }).subscribe(res => {
      this.topDevicesData = this.filterTopDevicesDataForSelectedVce(res?.data || res || null);
      this.buildTopDevicesCharts();
      this.topDevicesLoading = false;
      this.topDevicesSubscription = null;
    }, () => {
      this.topDevicesData = null;
      this.topDevicesGraphs = [];
      this.topDevicesError = 'Unable to load Top Devices analytics.';
      this.topDevicesLoading = false;
      this.topDevicesSubscription = null;
    });
  }

  cancelTopDevicesRequest() {
    this.topDevicesSubscription?.unsubscribe();
    this.topDevicesSubscription = null;
    this.topDevicesLoading = false;
  }

  retryTopDevices() {
    this.loadTopDevices();
  }

  filterTopDevicesDataForSelectedVce(data: any) {
    const selectedDevice = this.getSelectedTopDevicesVce();
    if (!data || !selectedDevice) {
      return data;
    }

    const selectedMac = this.normalizeLaneAnalyticsDeviceOptionValue(selectedDevice.mac);
    const matchesSelectedMac = (item: any) =>
      this.normalizeLaneAnalyticsDeviceOptionValue(item?.mac || item?.peerMac) === selectedMac;
    const filterItems = (items: any) => (Array.isArray(items) ? items : []).filter(matchesSelectedMac);
    let primaryRanking = filterItems(data.metric_rankings?.primary);
    let secondaryRanking = filterItems(data.metric_rankings?.secondary);
    const deviceSeries = filterItems(data.metric_device_series);
    const tableItems = filterItems(data.table?.items);
    const selectedSummary = tableItems[0] || primaryRanking[0] || secondaryRanking[0] || deviceSeries[0] || null;
    const metricConfig = this.getTopDevicesMetricConfig();
    if (!primaryRanking.length && selectedSummary) {
      primaryRanking = [{
        ...selectedSummary,
        value: selectedSummary?.[metricConfig.primaryField] ?? selectedSummary?.primary_value,
        unit: metricConfig.unit
      }];
    }
    if (!secondaryRanking.length && selectedSummary && metricConfig.secondaryField) {
      secondaryRanking = [{
        ...selectedSummary,
        value: selectedSummary?.[metricConfig.secondaryField],
        unit: metricConfig.secondaryUnit || metricConfig.unit
      }];
    }
    const selectedPoints = Array.isArray(deviceSeries[0]?.points) ? deviceSeries[0].points : [];
    const metricTimeBuckets = selectedPoints.map((point, index) =>
      point?.timestamp || point?.bucket || data.metric_time_buckets?.[index]).filter(Boolean);
    const metricTimeline = selectedPoints.map((point, index) => ({
      ...point,
      timestamp: point?.timestamp || point?.bucket || data.metric_time_buckets?.[index]
    })).filter(point => !!point.timestamp);

    return {
      ...data,
      metric_kpis: this.getTopDevicesSelectedVceKpis(data.metric_kpis, selectedSummary),
      metric_rankings: {
        ...(data.metric_rankings || {}),
        primary: primaryRanking,
        secondary: secondaryRanking
      },
      metric_time_buckets: metricTimeBuckets,
      metric_timeline: metricTimeline,
      metric_device_series: deviceSeries,
      rankings: data.rankings ? Object.keys(data.rankings).reduce((rankings, key) => ({
        ...rankings,
        [key]: filterItems(data.rankings[key])
      }), {}) : data.rankings,
      top_devices: filterItems(data.top_devices),
      device_series: filterItems(data.device_series),
      table: {
        ...(data.table || {}),
        total: tableItems.length,
        skip: 0,
        items: tableItems.map((item, index) => ({ ...item, rank: index + 1 }))
      }
    };
  }

  getTopDevicesSelectedVceKpis(kpis: any, device: any) {
    const valuesByKey = {
      total_devices: device ? 1 : 0,
      total_bytes: device?.total_bytes,
      tx_bytes: device?.tx_bytes,
      rx_bytes: device?.rx_bytes,
      tx_packets: device?.tx_packets,
      rx_packets: device?.rx_packets,
      avg_bandwidth_mbps: device?.avg_bandwidth_mbps,
      peak_bandwidth_mbps: device?.peak_bandwidth_mbps,
      avg_loss_pct: device?.loss_pct,
      max_loss_pct: device?.loss_pct,
      devices_with_loss: this.toNullableTopDevicesNumber(device?.loss_pct) > 0 ? 1 : 0,
      avg_mos: device?.mos,
      min_mos: device?.mos,
      max_mos: device?.mos,
      avg_jitter_ms: device?.jitter_ms,
      min_jitter_ms: device?.jitter_ms,
      max_jitter_ms: device?.jitter_ms,
      avg_latency_ms: device?.decode_latency_avg_ms,
      max_latency_ms: device?.decode_latency_avg_ms,
      p90_latency_ms: device?.decode_latency_p90_ms
    };

    return (Array.isArray(kpis) ? kpis : []).map(kpi => ({
      ...kpi,
      value: Object.prototype.hasOwnProperty.call(valuesByKey, kpi?.key)
        ? valuesByKey[kpi.key]
        : null
    }));
  }

  buildTopDevicesCharts() {
    const data = this.topDevicesData || {};
    const config = this.getTopDevicesMetricConfig();
    const rankingGraph = (title: string, items: any[], valueKey: string, axis: string) => {
      const rankingItems = items || [];
      return {
        title,
        type: 'horizontalBar',
        labels: rankingItems.map(item => this.getTopDevicesDeviceLabel(item)),
        data: [{
          label: config.label,
          data: rankingItems.map(item => this.toNullableTopDevicesNumber(item?.[valueKey])),
          deviceMacs: rankingItems.map(item => item?.mac || ''),
          valueUnit: axis
        }],
        options: this.getTopDevicesChartOptions(axis, true),
        plugins: this.topDevicesRankingChartPlugins
      };
    };
    const primaryRanking = Array.isArray(data.metric_rankings?.primary)
      ? data.metric_rankings.primary : [];
    const secondaryRanking = Array.isArray(data.metric_rankings?.secondary)
      ? data.metric_rankings.secondary : [];
    const timeline = Array.isArray(data.metric_timeline) ? data.metric_timeline : [];
    const graphs: any[] = [
      rankingGraph(config.rankingTitle, primaryRanking, 'value', config.unit)
    ];

    if (secondaryRanking.length && config.secondaryField) {
      graphs.push(rankingGraph(
        this.getTopDevicesSecondaryTitle(config.secondaryField),
        secondaryRanking,
        'value',
        config.secondaryUnit || config.unit
      ));
    }

    if (timeline.length) {
      graphs.push({
        title: config.timelineTitle,
        type: 'line',
        labels: this.getLaneAnalyticsAxisLabels(timeline.map(item => item.timestamp)),
        data: this.getTopDevicesTimelineData(timeline),
        options: this.getTopDevicesChartOptions(config.unit),
        plugins: []
      });
    }

    const deviceSeries = Array.isArray(data.metric_device_series) ? data.metric_device_series : [];
    const seriesField = config.seriesField || config.primaryField;
    if (deviceSeries.length) {
      graphs.push({
        title: `${config.label} by Device Over Time`,
        type: 'line',
        labels: this.getLaneAnalyticsAxisLabels(data.metric_time_buckets || []),
        data: deviceSeries.map(series => ({
          label: this.getTopDevicesDeviceLabel(series),
          data: (series.points || []).map(point =>
            this.toNullableTopDevicesNumber(point?.[seriesField])),
          fill: false
        })),
        options: this.getTopDevicesChartOptions(config.unit),
        plugins: []
      });
    }

    this.topDevicesGraphs = graphs;
    this.scheduleAnalyticsChartRender();
  }

  private scheduleAnalyticsChartRender() {
    if (!this.laneAnalyticsCanvases && !this.topDevicesCanvases) {
      return;
    }
    this.clearScheduledAnalyticsChartRender();
    this.chartRenderTimer = setTimeout(() => {
      this.chartRenderTimer = null;
      this.renderLaneAnalyticsCharts();
      this.renderTopDevicesCharts();
    });
  }

  private clearScheduledAnalyticsChartRender() {
    if (this.chartRenderTimer != null) {
      clearTimeout(this.chartRenderTimer);
      this.chartRenderTimer = null;
    }
  }

  private renderLaneAnalyticsCharts() {
    this.laneAnalyticsChartInstances = this.renderCharts(
      this.laneAnalyticsCanvases,
      this.laneAnalyticsGraphs,
      this.laneAnalyticsChartInstances,
      this.laneAnalyticsChartType,
      this.laneAnalyticsChartColors,
      false
    );
  }

  private renderTopDevicesCharts() {
    this.topDevicesChartInstances = this.renderCharts(
      this.topDevicesCanvases,
      this.topDevicesGraphs,
      this.topDevicesChartInstances,
      null,
      null,
      true
    );
  }

  private renderCharts(
    canvases: QueryList<ElementRef<HTMLCanvasElement>>,
    graphs: any[],
    currentInstances: Chart[],
    defaultType: string | null,
    defaultColors: any[] | null,
    showLegend: boolean
  ): Chart[] {
    this.destroyChartInstances(currentInstances);
    if (!canvases || !graphs || !graphs.length) {
      return [];
    }

    return canvases.toArray().map((canvasRef, index) => {
      const graph = graphs[index];
      const context = canvasRef.nativeElement.getContext('2d');
      if (!graph || !context) {
        return null;
      }
      const colors = graph.type === 'line'
        ? this.laneAnalyticsChartColors
        : (graph.type === 'horizontalBar' ? this.topDevicesRankingChartColors : defaultColors);
      const colorCount = (colors || []).length;
      const datasets = (graph.data || []).map((dataset: any, datasetIndex: number) => ({
        ...(colorCount ? colors[datasetIndex % colorCount] : {}),
        ...dataset
      }));

      return new Chart(context, {
        type: graph.type || defaultType || 'line',
        data: {
          // Labels are already formatted by the existing analytics helpers.
          labels: graph.labels || [],
          datasets
        },
        options: {
          ...(graph.options || {}),
          legend: {
            ...(graph.options?.legend || {}),
            display: showLegend
          }
        },
        plugins: graph.plugins || (defaultType === this.laneAnalyticsChartType ? this.laneAnalyticsChartPlugins : [])
      } as any);
    }).filter(instance => !!instance) as Chart[];
  }

  private destroyChartInstances(instances: Chart[]) {
    (instances || []).forEach(instance => instance && instance.destroy());
  }

  getTopDevicesDeviceLabel(item: any) {
    const mac = String(item?.mac || item?.peerMac || '').trim();
    const directName = this.getFirstValue(
      item?.device_name,
      item?.deviceName,
      item?.hostname,
      item?.peer_name,
      item?.name
    );
    if (directName && this.normalizeLaneAnalyticsDeviceOptionValue(directName) !==
      this.normalizeLaneAnalyticsDeviceOptionValue(mac)) {
      return directName;
    }

    const inventoryDevice = this.getClientLookupDevice(mac);
    const configuredPeer = this.tableRows
      .map(row => row?.peerData || row)
      .find(peer => this.normalizeLaneAnalyticsDeviceOptionValue(peer?.mac) ===
        this.normalizeLaneAnalyticsDeviceOptionValue(mac));
    const analytics = this.getLaneAnalyticsData();
    const analyticsPeer = (Array.isArray(analytics?.peers) ? analytics.peers : [])
      .find(peer => this.normalizeLaneAnalyticsDeviceOptionValue(peer?.mac) ===
        this.normalizeLaneAnalyticsDeviceOptionValue(mac));
    const mappedName = this.getFirstValue(
      inventoryDevice?.name,
      inventoryDevice?.hostname,
      configuredPeer?.hostname,
      configuredPeer?.name,
      configuredPeer?.server_name,
      analyticsPeer?.hostname,
      analyticsPeer?.name,
      analyticsPeer?.server_name
    );

    return mappedName || directName || mac || 'Unknown device';
  }

  getTopDevicesTimelineData(timeline: any[]): ChartDataSets[] {
    const metric = this.selectedTopDevicesMetric;
    if (metric === 'bandwidth') {
      return [
        { label: 'TX Mbps', data: timeline.map(item => this.toNullableTopDevicesNumber(item.tx_mbps)), fill: false },
        { label: 'RX Mbps', data: timeline.map(item => this.toNullableTopDevicesNumber(item.rx_mbps)), fill: false }
      ];
    }
    if (metric === 'networkTraffic') {
      return [
        { label: 'TX Bytes', data: timeline.map(item => this.toNullableTopDevicesNumber(item.tx_bytes)), fill: false },
        { label: 'RX Bytes', data: timeline.map(item => this.toNullableTopDevicesNumber(item.rx_bytes)), fill: false }
      ];
    }
    if (metric === 'latency') {
      return [
        { label: 'Avg Latency', data: timeline.map(item => this.toNullableTopDevicesNumber(item.decode_latency_avg_ms)), fill: false },
        { label: 'P90 Latency', data: timeline.map(item => this.toNullableTopDevicesNumber(item.decode_latency_p90_ms)), fill: false }
      ];
    }
    const config = this.getTopDevicesMetricConfig();
    return [{
      label: config.label,
      data: timeline.map(item => this.toNullableTopDevicesNumber(item?.[config.primaryField])),
      fill: false
    }];
  }

  getTopDevicesSecondaryTitle(field: string) {
    const titles = {
      peak_bandwidth_mbps: 'Top Devices by Peak Bandwidth',
      packet_count: 'Top Devices by Packet Count',
      decode_latency_p90_ms: 'Top Devices by P90 Latency'
    };
    return titles[field] || `Top Devices by ${this.getTopDevicesMetricConfig().label}`;
  }

  getTopDevicesChartOptions(axis: string, horizontal = false) {
    const formatTick = (value: any) => this.formatTopDevicesChartValue(value, axis);
    const numericAxis = {
      ticks: { beginAtZero: true, autoSkip: true, maxTicksLimit: 6, callback: formatTick },
      scaleLabel: { display: true, labelString: axis }
    };
    return {
      responsive: true,
      maintainAspectRatio: false,
      legend: { display: true },
      layout: horizontal ? { padding: { right: 110 } } : undefined,
      scales: {
        xAxes: [horizontal ? numericAxis : { ticks: { autoSkip: true, maxTicksLimit: 6 } }],
        yAxes: [horizontal ? { ticks: { beginAtZero: true } } : numericAxis]
      },
      tooltips: {
        callbacks: {
          title: (tooltipItems: any[], chartData: any) => {
            const tooltipItem = tooltipItems?.[0];
            const index = tooltipItem?.index;
            const deviceName = index != null ? chartData?.labels?.[index] : '';
            const dataset = index != null ? chartData?.datasets?.[tooltipItem?.datasetIndex || 0] : null;
            const mac = dataset?.deviceMacs?.[index];
            return mac && this.normalizeLaneAnalyticsDeviceOptionValue(mac) !==
              this.normalizeLaneAnalyticsDeviceOptionValue(deviceName)
              ? `${deviceName} (${mac})`
              : deviceName;
          },
          label: (tooltipItem: any, chartData: any) => {
            const value = horizontal ? tooltipItem.xLabel : tooltipItem.yLabel;
            const label = chartData?.datasets?.[tooltipItem.datasetIndex]?.label;
            return `${label ? `${label}: ` : ''}${this.formatTopDevicesChartValue(value, axis)}`;
          }
        }
      }
    };
  }

  formatTopDevicesChartValue(value: any, unit: string) {
    const numericValue = this.toNullableTopDevicesNumber(value);
    if (numericValue == null) {
      return '-';
    }
    if (unit === 'bytes') {
      return this.formatBytes(numericValue, 1);
    }
    if (unit === 'packets') {
      return numericValue.toLocaleString();
    }
    return `${Number(numericValue.toFixed(3)).toLocaleString()} ${unit}`.trim();
  }

  getTopDevicesTableItems() {
    return this.topDevicesData?.table?.items || [];
  }

  getTopDevicesTotal() {
    return this.toNumber(this.topDevicesData?.table?.total);
  }

  getTopDevicesPageEnd() {
    return Math.min(this.topDevicesSkip + this.topDevicesPageSize, this.getTopDevicesTotal());
  }

  getTopDevicesCurrentPage() {
    return this.getTopDevicesTotal()
      ? Math.floor(this.topDevicesSkip / this.topDevicesPageSize) + 1
      : 0;
  }

  getTopDevicesPageCount() {
    return this.getTopDevicesTotal()
      ? Math.ceil(this.getTopDevicesTotal() / this.topDevicesPageSize)
      : 0;
  }

  getSelectedTopDevicesRangeLabel() {
    return this.topDevicesRanges[this.selectedTopDevicesRange]?.label || 'Custom';
  }

  formatMbps(value: any) {
    return value == null || value === '' ? '-' : `${this.toNumber(value).toFixed(3)} Mbps`;
  }

  formatTopDevicesKpi(kpi: any) {
    return this.formatTopDevicesValue(kpi?.value, kpi?.unit || 'number');
  }

  formatTopDevicesTableValue(device: any, column: AnalyticsTableColumn) {
    return this.formatTopDevicesValue(device?.[column.key], column.format);
  }

  formatTopDevicesValue(value: any, format: AnalyticsValueFormat | string) {
    if (value == null || value === '' || !Number.isFinite(Number(value))) {
      return '-';
    }
    const numericValue = Number(value);
    if (format === 'bytes') {
      return this.formatBytes(numericValue);
    }
    if (format === 'packets' || format === 'count') {
      return numericValue.toLocaleString();
    }
    if (format === 'percent' || format === '%') {
      return `${numericValue.toFixed(3)}%`;
    }
    if (format === 'Mbps') {
      return `${numericValue.toFixed(3)} Mbps`;
    }
    if (format === 'ms') {
      return `${numericValue.toFixed(3)} ms`;
    }
    if (format === 'MOS') {
      return `${numericValue.toFixed(2)} MOS`;
    }
    return numericValue.toLocaleString();
  }

  getTopDevicesDeviceCount() {
    const totalDevices = (this.topDevicesData?.metric_kpis || [])
      .find(kpi => kpi?.key === 'total_devices');
    return this.toNumber(totalDevices?.value ?? this.topDevicesData?.kpis?.total_devices);
  }

  toNullableTopDevicesNumber(value: any): number | null {
    if (value == null || value === '') {
      return null;
    }
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  toTopDevicesIsoDate(value: string) {
    const date = new Date(value);
    return value && !Number.isNaN(date.getTime()) ? date.toISOString() : '';
  }

  previousTopDevicesPage() {
    this.topDevicesSkip = Math.max(0, this.topDevicesSkip - this.topDevicesPageSize);
    this.loadTopDevices();
  }

  nextTopDevicesPage() {
    if (this.getTopDevicesPageEnd() < this.getTopDevicesTotal()) {
      this.topDevicesSkip += this.topDevicesPageSize;
      this.loadTopDevices();
    }
  }

  drillDownTopDevice(device: any) {
    const mac = this.normalizeLaneAnalyticsDeviceOptionValue(device?.mac);
    if (!mac) {
      return;
    }
    this.selectedDeviceAnalyticsView = 'device';
    this.onLaneAnalyticsDeviceChange(mac);
  }

  updateAnalyticsQueryParams(view: 'device' | 'top-devices', device: string | null = this.selectedLaneAnalyticsDevice || null) {
    if (!this.router || !this.route) {
      return;
    }
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { analyticsView: view, analyticsDevice: device },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  clearLaneAnalyticsResults(clearError = true) {
    this.laneAnalyticsData = null;
    this.laneAnalyticsChartLabels = [];
    this.laneAnalyticsChartDataByMetric = {};
    this.laneAnalyticsGraphs = [];
    this.laneAnalyticsRows = [];
    this.laneAnalyticsTableLinkRowsByPeer = {};
    if (clearError) {
      this.laneAnalyticsError = '';
    }
  }

  getSelectedLaneAnalyticsRange() {
    const selectedRange = this.laneAnalyticsRanges[this.selectedLaneAnalyticsRange] || this.laneAnalyticsRanges[0];
    return selectedRange?.range || '';
  }

  getLaneAnalyticsType() {
    return this.vHubMode ? 'vhub' : 'vce';
  }

  sortClientDeviceLookupRowsByLocation() {
    const vhubLocation = this.normalizeDeviceLocation(this.currentDeviceLocation);
    const vhubRegion = this.currentDeviceRegion;
    if ((!vhubLocation && !vhubRegion) || !this.clientDeviceLookupRows.length) {
      return;
    }

    this.clientDeviceLookupRows = this.clientDeviceLookupRows
      .map((device, index) => ({ device, index }))
      .sort((a, b) => {
        const aScore = this.getDeviceLocationPriorityScore(a.device, vhubLocation, vhubRegion);
        const bScore = this.getDeviceLocationPriorityScore(b.device, vhubLocation, vhubRegion);

        if (aScore !== bScore) {
          return bScore - aScore;
        }

        return a.index - b.index;
      })
      .map(row => row.device);
  }

  getDeviceLocationPriorityScore(device: any, vhubLocation: string, vhubRegion: string) {
    const deviceLocation = this.normalizeDeviceLocation(this.getDevicePrimaryLocation(device));
    const deviceRegion = this.getDeviceRegionKey(device);
    let score = 0;

    if (vhubLocation && deviceLocation === vhubLocation) {
      score += 100;
    }

    if (vhubRegion && deviceRegion === vhubRegion) {
      score += 60;
    }

    if (vhubLocation && deviceLocation && this.hasSharedLocationToken(vhubLocation, deviceLocation)) {
      score += 20;
    }

    return score;
  }

  getDevicePrimaryLocation(device: any) {
    return this.getFirstValue(
      device?.location,
      device?.city,
      device?.state,
      device?.country
    );
  }

  normalizeDeviceLocation(value: any) {
    return String(value || '')
      .trim()
      .replace(/\s+/g, ' ')
      .toLowerCase();
  }

  hasSharedLocationToken(a: string, b: string) {
    const aTokens = this.getLocationTokens(a);
    const bTokens = this.getLocationTokens(b);
    return aTokens.some(token => bTokens.indexOf(token) > -1);
  }

  getLocationTokens(value: any) {
    return this.normalizeDeviceLocation(value)
      .split(/[\s,./()-]+/)
      .map(token => token.trim())
      .filter(token => token.length > 2);
  }

  getDeviceRegionKey(device: any) {
    const regionText = [
      device?.region,
      device?.domain,
      device?.orgName,
      device?.name,
      device?.hostname,
      device?.location,
      device?.city,
      device?.state,
      device?.country
    ].join(' ').toLowerCase();

    const regionGroups = [
      {
        key: 'north-america-east',
        terms: [
          'east', 'eastern', 'district of columbia', 'd.c', 'dc',
          'toronto', 'st petersburg', 'st. petersburg', 'new york', 'boston',
          'montreal', 'ottawa', 'miami', 'atlanta', 'virginia', 'carolina',
          'florida', 'georgia', 'pennsylvania', 'ontario', 'quebec'
        ]
      },
      {
        key: 'north-america-central',
        terms: [
          'central', 'chicago', 'dallas', 'houston', 'denver', 'winnipeg',
          'manitoba', 'texas', 'illinois', 'colorado', 'minnesota'
        ]
      },
      {
        key: 'north-america-west',
        terms: [
          'west', 'western', 'california', 'los angeles', 'san francisco',
          'seattle', 'portland', 'vancouver', 'alberta', 'british columbia',
          'nevada', 'oregon', 'washington state'
        ]
      }
    ];

    return regionGroups.find(group => group.terms.some(term => regionText.indexOf(term) > -1))?.key || '';
  }

  getCurrentUserOrganizationId() {
    try {
      const storedUser = localStorage.getItem(UserConstant.USER);
      const user = storedUser ? JSON.parse(storedUser) : null;
      return user?.organization || '';
    } catch (error) {
      return '';
    }
  }

  buildOverlayPlusView(device: any) {
    const midlay = device?.statistics?.rrt_midlay || {};
    const dpcs = this.getDpcs(midlay);
    const peers = this.getPeers(midlay);
    const legacyEntries = this.getLegacyMidlayEntries(midlay);

    this.overlayPlusTeam = dpcs[0] || peers[0] || legacyEntries[0]?.team || null;

    if (!this.overlayPlusTeam) {
      this.resetView();
      return;
    }

    if (this.vHubMode) {
      this.interfaceRow = null;
      this.interfaceRows = [];
      this.connectionRows = [];
      this.dpcServiceRows = dpcs.length
        ? dpcs.map((dpc: any) => this.getDpcServiceRow(dpc))
        : legacyEntries.map(entry => this.getDpcServiceRow(entry.team, entry.key));
      this.tableRows = peers.length
        ? this.getPeerRows(peers)
        : this.getLegacyPeerRows(legacyEntries);
      this.updateAddPeerDefaults();
      return;
    }

    const vceTeams = legacyEntries.length
      ? legacyEntries.map(entry => entry.team).filter(team => !!team)
      : [this.overlayPlusTeam];
    this.buildDeviceOverlayView(vceTeams, device?.statistics?.interfaces);
  }

  updateAddPeerDefaults() {
    const firstDpc = this.dpcServiceRows[0];
    if (firstDpc && !this.addPeerModel.dpcService) {
      this.addPeerModel.dpcService = `${firstDpc.name} (${firstDpc.server_url})`;
    }
  }

  onClientMacInputFocus() {
    this.clientMacLookupOpen = !!this.clientDeviceLookupRows.length;
  }

  onClientMacInputBlur() {
    setTimeout(() => {
      this.clientMacLookupOpen = false;
    }, 120);
  }

  onClientMacInputChange() {
    this.clientMacLookupOpen = !!this.clientDeviceLookupRows.length;
    this.clientMacLookupIndex = -1;
    this.clearClientMacSearchDebounce();
    this.clientMacSearchDebounceTimer = setTimeout(() => {
      this.clientMacSearchDebounceTimer = null;
      this.clientMacSearchText = String(this.addPeerModel.clientMac || '');
      this.clientMacLookupIndex = -1;
      this.loadClientDeviceLookup(this.clientMacSearchText, true);
    }, this.clientMacSearchDebounceMs);
  }

  clearClientMacSearchDebounce() {
    if (this.clientMacSearchDebounceTimer) {
      clearTimeout(this.clientMacSearchDebounceTimer);
      this.clientMacSearchDebounceTimer = null;
    }
  }

  onClientMacKeydown(event: KeyboardEvent) {
    const options = this.getFilteredClientDeviceLookupRows();
    if (!options.length) {
      return;
    }

    if (event.key === 'Tab') {
      event.preventDefault();
      this.clientMacLookupOpen = true;
      const direction = event.shiftKey ? -1 : 1;
      const nextIndex = this.clientMacLookupIndex + direction;
      this.clientMacLookupIndex = (nextIndex + options.length) % options.length;
      this.selectClientLookupDevice(options[this.clientMacLookupIndex], false);
      this.scrollClientMacOptionIntoView();
      return;
    }

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      this.clientMacLookupOpen = true;
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const nextIndex = this.clientMacLookupIndex + direction;
      this.clientMacLookupIndex = (nextIndex + options.length) % options.length;
      this.scrollClientMacOptionIntoView();
      return;
    }

    if (event.key === 'Enter' && this.clientMacLookupIndex > -1) {
      event.preventDefault();
      this.selectClientLookupDevice(options[this.clientMacLookupIndex]);
      return;
    }

    if (event.key === 'Escape') {
      this.clientMacLookupOpen = false;
    }
  }

  onClientMacLookupChange() {
    const device = this.getClientLookupDevice(this.addPeerModel.clientMac);
    if (!device) {
      return;
    }

    this.selectClientLookupDevice(device, false);
  }

  selectClientLookupDevice(device: any, closeLookup = true) {
    if (!device) {
      return;
    }

    this.clearClientMacSearchDebounce();
    this.clientDeviceLookupRequestId += 1;
    this.addPeerModel.clientMac = device.mac || this.addPeerModel.clientMac;
    this.clientMacSearchText = this.addPeerModel.clientMac;
    this.addPeerModel.clientHostname = device.name || device.hostname || this.addPeerModel.clientHostname || '';

    const model = this.getClientLookupDeviceModel(device);
    if (model) {
      this.addPeerModel.clientDeviceType = model;
    }

    const selectedIndex = this.getFilteredClientDeviceLookupRows()
      .findIndex(option => String(option?.mac || '').toLowerCase() === String(device?.mac || '').toLowerCase());
    this.clientMacLookupIndex = selectedIndex;
    this.clientMacLookupOpen = !closeLookup;
  }

  scrollClientMacOptionIntoView() {
    setTimeout(() => {
      const activeOption = document.querySelector('.client-mac-options button.active') as HTMLElement;
      if (activeOption) {
        activeOption.scrollIntoView({ block: 'nearest' });
      }
    });
  }

  getClientLookupDevice(mac: string) {
    const normalizedMac = String(mac || '').trim().toLowerCase();
    if (!normalizedMac) {
      return null;
    }

    return this.clientDeviceLookupRows.find(device => String(device?.mac || '').toLowerCase() === normalizedMac) || null;
  }

  getClientLookupDeviceModel(device: any) {
    const normalizedModel = this.normalizeClientDeviceType(this.getClientLookupRawDeviceModel(device));
    return this.clientDeviceTypeOptions.find(option => {
      const optionValue = this.normalizeClientDeviceType(option.value);
      return optionValue === normalizedModel || normalizedModel.indexOf(optionValue) > -1;
    })?.value || '';
  }

  getClientLookupRawDeviceModel(device: any) {
    return this.getFirstValue(
      device?.deviceMetaInfo?.deviceModel,
      device?.deviceMetaInfo?.model,
      device?.deviceModel,
      device?.model,
      device?.client_model
    );
  }

  normalizeClientDeviceType(value: any) {
    return String(value || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
  }

  getClientLookupLabel(device: any) {
    const name = device?.name || device?.hostname || 'Unnamed';
    const location = this.getDevicePrimaryLocation(device);
    const model = this.getClientLookupRawDeviceModel(device);
    const locationText = location ? ` - ${location}` : '';
    const proximityText = device?.region_match === 'exact'
      ? 'Exact region - '
      : device?.region_match === 'nearby'
        ? 'Nearest - '
        : device?.region_match === 'other'
          ? 'Other region - '
          : '';
    return model
      ? `${proximityText}${device.mac} - ${name}${locationText} (${model})`
      : `${proximityText}${device.mac} - ${name}${locationText}`;
  }

  getFilteredClientDeviceLookupRows() {
    const searchText = String(this.clientMacSearchText || '').trim().toLowerCase();
    const sourceRows = this.clientDeviceLookupRows || [];
    if (!searchText) {
      return sourceRows;
    }

    return sourceRows.filter(device => {
      const haystack = [
        device?.mac,
        device?.name,
        device?.hostname,
        this.getDevicePrimaryLocation(device),
        this.getClientLookupDeviceModel(device),
        device?.deviceMetaInfo?.deviceModel
      ].join(' ').toLowerCase();
      return haystack.indexOf(searchText) > -1;
    });
  }

  saveAddPeer(applyConfirmTemplate?: TemplateRef<any>) {
    if (this.addPeerSaveLoading || this.overlayPlusCrudLoading) {
      return;
    }

    this.addPeerSubmitted = true;
    if (!this.isAddPeerFormValid()) {
      return;
    }

    const selectedDpc = this.getSelectedDpcRow(this.addPeerModel.dpcService);
    const peer = this.getPeerFromAddModel();
    const submitPayload = this.getAddPeerSubmitPayload(peer, selectedDpc);
    const applyContext = this.getAddPeerApplyContext(submitPayload);

    this.addPeerSaveLoading = true;
    this.startPeerCrudProcess('add');
    this.vceVhubService.addMidlayVhubPeer(this.currentDeviceMac, this.getApplyAddPeerPayload(applyContext)).subscribe(() => {
      this.addPeerSaveLoading = false;
      this.stopPeerCrudProcess();
      applyContext.peerSavedOnVhub = true;
      this.completeAddPeerSave(peer, selectedDpc);

      if (applyConfirmTemplate) {
        this.openApplyActivationConfirm(applyConfirmTemplate, applyContext);
        return;
      }

      this.startApplyProcess(applyContext);
    }, error => {
      this.addPeerSaveLoading = false;
      this.stopPeerCrudProcess();
      this.showOverlayPlusError('Unable to add peer on vHUB.', error);
    });
  }

  applyAddPeerToDevice(submitPayload: any) {
    this.startApplyProcess(this.getAddPeerApplyContext(submitPayload));
  }

  getAddPeerApplyContext(submitPayload: any): ApplyProcessContext {
    const peerMac = submitPayload?.vce_mac || submitPayload?.mac || '';
    return {
      type: 'addPeer',
      vhubMac: this.currentDeviceMac,
      peerMac,
      vceMac: submitPayload?.vce_mac || peerMac,
      dpc: submitPayload?.dpc_allowed || '',
      submitPayload,
      peerPayload: this.getAddPeerAutomationPayload(submitPayload, true),
      existingVceProvision: this.getExistingVceProvision(peerMac)
    };
  }

  getAddPeerAutomationPayload(submitPayload: any, applyVce: boolean) {
    const { vce_mac, ...peerPayload } = submitPayload || {};
    return {
      apply_vce: applyVce,
      vce_mac: vce_mac || peerPayload?.mac || '',
      peer: {
        mac: vce_mac || peerPayload?.mac || '',
        ...peerPayload
      }
    };
  }

  completeAddPeerSave(peer: any, selectedDpc: DpcServiceRow | null) {
    const peerRows = this.getPeerDisplayRows(peer, this.tableRows.length);
    this.tableRows = [...this.tableRows, ...peerRows];
    this.buildAddPeerProvisioningOutput(peer, selectedDpc);
    this.resetAddPeerForm();
    this.addPeerProvisioningReady = true;
    this.toastr.success('Peer added successfully.');
  }

  resetAddPeerForm() {
    this.clearClientMacSearchDebounce();
    this.clientMacSearchText = '';
    this.clientMacLookupOpen = false;
    this.clientMacLookupIndex = -1;
    this.addPeerModel = this.getDefaultAddPeerModel();
    this.qfecLanes = [];
    this.customLinkValues = {};
    this.openLinkPickerKey = '';
    this.addPeerSubmitted = false;
    this.updateAddPeerDefaults();
  }

  showAddPeerForm() {
    this.addPeerProvisioningReady = false;
    this.addPeerUiBlock = '';
    this.addPeerCliScript = '';
    this.resetAddPeerForm();
  }

  getDefaultAddPeerModel(): AddPeerModel {
    return {
      clientDeviceType: this.clientDeviceTypeOptions[0].value,
      clientMac: '',
      clientHostname: '',
      dpcService: '',
      dpcIpAssignment: DpcIpAssignment.AutoPool,
      dpcIp: '',
      psk: '',
      wanId: '',
      wanSubnet: '',
      dmzIp: '',
      dmzSubnets: '',
      mode: PathManagementMode.RouteTables,
      link1: '',
      link2: '',
      link3: '',
      link4: '',
      routeTable1: '',
      routeTable2: '',
      routeTable3: '',
      routeTable4: '',
      weightTx1: this.getNumberValue('', this.addPeerPlaceholders.weightTx1),
      weightTx2: this.getNumberValue('', this.addPeerPlaceholders.weightTx2),
      weightRx1: this.getNumberValue('', this.addPeerPlaceholders.weightRx1),
      weightRx2: this.getNumberValue('', this.addPeerPlaceholders.weightRx2),
      weightTx3: null,
      weightTx4: null,
      weightRx3: null,
      weightRx4: null,
      failoverTimeout1: 3200,
      failoverTimeout2: 45000,
      failoverTimeout3: null,
      failoverTimeout4: null,
      flapcheck1: 30000,
      flapcheck2: this.getNumberValue('', this.addPeerPlaceholders.flapcheck2),
      flapcheck3: null,
      flapcheck4: null,
      transport1: TransportProtocol.Udp,
      transport2: TransportProtocol.Udp,
      transport3: TransportProtocol.Udp,
      transport4: TransportProtocol.Udp,
      distribution: DistributionMode.PerPacket,
      maxPacketSize: this.getNumberValue('', this.addPeerPlaceholders.maxPacketSize),
      rseqHoldMs: this.getNumberValue('', this.addPeerPlaceholders.rseqHoldMs),
      rseqThinkAcks: this.getNumberValue('', this.addPeerPlaceholders.rseqThinkAcks),
      maxblocks: this.getNumberValue('', this.addPeerPlaceholders.maxblocks),
      fecGroupHold: this.addPeerPlaceholders.fecGroupHold,
      fecGroupHoldDepth: this.addPeerPlaceholders.fecGroupHoldDepth,
      defaultLaneOrigDupes: this.addPeerPlaceholders.defaultLaneOrigDupes
    };
  }

  isAddPeerFormValid() {
    return !this.isAddPeerFieldInvalid('clientMac') &&
      !this.isAddPeerFieldInvalid('dpcService') &&
      !this.isAddPeerFieldInvalid('psk');
  }

  isAddPeerFieldInvalid(field: 'clientMac' | 'dpcService' | 'psk') {
    if (!this.addPeerSubmitted) {
      return false;
    }

    if (field === 'dpcService') {
      return !this.addPeerModel.dpcService || !this.getSelectedDpcRow(this.addPeerModel.dpcService);
    }

    if (field === 'clientMac') {
      return !this.hasRequiredValue(this.addPeerModel.clientMac) || !this.getClientLookupDevice(this.addPeerModel.clientMac);
    }

    return !this.hasRequiredValue(this.addPeerModel[field]);
  }

  hasRequiredValue(value: any) {
    return String(value || '').trim().length > 0;
  }

  isManualAddPeerDpcIp() {
    return this.addPeerModel.dpcIpAssignment === DpcIpAssignment.ManualCidr;
  }

  getNonBlankPeerNetworkFields(values: any) {
    const fieldNames = ['dpc_ip', 'wan_id', 'wan_subnet', 'dmz_ip', 'dmz_subnet'];
    return fieldNames.reduce((fields, fieldName) => {
      const value = values?.[fieldName];
      if (value === undefined || value === null || String(value).trim() === '') {
        return fields;
      }

      fields[fieldName] = typeof value === 'string' ? value.trim() : value;
      return fields;
    }, {} as any);
  }

  getPeerFromAddModel() {
    const dpcName = this.getSelectedDpcName(this.addPeerModel.dpcService);
    const mac = this.getTextValue(this.addPeerModel.clientMac, this.addPeerPlaceholders.clientMac);

    return {
      mac,
      hostname: this.getTextValue(this.addPeerModel.clientHostname, this.addPeerPlaceholders.clientHostname),
      client_model: this.addPeerModel.clientDeviceType,
      dpc_allowed: dpcName,
      encrypt: 'on',
      psk: this.addPeerModel.psk,
      ...this.getNonBlankPeerNetworkFields({
        dpc_ip: this.isManualAddPeerDpcIp() ? this.addPeerModel.dpcIp : 'auto',
        wan_id: this.addPeerModel.wanId,
        wan_subnet: this.addPeerModel.wanSubnet,
        dmz_ip: this.addPeerModel.dmzIp,
        dmz_subnet: this.addPeerModel.dmzSubnets
      }),
      links: this.getAddPeerLinks(),
      vce_provision: {
        route_tables: this.getAddPeerRouteTables(),
        distribution: this.addPeerModel.distribution,
        maxpacketsize: this.getNumberValue(this.addPeerModel.maxPacketSize, this.addPeerPlaceholders.maxPacketSize),
        rseq_hold_ms: this.getNumberValue(this.addPeerModel.rseqHoldMs, this.addPeerPlaceholders.rseqHoldMs),
        rseq_thin_acks: this.getNumberValue(this.addPeerModel.rseqThinkAcks, this.addPeerPlaceholders.rseqThinkAcks),
        maxblocks: this.getNumberValue(this.addPeerModel.maxblocks, this.addPeerPlaceholders.maxblocks),
        fec_group_hold: this.getTextValue(this.addPeerModel.fecGroupHold, this.addPeerPlaceholders.fecGroupHold),
        fec_group_hold_depth: this.getTextValue(this.addPeerModel.fecGroupHoldDepth, this.addPeerPlaceholders.fecGroupHoldDepth),
        fec: this.getTextValue(this.addPeerModel.defaultLaneOrigDupes, this.addPeerPlaceholders.defaultLaneOrigDupes),
        fec_lanes: this.qfecLanes.map(lane => ({
          lane: lane.laneQ,
          orig_dupes: lane.origDupes,
          mode: lane.mode,
          mode_str: lane.mode,
          steer: lane.steer
        }))
      }
    };
  }

  getAddPeerLinks(): OverlayPlusLink[] {
    const links = [
      {
        name: this.getTextValue(this.addPeerModel.link1, this.addPeerPlaceholders.link1),
        weight: this.getNumberValue(this.addPeerModel.weightTx1, this.addPeerPlaceholders.weightTx1),
        rx_weight: this.getNumberValue(this.addPeerModel.weightRx1, this.addPeerPlaceholders.weightRx1),
        timeout: this.getNumberValue(this.addPeerModel.failoverTimeout1, this.getAddPeerTimeoutDefault(1)),
        flapcheck: this.getNumberValue(this.addPeerModel.flapcheck1, this.addPeerPlaceholders.flapcheck1),
        transport: this.addPeerModel.transport1,
        action: 'primary',
        state: 'PENDING'
      },
      {
        name: this.getTextValue(this.addPeerModel.link2, this.addPeerPlaceholders.link2),
        weight: this.getNumberValue(this.addPeerModel.weightTx2, this.addPeerPlaceholders.weightTx2),
        rx_weight: this.getNumberValue(this.addPeerModel.weightRx2, this.addPeerPlaceholders.weightRx2),
        timeout: this.getNumberValue(this.addPeerModel.failoverTimeout2, this.getAddPeerTimeoutDefault(2)),
        flapcheck: this.getNumberValue(this.addPeerModel.flapcheck2, this.addPeerPlaceholders.flapcheck2),
        transport: this.addPeerModel.transport2,
        action: 'backup',
        state: 'PENDING'
      },
      {
        name: this.getTextValue(this.addPeerModel.link3, this.addPeerPlaceholders.link3),
        weight: this.getNumberValue(this.addPeerModel.weightTx3, this.addPeerPlaceholders.weightTx3),
        rx_weight: this.getNumberValue(this.addPeerModel.weightRx3, this.addPeerPlaceholders.weightRx3),
        timeout: this.getNumberValue(this.addPeerModel.failoverTimeout3, this.getAddPeerTimeoutDefault(3)),
        flapcheck: this.getNumberValue(this.addPeerModel.flapcheck3, this.addPeerPlaceholders.flapcheck3),
        transport: this.addPeerModel.transport3,
        action: 'optional',
        state: 'PENDING'
      },
      {
        name: this.getTextValue(this.addPeerModel.link4, this.addPeerPlaceholders.link4),
        weight: this.getNumberValue(this.addPeerModel.weightTx4, this.addPeerPlaceholders.weightTx4),
        rx_weight: this.getNumberValue(this.addPeerModel.weightRx4, this.addPeerPlaceholders.weightRx4),
        timeout: this.getNumberValue(this.addPeerModel.failoverTimeout4, this.getAddPeerTimeoutDefault(4)),
        flapcheck: this.getNumberValue(this.addPeerModel.flapcheck4, this.addPeerPlaceholders.flapcheck4),
        transport: this.addPeerModel.transport4,
        action: 'optional',
        state: 'PENDING'
      }
    ];

    return links.filter(link => link.name && link.name !== '[not set]');
  }

  getAddPeerRouteTables() {
    return [
      this.getAddPeerRouteTableValue('routeTable1', 'link1'),
      this.getAddPeerRouteTableValue('routeTable2', 'link2'),
      this.isAddPeerLinkSet(this.addPeerModel.link3) ? this.getAddPeerRouteTableValue('routeTable3', 'link3') : '',
      this.isAddPeerLinkSet(this.addPeerModel.link4) ? this.getAddPeerRouteTableValue('routeTable4', 'link4') : ''
    ].filter(Boolean).join(',');
  }

  getAddPeerRouteTableValue(routeTableField: string, linkField: string) {
    const routeTable = String((this.addPeerModel as any)?.[routeTableField] || '').trim();
    if (routeTable) {
      return routeTable;
    }

    const placeholder = String((this.addPeerPlaceholders as any)?.[routeTableField] || '').trim();
    if (placeholder) {
      return placeholder;
    }

    const link = String((this.addPeerModel as any)?.[linkField] || '').trim();
    return link && link !== '[not set]' ? link : '';
  }

  getSelectedDpcName(value: string) {
    const selectedValue = value || (this.dpcServiceRows[0] ? `${this.dpcServiceRows[0].name} (${this.dpcServiceRows[0].server_url})` : '');
    return this.getDpcNameFromValue(selectedValue) || '-';
  }

  getDpcNameFromValue(value: any) {
    const normalized = String(value || '').trim();
    return normalized && normalized !== '-' ? normalized.split(' (')[0] : '';
  }

  getSelectedDpcRow(value: string) {
    const dpcName = this.getSelectedDpcName(value);
    return this.dpcServiceRows.find(dpc => dpc.name === dpcName) || this.dpcServiceRows[0] || null;
  }

  buildAddPeerProvisioningOutput(peer: any, dpc: DpcServiceRow | null) {
    const generatedAt = this.getProvisioningTimestamp();
    const submitPayload = this.getAddPeerSubmitPayload(peer, dpc);
    const submitPeer = submitPayload;
    const provision = submitPeer.vce_provision || {};
    const vhub = submitPeer.dpc_allowed || '-';

    this.addPeerUiBlock = [
      '# RRT-Midlay Provisioning',
      `# Generated: ${generatedAt}`,
      `# vHUB: ${vhub}`,
      `# Client: ${submitPeer.vce_mac}`,
      `REMOTE_ADDR=${submitPeer.remote_addr}`,
      `DPC_GATEWAY=${submitPeer.dpc_gateway}`,
      `DPC_IP=${submitPeer.dpc_ip}`,
      `ENCRYPT=${submitPeer.encrypt}`,
      `PSK=${submitPeer.psk || 'GENERATE_ON_DEVICE'}`,
      `MTU=${submitPeer.mtu}`,
      `KEEPALIVE=${submitPeer.keepalive}`,
      `DPC_DNS=${submitPeer.dpc_gateway}`,
      'VHUB=vhub',
      `VCE_DISTRIBUTION=${provision?.distribution}`,
      `VCE_TRANSPORT=${provision?.transport}`,
      `VCE_RSEQ_HOLD_MS=${provision?.rseq_hold_ms}`,
      `VCE_RSEQ_THIN_ACKS=${provision?.rseq_thin_acks}`,
      `VCE_MAXBLOCKS=${provision?.maxblocks}`,
      `VCE_FEC_GROUP_HOLD=${provision?.fec_group_hold}`
    ].join('\n');

    this.addPeerCliScript = [
      '#!/bin/sh',
      '# RRT-Midlay Provisioning Script',
      `# Generated: ${generatedAt}`,
      `# vHUB: ${vhub}`,
      `# Client: ${submitPeer.vce_mac}`,
      '# Usage: paste into device SSH and run: sh <this-script>',
      '',
      `/usr/bin/vce_util-midlay.sh apply_inline '${JSON.stringify(submitPayload)}'`
    ].join('\n');
  }

  getAddPeerSubmitPayload(peer: any, dpc: DpcServiceRow | null) {
    const links = Array.isArray(peer?.links) ? peer.links : [];
    const dpcGateway = dpc?.dpc_gateway || this.stripCidr(dpc?.dpc_ip) || '10.0.141.254';
    const provision = this.getAddPeerSubmitProvision(peer, links);

    return {
      vce_mac: peer?.mac || peer?.vce_mac || '',
      hostname: peer?.hostname || '',
      client_model: peer?.client_model || '',
      dpc_allowed: peer?.dpc_allowed || '',
      encrypt: this.getEncryptToken(peer?.encrypt || 'on'),
      psk: peer?.psk || '$PSK',
      ...this.getNonBlankPeerNetworkFields(peer),
      remote_addr: dpc?.server_url || '',
      dpc_gateway: dpcGateway,
      mtu: String(dpc?.mtu || '1420'),
      keepalive: '10',
      vce_provision: provision
    };
  }

  getAddPeerSubmitProvision(peer: any, links: any[]) {
    const provision = peer?.vce_provision || {};
    const payload: any = {
      link_names: links.map(link => link?.name).filter(Boolean).join(','),
      ulay_mode: provision?.ulay_mode || this.getUlayModeToken(this.addPeerModel.mode),
      weights: links.map(link => `${this.toNumber(link?.weight)}:${this.toNumber(link?.rx_weight)}`).join(','),
      timeouts: links.map(link => String(this.toNumber(link?.timeout))).join(','),
      flapcheck_period: links.map(link => String(this.toNumber(link?.flapcheck))).join(','),
      route_tables: provision?.route_tables || '',
      maxpacketsize: String(provision?.maxpacketsize ?? ''),
      fec: provision?.fec || '15:1',
      transport: links.map(link => this.getTransportToken(link?.transport)).join(','),
      distribution: this.getDistributionToken(provision?.distribution),
      rseq_hold_ms: String(provision?.rseq_hold_ms ?? ''),
      rseq_thin_acks: String(provision?.rseq_thin_acks ?? provision?.rseq_think_acks ?? ''),
      maxblocks: String(provision?.maxblocks ?? ''),
      fec_group_hold: String(provision?.fec_group_hold ?? '0'),
      fec_group_hold_depth: String(provision?.fec_group_hold_depth ?? '')
    };

    (provision?.fec_lanes || []).forEach((lane: any, index: number) => {
      const laneNumber = this.getPeerFecLaneNumber(lane, index);
      payload[`fec_q${laneNumber}`] = lane?.orig_dupes || '';
      payload[`fec_q${laneNumber}_mode`] = lane?.mode || lane?.mode_str || '';
      payload[`fec_q${laneNumber}_steer`] = String(lane?.steer ?? '');
    });

    return payload;
  }

  openPeerProvisioningModal(templateRef: TemplateRef<any>) {
    const peer = this.getPeerFromDetailModel();
    const dpc = this.dpcServiceRows.find(row => row.name === peer?.dpc_allowed) || this.dpcServiceRows[0] || null;
    const submitPayload = this.getAddPeerSubmitPayload(peer, dpc);
    const generatedAt = this.getProvisioningTimestamp();
    const provision = submitPayload.vce_provision || {};
    const provisionJson = this.stringifyProvisionInline(provision);
    const clientLabel = `${submitPayload.hostname || ''} (${submitPayload.vce_mac || peer?.mac || ''})`;
    const vhubLine = `# vHUB: vhub  DPC: ${submitPayload.dpc_allowed || '-'}`;

    this.peerProvisioningTitle = `Provisioning - ${submitPayload.vce_mac || peer?.mac || '-'}`;
    this.peerProvisioningUiBlock = [
      '# RRT-Midlay Provisioning',
      `# Generated: ${generatedAt}`,
      vhubLine,
      `# Client: ${clientLabel}`,
      `REMOTE_ADDR=${submitPayload.remote_addr}`,
      `DPC_GATEWAY=${submitPayload.dpc_gateway}`,
      `DPC_IP=${submitPayload.dpc_ip}`,
      `ENCRYPT=${submitPayload.encrypt}`,
      `PSK=${submitPayload.psk || 'GENERATE_ON_DEVICE'}`,
      `MTU=${submitPayload.mtu}`,
      `KEEPALIVE=${submitPayload.keepalive}`,
      `DPC=${submitPayload.dpc_allowed}`,
      'VHUB=vhub',
      `VCE_DISTRIBUTION=${provision.distribution || ''}`,
      `VCE_TRANSPORT=${provision.transport || ''}`,
      `VCE_RSEQ_HOLD_MS=${provision.rseq_hold_ms || ''}`,
      `VCE_RSEQ_THIN_ACKS=${provision.rseq_thin_acks || ''}`,
      `VCE_MAXBLOCKS=${provision.maxblocks || ''}`,
      `VCE_FEC_GROUP_HOLD=${provision.fec_group_hold || ''}`,
      '',
      '',
      '',
      `VCE_PROVISION=${provisionJson}`
    ].join('\n');

    this.peerProvisioningCliScript = [
      '#!/bin/sh',
      '# RRT-Midlay Provisioning Script',
      `# Generated: ${generatedAt}`,
      vhubLine,
      `# Client: ${clientLabel}`,
      '# Usage: paste into device SSH and run: sh <this-script>',
      '',
      [
        '/usr/bin/vce_util-midlay.sh apply_inline',
        this.shellQuote(submitPayload.remote_addr),
        this.shellQuote(submitPayload.dpc_gateway),
        this.shellQuote(submitPayload.dpc_ip),
        this.shellQuote(submitPayload.encrypt),
        this.shellQuote(submitPayload.psk || 'GENERATE_ON_DEVICE'),
        this.shellQuote(submitPayload.mtu),
        this.shellQuote(submitPayload.keepalive),
        this.shellQuote(provisionJson)
      ].join('     ')
    ].join('\n');

    this.dialog.open(templateRef, {
      width: '620px',
      panelClass: 'overlay-plus-peer-provisioning-dialog',
      autoFocus: false
    });
  }

  getProvisioningTimestamp() {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = String(now.getUTCMonth() + 1).padStart(2, '0');
    const day = String(now.getUTCDate()).padStart(2, '0');
    const hour = String(now.getUTCHours()).padStart(2, '0');
    const minute = String(now.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hour}:${minute} UTC`;
  }

  stringifyProvisionInline(provision: any) {
    const entries = Object.keys(provision || {})
      .map(key => `"${key}": ${JSON.stringify(String(provision[key] ?? ''))}`);
    return `{ ${entries.join(', ')} }`;
  }

  shellQuote(value: any) {
    return `'${String(value ?? '').replace(/'/g, `'\\''`)}'`;
  }

  getUlayModeToken(value: string) {
    return FormatterHelper.getUlayModeToken(value);
  }

  getTransportToken(value: string) {
    return FormatterHelper.getTransportToken(value);
  }

  getDistributionToken(value: string) {
    return FormatterHelper.getDistributionToken(value);
  }

  copyProvisioningText(value: string, target = '') {
    const text = String(value || '');
    if (!text) {
      return;
    }

    const markCopied = () => {
      this.copiedProvisioningTarget = target;
      setTimeout(() => {
        if (this.copiedProvisioningTarget === target) {
          this.copiedProvisioningTarget = '';
        }
      }, 1500);
    };

    if (navigator?.clipboard?.writeText) {
      navigator.clipboard.writeText(text)
        .then(markCopied)
        .catch(() => {
          this.copyProvisioningTextFallback(text);
          markCopied();
        });
      return;
    }

    this.copyProvisioningTextFallback(text);
    markCopied();
  }

  copyProvisioningTextFallback(value: string) {
    const textarea = document.createElement('textarea');
    textarea.value = value;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'fixed';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    document.execCommand('copy');
    document.body.removeChild(textarea);
  }

  getTextValue(value: string | number | null | undefined, fallback: string) {
    return FormatterHelper.getTextValue(value, fallback);
  }

  getNumberValue(value: string | number | null | undefined, fallback: string | number) {
    return FormatterHelper.getNumberValue(value, fallback);
  }

  isAddPeerLinkSet(value: string) {
    const normalized = String(value || '').trim();
    return !!normalized && normalized !== '[not set]';
  }

  generatePsk() {
    this.addPeerModel.psk = this.generateBase64UrlPsk();
  }

  generateBase64UrlPsk(byteLength = 32) {
    return FormatterHelper.generateBase64UrlPsk(byteLength);
  }

  addQfecLane() {
    const nextLane = this.qfecLanes.length + 1;
    this.qfecLanes = [...this.qfecLanes, { laneQ: nextLane, origDupes: '1:3', mode: 'raw', steer: nextLane }];
  }

  removeQfecLane(index: number) {
    this.qfecLanes = this.qfecLanes.filter((_, laneIndex) => laneIndex !== index);
  }

  toggleLinkPicker(key: string) {
    this.openLinkPickerKey = this.openLinkPickerKey === key ? '' : key;
  }

  setLinkPickerValue(target: any, field: string, value: string) {
    const previousTimeoutDefault = this.getLinkPickerTimeoutDefault(target, field);
    target[field] = value;
    this.seedAddPeerRouteTable(field, value);
    this.seedPeerLinkDefaults(target, field);
    this.seedOptionalLinkDefaults(field);
    this.applyAddPeerBackupLinkDefaults(field, value);
    this.seedAddPeerTimeout(target, field, previousTimeoutDefault);
    this.openLinkPickerKey = '';
  }

  setCustomLinkPickerValue(target: any, field: string, key: string) {
    const value = String(this.customLinkValues[key] || '').trim();
    if (!value) {
      return;
    }
    const previousTimeoutDefault = this.getLinkPickerTimeoutDefault(target, field);
    target[field] = value;
    this.seedAddPeerRouteTable(field, value);
    this.seedPeerLinkDefaults(target, field);
    this.seedOptionalLinkDefaults(field);
    this.applyAddPeerBackupLinkDefaults(field, value);
    this.seedAddPeerTimeout(target, field, previousTimeoutDefault);
    this.customLinkValues[key] = '';
    this.openLinkPickerKey = '';
  }

  clearLinkPickerValue(target: any, field: string) {
    target[field] = '[not set]';
    this.clearAddPeerRouteTable(field);
    this.clearPeerLinkDefaults(target, field);
    this.clearOptionalLinkDefaults(field);
    this.openLinkPickerKey = '';
  }

  seedAddPeerRouteTable(linkField: string, value: string) {
    const routeTableField = this.getAddPeerRouteTableField(linkField);
    if (routeTableField && !String((this.addPeerModel as any)[routeTableField] || '').trim()) {
      (this.addPeerModel as any)[routeTableField] =
        String((this.addPeerPlaceholders as any)[routeTableField] || '').trim() || value;
    }
  }

  clearAddPeerRouteTable(linkField: string) {
    const routeTableField = this.getAddPeerRouteTableField(linkField);
    if (routeTableField) {
      (this.addPeerModel as any)[routeTableField] = '';
    }
  }

  getAddPeerRouteTableField(linkField: string) {
    const match = String(linkField || '').match(/^link([1-4])$/);
    return match ? `routeTable${match[1]}` : '';
  }

  applyAddPeerBackupLinkDefaults(field: string, value: string) {
    if (field !== 'link2' || !/^(4glte|wwan0)$/i.test(String(value || '').trim())) {
      return;
    }

    this.addPeerModel.weightTx2 = 0;
    this.addPeerModel.weightRx2 = 0;
  }

  getAddPeerTimeoutDefault(index: number): string {
    const model = this.addPeerModel as any;
    const placeholders = this.addPeerPlaceholders as any;
    const link = this.getTextValue(model[`link${index}`], placeholders[`link${index}`]);
    const route = String(model[`routeTable${index}`] || '').trim();
    return [link, route].some(value => /^(4glte|wwan0)$/i.test(value))
      ? '45000'
      : placeholders[`failoverTimeout${index}`];
  }

  private getLinkPickerTimeoutDefault(target: any, field: string): string | null {
    const match = /^link([1-4])$/.exec(field);
    return target === this.addPeerModel && match ? this.getAddPeerTimeoutDefault(Number(match[1])) : null;
  }

  private seedAddPeerTimeout(target: any, field: string, previousDefault: string | null): void {
    if (previousDefault === null || target !== this.addPeerModel) return;
    const index = Number(field.slice(4));
    const key = `failoverTimeout${index}`;
    const current = target[key];
    if (current === null || current === undefined || current === '' || Number(current) === Number(previousDefault)) {
      target[key] = Number(this.getAddPeerTimeoutDefault(index));
    }
  }

  seedPeerLinkDefaults(target: any, field: string) {
    if (field !== 'name' || !target || !Object.prototype.hasOwnProperty.call(target, 'transport')) {
      return;
    }

    target.transport = target.transport || TransportProtocol.Udp;
    target.path = target.path || target.name;
    target.tx = target.tx ?? null;
    target.rx = target.rx ?? null;
    target.timeout = target.timeout ?? null;
    target.flapcheck = target.flapcheck ?? null;
  }

  clearPeerLinkDefaults(target: any, field: string) {
    if (field !== 'name' || !target || !Object.prototype.hasOwnProperty.call(target, 'transport')) {
      return;
    }

    target.transport = '';
    target.path = '';
    target.tx = null;
    target.rx = null;
    target.timeout = null;
    target.flapcheck = null;
  }

  seedOptionalLinkDefaults(field: string) {
    if (field === 'link3') {
      this.addPeerModel.weightTx3 = this.getNumberValue(this.addPeerModel.weightTx3, this.addPeerPlaceholders.weightTx3);
      this.addPeerModel.weightRx3 = this.getNumberValue(this.addPeerModel.weightRx3, this.addPeerPlaceholders.weightRx3);
      this.addPeerModel.flapcheck3 = this.getNumberValue(this.addPeerModel.flapcheck3, this.addPeerPlaceholders.flapcheck3);
      this.addPeerModel.transport3 = this.addPeerModel.transport3 || TransportProtocol.Udp;
    }

    if (field === 'link4') {
      this.addPeerModel.weightTx4 = this.getNumberValue(this.addPeerModel.weightTx4, this.addPeerPlaceholders.weightTx4);
      this.addPeerModel.weightRx4 = this.getNumberValue(this.addPeerModel.weightRx4, this.addPeerPlaceholders.weightRx4);
      this.addPeerModel.flapcheck4 = this.getNumberValue(this.addPeerModel.flapcheck4, this.addPeerPlaceholders.flapcheck4);
      this.addPeerModel.transport4 = this.addPeerModel.transport4 || TransportProtocol.Udp;
    }
  }

  clearOptionalLinkDefaults(field: string) {
    if (field === 'link3') {
      this.addPeerModel.weightTx3 = null;
      this.addPeerModel.weightRx3 = null;
      this.addPeerModel.failoverTimeout3 = null;
      this.addPeerModel.flapcheck3 = null;
      this.addPeerModel.transport3 = TransportProtocol.Udp;
    }

    if (field === 'link4') {
      this.addPeerModel.weightTx4 = null;
      this.addPeerModel.weightRx4 = null;
      this.addPeerModel.failoverTimeout4 = null;
      this.addPeerModel.flapcheck4 = null;
      this.addPeerModel.transport4 = TransportProtocol.Udp;
    }
  }

  openNewDpcModal(templateRef: TemplateRef<any>) {
    this.dpcDialogMode = 'add';
    this.dpcDialogTemplateRef = templateRef;
    this.editingDpcIndex = -1;
    this.dpcSubmitted = false;
    this.newDpcModel = this.getDefaultDpcModel();
    this.openDpcFormModal(templateRef);
  }

  openEditDpcModal(row: DpcServiceRow, index: number, templateRef: TemplateRef<any>) {
    this.dpcDialogMode = 'edit';
    this.dpcDialogTemplateRef = templateRef;
    this.editingDpcIndex = index;
    this.dpcSubmitted = false;
    this.newDpcModel = {
      ...this.getDefaultDpcModel(),
      name: row?.name || '',
      listenAddr: row?.listen_addr || '',
      forwardAddr: row?.forward_addr || '127.0.0.1:4021',
      serverUrl: row?.server_url || '',
      dpcIp: row?.dpc_ip || '',
      tunnelIp: row?.tunnel_ip || this.stripCidr(row?.dpc_ip) || '',
      mtu: row?.mtu || 1420,
      maxPacketSize: row?.maxpacketsize || 1420,
      fecRatios: row?.fec_ratios || this.newDpcModel.fecRatios,
      fecTimeout: row?.fec_timeout || 100,
      sdwanLink: row?.sdwan_link || '',
      qosRate: row?.qos_rate || '800mbit',
      authRequired: row?.auth_required !== false,
      enabled: row?.enabled !== false
    };
    this.openDpcFormModal(templateRef);
  }

  openDpcFormModal(templateRef: TemplateRef<any>) {
    this.dialog.open(templateRef, {
      width: this.dpcDialogMode === 'edit' ? '500px' : 'auto',
      maxWidth: 'calc(100vw - 16px)',
      maxHeight: this.dpcDialogMode === 'edit' ? '90vh' : undefined,
      panelClass: 'overlay-plus-dpc-dialog',
      autoFocus: false
    });
  }

  saveDpc() {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    this.dpcSubmitted = true;
    if (!this.isDpcFormValid()) {
      return;
    }

    const oldDpc = this.dpcServiceRows[this.editingDpcIndex];
    const dpcRow = this.getDpcRowFromModel();

    if (this.dpcDialogMode === 'add' && this.currentDeviceMac) {
      this.dialog.closeAll();
      this.startDpcCrudProcess('add');
      this.vceVhubService.addMidlayVhubDpc(this.currentDeviceMac, this.getDpcSubmitPayload(dpcRow)).subscribe(() => {
        this.completeSaveDpc(dpcRow, oldDpc);
        this.stopDpcCrudProcess();
      }, error => {
        this.stopDpcCrudProcess();
        this.restoreDpcFormModal();
        this.showOverlayPlusError('Unable to add DPC service.', error);
      });
      return;
    }

    if (this.dpcDialogMode === 'edit' && this.currentDeviceMac && oldDpc?.name) {
      this.dialog.closeAll();
      this.startDpcCrudProcess('edit');
      this.vceVhubService.editMidlayVhubDpc(this.currentDeviceMac, oldDpc.name, this.getDpcEditSubmitPayload(dpcRow)).subscribe(() => {
        this.completeSaveDpc(dpcRow, oldDpc);
        this.stopDpcCrudProcess();
      }, error => {
        this.stopDpcCrudProcess();
        this.restoreDpcFormModal();
        this.showOverlayPlusError('Unable to update DPC service.', error);
      });
      return;
    }

    this.completeSaveDpc(dpcRow, oldDpc);
  }

  completeSaveDpc(dpcRow: DpcServiceRow, oldDpc?: DpcServiceRow) {
    if (this.dpcDialogMode === 'edit' && this.editingDpcIndex > -1) {
      this.dpcServiceRows = this.dpcServiceRows.map((row, index) =>
        index === this.editingDpcIndex ? dpcRow : row
      );
      if (oldDpc?.name && oldDpc.name !== dpcRow.name) {
        this.tableRows = this.tableRows.map(row => row.dpc === oldDpc.name ? { ...row, dpc: dpcRow.name } : row);
      }
    } else {
      this.dpcServiceRows = [...this.dpcServiceRows, dpcRow];
    }
    if (!this.addPeerModel.dpcService || this.getSelectedDpcName(this.addPeerModel.dpcService) === oldDpc?.name) {
      this.addPeerModel.dpcService = `${dpcRow.name} (${dpcRow.server_url})`;
    }
    this.dialog.closeAll();
    this.toastr.success(this.dpcDialogMode === 'edit' ? 'DPC service updated successfully.' : 'DPC service added successfully.');
  }

  isDpcFormValid() {
    return !this.isDpcFieldInvalid('name') && !this.isDpcFieldInvalid('listenAddr');
  }

  isDpcFieldInvalid(field: 'name' | 'listenAddr') {
    return this.dpcSubmitted && !this.hasRequiredValue(this.newDpcModel[field]);
  }

  getDpcSubmitPayload(dpcRow: DpcServiceRow) {
    return {
      name: dpcRow.name,
      ...this.getDpcEditSubmitPayload(dpcRow)
    };
  }

  getDpcEditSubmitPayload(dpcRow: DpcServiceRow) {
    return {
      listen_addr: dpcRow.listen_addr,
      forward_addr: dpcRow.forward_addr || '',
      server_url: dpcRow.server_url,
      dpc_ip: dpcRow.dpc_ip,
      tunnel_ip: dpcRow.tunnel_ip === 'auto' ? '' : dpcRow.tunnel_ip,
      mtu: String(dpcRow.mtu),
      maxpacketsize: String(dpcRow.maxpacketsize),
      fec: dpcRow.fec_ratios || '',
      fec_timeout: String(dpcRow.fec_timeout),
      sdwan_link: dpcRow.sdwan_link,
      qos_rate: dpcRow.qos_rate,
      auth_required: dpcRow.auth_required ? '1' : '0',
      enabled: dpcRow.enabled ? '1' : '0'
    };
  }

  getDefaultDpcModel(): DpcModel {
    return {
      name: 'dpc1',
      listenAddr: '0.0.0.0:3121',
      forwardAddr: '127.0.0.1:4021',
      serverUrl: '',
      dpcIp: '',
      tunnelIp: '',
      mtu: 1460,
      maxPacketSize: 1420,
      fecRatios: '1:3,2:4,2:1,10:5,20:10,3:1,30:10,5:1,6:1',
      fecTimeout: 100,
      sdwanLink: '',
      qosRate: '800mbit',
      authRequired: true,
      enabled: true
    };
  }

  get dpcServerUrlOptions() {
    return Array.from(new Set(
      this.dpcServiceRows
        .map(row => String(row?.server_url || '').trim())
        .filter(serverUrl => !!serverUrl && serverUrl !== '-')
    ));
  }

  getDefaultDpcServerUrl() {
    return this.dpcServerUrlOptions[0] || 'vhub5-east-ca.rabbitrun.ca:3122';
  }

  getDpcRowFromModel(): DpcServiceRow {
    return {
      name: this.newDpcModel.name || '-',
      listen_addr: this.newDpcModel.listenAddr || '-',
      forward_addr: this.newDpcModel.forwardAddr || '-',
      server_url: this.newDpcModel.serverUrl || '-',
      dpc_ip: this.newDpcModel.dpcIp || '-',
      tunnel_ip: this.newDpcModel.tunnelIp || '-',
      mtu: this.newDpcModel.mtu || '-',
      maxpacketsize: this.newDpcModel.maxPacketSize || '-',
      fec_ratios: this.newDpcModel.fecRatios || '-',
      fec_timeout: this.newDpcModel.fecTimeout || '-',
      sdwan_link: this.newDpcModel.sdwanLink || '-',
      qos_rate: this.newDpcModel.qosRate || '-',
      auth_required: this.newDpcModel.authRequired,
      enabled: this.newDpcModel.enabled
    };
  }

  startDpcCrudProcess(process: 'add' | 'edit' | 'delete') {
    this.dpcCrudProcess = process;
    this.overlayPlusCrudLoading = true;
  }

  stopDpcCrudProcess() {
    this.dpcCrudProcess = '';
    this.overlayPlusCrudLoading = false;
  }

  startPeerCrudProcess(process: 'add' | 'edit' | 'delete') {
    this.peerCrudProcess = process;
    this.overlayPlusCrudLoading = true;
  }

  stopPeerCrudProcess() {
    this.peerCrudProcess = '';
    this.overlayPlusCrudLoading = false;
  }

  isDpcCrudLoading(process?: 'add' | 'edit' | 'delete') {
    return !!this.dpcCrudProcess && (!process || this.dpcCrudProcess === process);
  }

  isPeerCrudLoading(process?: 'add' | 'edit' | 'delete') {
    return !!this.peerCrudProcess && (!process || this.peerCrudProcess === process);
  }

  isOverlayCrudLoading() {
    return this.isDpcCrudLoading() || this.isPeerCrudLoading();
  }

  getOverlayCrudLoadingTitle() {
    if (this.isPeerCrudLoading('add')) {
      return 'Adding Peer';
    }

    if (this.isPeerCrudLoading('edit')) {
      return 'Updating Peer';
    }

    if (this.isPeerCrudLoading('delete')) {
      return 'Deleting Peer';
    }

    return this.getDpcCrudLoadingTitle();
  }

  getOverlayCrudLoadingMessage() {
    if (this.isPeerCrudLoading('add')) {
      return 'Saving the authenticated peer to the selected vHUB.';
    }

    if (this.isPeerCrudLoading('edit')) {
      return 'Saving the authenticated peer changes to the selected vHUB.';
    }

    if (this.isPeerCrudLoading('delete')) {
      return 'Deleting the authenticated peer from the selected vHUB.';
    }

    return this.getDpcCrudLoadingMessage();
  }

  getDpcCrudLoadingTitle() {
    if (this.dpcCrudProcess === 'add') {
      return 'Adding DPC';
    }

    if (this.dpcCrudProcess === 'edit') {
      return 'Updating DPC';
    }

    if (this.dpcCrudProcess === 'delete') {
      return 'Deleting DPC';
    }

    return 'Processing DPC';
  }

  getDpcCrudLoadingMessage() {
    if (this.dpcCrudProcess === 'add') {
      return 'Creating the DPC service on the selected vHUB.';
    }

    if (this.dpcCrudProcess === 'edit') {
      return 'Saving the DPC service changes to the selected vHUB.';
    }

    if (this.dpcCrudProcess === 'delete') {
      return 'Removing the DPC service from the selected vHUB.';
    }

    return 'Please wait while the DPC request finishes.';
  }

  getDpcSaveButtonText() {
    if (this.isDpcCrudLoading('add')) {
      return 'Adding DPC...';
    }

    if (this.isDpcCrudLoading('edit')) {
      return 'Updating DPC...';
    }

    return this.dpcDialogMode === 'edit' ? 'Save Changes' : 'Add DPC';
  }

  restoreDpcFormModal() {
    if (!this.dpcDialogTemplateRef) {
      return;
    }

    this.openDpcFormModal(this.dpcDialogTemplateRef);
  }

  restoreConfirmDeleteModal() {
    if (!this.confirmDeleteDialogTemplateRef || (this.confirmDeleteModel.type !== 'dpc' && this.confirmDeleteModel.type !== 'peer')) {
      return;
    }

    this.dialog.open(this.confirmDeleteDialogTemplateRef, {
      width: '360px',
      panelClass: 'overlay-plus-confirm-dialog',
      autoFocus: false
    });
  }

  restorePeerFormModal() {
    if (!this.peerDialogTemplateRef) {
      return;
    }

    this.openPeerDetailModal(this.peerDialogTemplateRef);
  }

  removeDpc(index: number) {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    const dpcName = this.dpcServiceRows[index]?.name || '';
    if (this.currentDeviceMac && dpcName) {
      this.dialog.closeAll();
      this.startDpcCrudProcess('delete');
      this.vceVhubService.deleteMidlayVhubDpc(this.currentDeviceMac, dpcName).subscribe(() => {
        this.completeRemoveDpc(index);
        this.dialog.closeAll();
        this.stopDpcCrudProcess();
      }, error => {
        this.stopDpcCrudProcess();
        this.restoreConfirmDeleteModal();
        this.showOverlayPlusError('Unable to delete DPC service.', error);
      });
      return;
    }

    this.completeRemoveDpc(index);
    this.dialog.closeAll();
  }

  completeRemoveDpc(index: number) {
    this.dpcServiceRows = this.dpcServiceRows.filter((_, rowIndex) => rowIndex !== index);
    this.addPeerModel.dpcService = '';
    this.updateAddPeerDefaults();
    this.toastr.success('DPC service deleted successfully.');
  }

  deletePeer(row: PeerTableRow | null) {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    const peerId = row?.peerGroupId || row?.id;
    const peerMac = row?.peerData?.mac || row?.mac || '';
    if (this.currentDeviceMac && peerMac) {
      this.dialog.closeAll();
      this.startPeerCrudProcess('delete');
      this.vceVhubService.deleteMidlayVhubPeer(this.currentDeviceMac, peerMac).subscribe(() => {
        this.completeDeletePeer(peerId);
        this.dialog.closeAll();
        this.stopPeerCrudProcess();
      }, error => {
        this.stopPeerCrudProcess();
        this.restoreConfirmDeleteModal();
        this.showOverlayPlusError('Unable to delete peer.', error);
      });
      return;
    }

    this.completeDeletePeer(peerId);
    this.dialog.closeAll();
  }

  completeDeletePeer(peerId: string | undefined) {
    this.tableRows = this.tableRows.filter(peerRow => (peerRow.peerGroupId || peerRow.id) !== peerId);
    this.toastr.success('Peer deleted successfully.');
  }

  openConfirmDeleteModal(type: 'dpc' | 'peer', row: DpcServiceRow | PeerTableRow, index: number, templateRef: TemplateRef<any>) {
    this.confirmDeleteDialogTemplateRef = templateRef;
    const isDpc = type === 'dpc';
    const targetName = isDpc
      ? (row as DpcServiceRow)?.name || '-'
      : (row as PeerTableRow)?.mac || (row as PeerTableRow)?.hostname || '-';
    this.confirmDeleteModel = {
      type,
      title: isDpc ? 'Remove DPC' : 'Delete Peer',
      message: isDpc
        ? 'Remove this DPC service from the vHub overlay plus table?'
        : 'Delete this authorized peer from the vHub overlay plus table?',
      targetName,
      row,
      index
    };
    this.dialog.open(templateRef, {
      width: '360px',
      panelClass: 'overlay-plus-confirm-dialog',
      autoFocus: false
    });
  }

  confirmDelete() {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    if (this.confirmDeleteModel.type === 'dpc') {
      this.removeDpc(this.confirmDeleteModel.index);
      return;
    }

    if (this.confirmDeleteModel.type === 'peer') {
      this.deletePeer(this.confirmDeleteModel.row as PeerTableRow);
      return;
    }

    this.dialog.closeAll();
  }

  openPeerShowModal(row: PeerTableRow, templateRef: TemplateRef<any>) {
    this.peerDialogTemplateRef = templateRef;
    this.editingPeerId = row?.peerGroupId || row?.id || '';
    const peer = row?.peerData || {};
    this.editingPeerData = peer;
    const provision = this.getPeerProvision(peer);
    const assignedDpcIp = this.getAssignedValue(row, '') || row?.assignedIpLines?.[0]?.value || '';
    this.peerDetailModel = {
      ...this.getBlankPeerDetailModel(),
      clientDeviceType: this.getFirstValue(peer?.client_model, peer?.clientModel, peer?.model),
      provisioned: this.getPeerProvisionedText(peer),
      mac: peer?.mac || row?.mac || '',
      hostname: peer?.hostname || row?.hostname || '',
      dpcAllowed: this.getDpcNameFromValue(this.getFirstValue(peer?.dpc_allowed, row?.dpc)),
      encrypt: this.getEncryptLabel(this.getFirstValue(peer?.encrypt, peer?.encryption)),
      psk: peer?.psk || '',
      dpcIp: this.getPeerConfiguredDpcIp(peer) || this.normalizePeerDpcIpForEdit(assignedDpcIp),
      wanId: peer?.wan_id || row?.wanId || '',
      wanSubnet: this.getFirstValue(peer?.wan_subnet, peer?.wan_subnets, this.getAssignedValue(row, 'wan:')),
      dmzIps: this.getFirstValue(peer?.dmz_ip, peer?.dmz_ips),
      dmzSubnets: this.getFirstValue(peer?.dmz_subnet, peer?.dmz_subnets),
      mode: provision?.ulay_mode ? this.getPeerDetailMode(provision?.ulay_mode) : '',
      distribution: this.getDistributionLabel(provision?.distribution || peer?.distribution),
      maxPacketSize: this.getOptionalNumber(this.getFirstValue(provision?.maxpacketsize, provision?.max_packet_size, peer?.maxpacketsize, peer?.max_packet_size)),
      rseqHoldMs: this.getOptionalNumber(this.getFirstValue(provision?.rseq_hold_ms, peer?.rseq_hold_ms)),
      rseqThinkAcks: this.getOptionalNumber(this.getFirstValue(provision?.rseq_thin_acks, provision?.rseq_think_acks, peer?.rseq_thin_acks, peer?.rseq_think_acks)),
      fecMaxBlocks: this.getOptionalNumber(this.getFirstValue(provision?.maxblocks, peer?.maxblocks)),
      fecGroupHold: this.getOptionalNumber(this.getFirstValue(provision?.fec_group_hold, peer?.fec_group_hold)),
      fecGroupHoldDepth: this.getFirstValue(provision?.fec_group_hold_depth, peer?.fec_group_hold_depth),
      defaultFec: this.getFirstValue(provision?.fec, provision?.qfec, provision?.default_qfec, peer?.fec, peer?.qfec, peer?.default_qfec),
      links: this.getPeerDetailLinks(peer)
    };
    this.peerFecLanes = this.getPeerDetailFecLanes(peer);
    this.showPeerPsk = false;
    this.peerPskLoading = false;
    this.openPeerDetailModal(templateRef);
    this.loadPeerPsk(false);
  }

  openPeerDetailModal(templateRef: TemplateRef<any>) {
    this.dialog.open(templateRef, {
      width: 'auto',
      maxWidth: '640px',
      maxHeight: 'calc(100vh - 24px)',
      panelClass: 'overlay-plus-peer-dialog',
      autoFocus: false
    });
  }

  getPeerProvision(peer: any) {
    return [
      peer?.team_config,
      peer?.teamConfig,
      peer?.config,
      peer?.provision,
      peer?.vce_provision
    ].reduce((mergedProvision, provisionPart) => {
      if (!provisionPart || typeof provisionPart !== 'object') {
        return mergedProvision;
      }

      Object.keys(provisionPart).forEach(key => {
        const value = provisionPart[key];
        if (value !== null && value !== undefined && value !== '') {
          mergedProvision[key] = value;
        }
      });

      return mergedProvision;
    }, {} as any);
  }

  getPeerProvisionedText(peer: any) {
    const value = this.getFirstValue(
      peer?.provisioned,
      peer?.provisioned_at,
      peer?.provisionedAt,
      peer?.created_at,
      peer?.createdAt
    );
    return value ? `Provisioned: ${value}` : '';
  }

  getFirstValue(...values: any[]) {
    const value = values.find(item => item !== null && item !== undefined && item !== '');
    return value === undefined ? '' : value;
  }

  getBlankPeerDetailModel(): PeerDetailModel {
    return {
      clientDeviceType: '',
      provisioned: '',
      mac: '',
      hostname: '',
      dpcAllowed: '',
      encrypt: '',
      psk: '',
      dpcIp: '',
      wanId: '',
      wanSubnet: '',
      dmzIps: '',
      dmzSubnets: '',
      mode: '',
      distribution: '',
      maxPacketSize: null,
      rseqHoldMs: null,
      rseqThinkAcks: null,
      fecMaxBlocks: null,
      fecGroupHold: null,
      fecGroupHoldDepth: '',
      defaultFec: '',
      links: this.getBlankPeerLinks()
    };
  }

  getBlankPeerLinks(): PeerLinkModel[] {
    return ['Link 1 (Primary)', 'Link 2 (Backup)', 'Link 3 (Optional)', 'Link 4 (Optional)']
      .map(label => ({
        label,
        name: '[not set]',
        transport: '',
        path: '',
        tx: null,
        rx: null,
        timeout: null,
        flapcheck: null
      }));
  }

  getPeerDetailLinks(peer: any) {
    const labels = ['Link 1 (Primary)', 'Link 2 (Backup)', 'Link 3 (Optional)', 'Link 4 (Optional)'];
    const provision = this.getPeerProvision(peer);
    const provisionLinkNames = this.getCsvParts(provision?.link_names);
    const provisionRouteTables = this.getCsvParts(provision?.route_tables);
    const provisionWeights = this.getCsvParts(provision?.weights);
    const provisionTimeouts = this.getCsvParts(provision?.timeouts);
    const provisionFlapchecks = this.getCsvParts(provision?.flapcheck_period);
    const provisionTransports = this.getCsvParts(provision?.transport);
    const sourceLinks = Array.isArray(peer?.links) && peer.links.length ? peer.links : [];
    const links = labels.map((_, index) => ({
        ...(sourceLinks[index] || {}),
        name: this.getFirstValue(sourceLinks[index]?.name, sourceLinks[index]?.link, sourceLinks[index]?.link_name, provisionLinkNames[index]),
        path: this.getFirstValue(sourceLinks[index]?.path, sourceLinks[index]?.route_table, sourceLinks[index]?.routeTable, sourceLinks[index]?.interface, provisionRouteTables[index]),
        transport: this.getPeerDetailTransport(this.getFirstValue(sourceLinks[index]?.transport, sourceLinks[index]?.protocol, provisionTransports[index])),
        ...this.getWeightPair(provisionWeights[index]),
        timeout: this.getFirstValue(sourceLinks[index]?.timeout, sourceLinks[index]?.failover_timeout, sourceLinks[index]?.failoverTimeout, provisionTimeouts[index]),
        flapcheck: this.getFirstValue(sourceLinks[index]?.flapcheck, sourceLinks[index]?.flapcheck_period, sourceLinks[index]?.flapcheckPeriod, provisionFlapchecks[index])
      }));

    return labels.map((label, index) => {
      const link: any = links[index] || {};
      return {
        label,
        name: link?.name || '[not set]',
        transport: link?.transport || '',
        path: link?.path || '',
        tx: this.getOptionalNumber(this.getFirstValue(link?.weight, link?.tx, link?.tx_weight, link?.txWeight)),
        rx: this.getOptionalNumber(this.getFirstValue(link?.rx_weight, link?.rx, link?.rxWeight)),
        timeout: this.getOptionalNumber(link?.timeout),
        flapcheck: this.getOptionalNumber(link?.flapcheck)
      };
    });
  }

  getEditablePeerDetailLinks() {
    return this.peerDetailModel.links.filter((link, index) => index < 2 || this.isPeerDetailLinkSet(link));
  }

  openVceAddOverlayModal(templateRef: TemplateRef<any>) {
    this.vceAddProvisioningError = '';
    this.dialog.open(templateRef, {
      width: 'auto',
      maxWidth: '460px',
      panelClass: 'overlay-plus-vce-add-dialog',
      autoFocus: false
    });
  }

  validateVceAddOverlayProvisioning() {
    const provisioning = this.parseVceProvisioningBlock(this.vceAddProvisioningBlock);
    const missingFields = ['REMOTE_ADDR', 'DPC_GATEWAY', 'DPC_IP'].filter(field => !provisioning[field]);

    if (missingFields.length) {
      this.vceAddProvisioningError = `Missing required field(s): ${missingFields.join(', ')}`;
      return;
    }

    this.applyVceProvisioningBlock(provisioning);
    this.vceAddProvisioningError = '';
    this.dialog.closeAll();
  }

  parseVceProvisioningBlock(block: string) {
    return String(block || '')
      .split(/\r?\n/)
      .map(line => line.trim())
      .filter(line => line && !line.startsWith('#'))
      .reduce((parsed, line) => {
        const separatorIndex = line.indexOf('=');
        if (separatorIndex < 0) {
          return parsed;
        }

        const key = line.slice(0, separatorIndex).trim().toUpperCase();
        const value = line.slice(separatorIndex + 1).trim();
        if (key) {
          parsed[key] = value;
        }
        return parsed;
      }, {} as { [key: string]: string });
  }

  applyVceProvisioningBlock(provisioning: { [key: string]: string }) {
    const dpcIp = provisioning.DPC_IP || '-';
    const remoteAddr = provisioning.REMOTE_ADDR || '-';
    const dpcGateway = provisioning.DPC_GATEWAY || '-';
    const mtu = provisioning.MTU || this.interfaceRow?.mtu || '-';
    const keepalive = provisioning.KEEPALIVE || '10';
    const fec = provisioning.FEC || 'auto';
    const sdwanLink = this.interfaceRow?.sdwan_link || 'link1';
    const iface = this.interfaceRow?.iface || 'rrt1';
    const distribution = this.interfaceRow?.distribution || DistributionMode.PerPacket;
    const maxPacketSize = this.interfaceRow?.max_packet_size || this.addPeerPlaceholders.maxPacketSize;

    this.overlayPlusTeam = {
      ...(this.overlayPlusTeam || {}),
      name: iface,
      vce_provision: {
        ...(this.getTeamConfig(this.overlayPlusTeam || {})),
        iface,
        sdwan_link: sdwanLink,
        dpc_ip: dpcIp,
        remote_addr: remoteAddr,
        dpc_gateway: dpcGateway,
        mtu,
        keepalive,
        fec,
        maxpacketsize: maxPacketSize,
        distribution
      }
    };

    this.interfaceRow = {
      iface,
      sdwan_link: sdwanLink,
      tunnel_ip: dpcIp,
      dmz_ip: this.interfaceRow?.dmz_ip || '',
      server_url: remoteAddr,
      dpc_gateway: dpcGateway,
      ip4_table: this.interfaceRow?.ip4_table || iface,
      mtu,
      max_packet_size: maxPacketSize,
      distribution
    };
    this.interfaceRows = [this.interfaceRow];

    this.connectionRows = [{
      sdwan_link: sdwanLink,
      endpoint: remoteAddr,
      assigned_ips: this.cleanIpValue(dpcIp) || 'auto',
      link_lane: sdwanLink,
      assuranceLines: [
        `${sdwanLink} [udp]`,
        `FEC: ${fec} | Keepalive: ${keepalive}s`
      ],
      weight: '-',
      wan_id: '-',
      uptime: '-',
      state: 'PENDING'
    }];
  }

  openVceOverlayEditModal(templateRef: TemplateRef<any>) {
    const team = this.overlayPlusTeam || {};
    const teamConfig = this.getTeamConfig(team);
    const editSource = {
      ...team,
      vce_provision: teamConfig,
      links: Array.isArray(team?.links) ? team.links : []
    };

    this.vceEditModel = {
      title: team?.name || teamConfig?.iface || 'team1',
      readOnly: {
        deviceModel: this.getFirstValue(
          this.currentDevice?.deviceMetaInfo?.deviceModel,
          this.currentDevice?.deviceMetaInfo?.model,
          this.currentDevice?.model,
          team?.client_model,
          'RRT-300-LTE'
        ),
        dpcIp: this.getFirstValue(teamConfig?.dpc_ip, interfaceRowValue(this.interfaceRow, 'tunnel_ip')),
        remoteAddr: this.getFirstValue(teamConfig?.remote_addr, interfaceRowValue(this.interfaceRow, 'server_url')),
        dpcGateway: this.getFirstValue(teamConfig?.dpc_gateway, interfaceRowValue(this.interfaceRow, 'dpc_gateway')),
        mtu: this.getFirstValue(teamConfig?.mtu, interfaceRowValue(this.interfaceRow, 'mtu')),
        keepalive: this.getFirstValue(teamConfig?.keepalive, team?.keepalive, 10)
      },
      mode: this.getPeerDetailMode(this.getFirstValue(teamConfig?.mode, team?.mode)),
      distribution: this.getDistributionLabel(this.getFirstValue(teamConfig?.distribution, team?.distribution)) || DistributionMode.PerPacket,
      maxPacketSize: this.getOptionalNumber(this.getFirstValue(teamConfig?.maxpacketsize, teamConfig?.max_packet_size, interfaceRowValue(this.interfaceRow, 'max_packet_size'))),
      rseqHoldMs: this.getOptionalNumber(this.getFirstValue(teamConfig?.rseq_hold_ms, team?.rseq_hold_ms)),
      rseqThinkAcks: this.getOptionalNumber(this.getFirstValue(teamConfig?.rseq_thin_acks, teamConfig?.rseq_think_acks, team?.rseq_thin_acks)),
      fecMaxBlocks: this.getOptionalNumber(this.getFirstValue(teamConfig?.maxblocks, team?.maxblocks)),
      fecGroupHold: this.getOptionalNumber(this.getFirstValue(teamConfig?.fec_group_hold, team?.fec_group_hold)),
      fecGroupHoldDepth: this.getFirstValue(teamConfig?.fec_group_hold_depth, team?.fec_group_hold_depth),
      defaultFec: this.getFirstValue(
        teamConfig?.fec, teamConfig?.qfec, teamConfig?.default_qfec,
        team?.fec, team?.fec_ratio, team?.qfec, team?.default_qfec
      ),
      links: this.getPeerDetailLinks(editSource),
      fecLanes: this.getPeerDetailFecLanes(editSource)
    };

    this.dialog.open(templateRef, {
      width: 'auto',
      maxWidth: '680px',
      maxHeight: 'calc(100vh - 24px)',
      panelClass: 'overlay-plus-vce-edit-dialog',
      autoFocus: false
    });

    function interfaceRowValue(row: InterfaceRow | null, key: keyof InterfaceRow) {
      return row ? row[key] : '';
    }
  }

  saveVceOverlayEdit() {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    this.overlayPlusCrudLoading = true;
    if (this.interfaceRow) {
      this.interfaceRow = {
        ...this.interfaceRow,
        max_packet_size: this.vceEditModel.maxPacketSize || this.interfaceRow.max_packet_size,
        distribution: this.vceEditModel.distribution || this.interfaceRow.distribution
      };
      this.interfaceRows = [
        this.interfaceRow,
        ...this.interfaceRows.slice(1)
      ];
    }

    const editableLinks = this.vceEditModel.links || [];
    this.connectionRows = this.connectionRows.map((row, index) => {
      const link = editableLinks[index];
      if (!link) {
        return row;
      }

      return {
        ...row,
        link_lane: link.name || row.link_lane,
        weight: link.tx != null || link.rx != null ? `${this.toNumber(link.tx)}/${this.toNumber(link.rx)}` : row.weight
      };
    });

    setTimeout(() => {
      this.overlayPlusCrudLoading = false;
      this.dialog.closeAll();
      this.toastr.success('VCE overlay updated successfully.');
    }, 250);
  }

  addVceEditFecLane() {
    const laneNumbers = (this.vceEditModel.fecLanes || []).map((lane, index) => lane.laneNumber || this.getPeerFecLaneNumber(lane, index));
    const nextLaneNumber = laneNumbers.length ? Math.max(...laneNumbers) + 1 : 1;
    this.vceEditModel.fecLanes = [
      ...(this.vceEditModel.fecLanes || []),
      { lane: `Lane ${nextLaneNumber}`, laneNumber: nextLaneNumber, origDupes: '0:0', mode: 'raw', steer: String(nextLaneNumber) }
    ];
  }

  removeVceEditFecLane(index: number) {
    this.vceEditModel.fecLanes = (this.vceEditModel.fecLanes || []).filter((_, laneIndex) => laneIndex !== index);
  }

  getVceEditQosLabel(lane: PeerFecLaneModel) {
    const steer = String(lane?.steer || lane?.laneNumber || '').trim();
    return steer ? `QoS Class ${steer} - Selected` : 'QoS Class - Selected';
  }

  isPeerDetailLinkSet(link: any) {
    const name = String(link?.name || '').trim();
    return !!name && name !== '[not set]';
  }

  getPeerDetailFecLanes(peer: any): PeerFecLaneModel[] {
    const lanes = this.getPeerDetailProvisionLanes(peer);
    if (!lanes.length) {
      return [];
    }

    return lanes
      .map((lane, index) => {
        const laneNumber = this.getPeerFecLaneNumber(lane, index);
        return {
          lane: this.getPeerFecLaneLabel(lane, index),
          laneNumber,
          origDupes: this.getPeerFecLaneOrigDupes(lane),
          mode: this.getFirstValue(lane?.mode, lane?.mode_str, lane?.class) || 'raw',
          steer: this.getPeerFecLaneSteerClass(
            this.getFirstValue((lane as any)?.steer, (lane as any)?.fec_steer, (lane as any)?.steering),
            laneNumber
          )
        };
      })
      .filter(lane => !this.isHiddenDefaultPeerFecLane(lane))
      .sort((firstLane, secondLane) => (firstLane.laneNumber || 0) - (secondLane.laneNumber || 0));
  }

  isHiddenDefaultPeerFecLane(lane: PeerFecLaneModel) {
    const laneNumber = lane?.laneNumber || this.getPeerFecLaneNumber(lane, 0);
    const mode = String(lane?.mode || '').trim().toLowerCase();
    return laneNumber === 15 || mode === 'default';
  }

  getPeerFecLaneOrigDupes(lane: any) {
    const configured = this.getFirstValue(lane?.orig_dupes, lane?.origDupes, lane?.qfec, lane?.fec, lane?.fec_ratio);
    if (configured) {
      return String(configured);
    }
    if (lane?.k != null && lane?.n != null) {
      const original = this.toNumber(lane.k);
      const dupes = Math.max(this.toNumber(lane.n) - original, 0);
      return `${original}:${dupes}`;
    }
    return '0:0';
  }

  getPeerFecLaneSteerClass(value: any, laneNumber: number) {
    const steer = String(value ?? '').trim();
    if (!steer) {
      return String(laneNumber);
    }
    const parts = steer.split(':').map(part => part.trim()).filter(part => !!part);
    return parts[parts.length - 1] || String(laneNumber);
  }

  getPeerFecLaneLabel(lane: any, index: number) {
    const laneValue = this.getPeerFecLaneNumber(lane, index);
    return String(laneValue).toLowerCase().startsWith('lane') ? String(laneValue) : `Lane ${laneValue}`;
  }

  getPeerFecLaneNumber(lane: any, index: number) {
    const laneValue = lane?.lane ?? lane?.laneNumber ?? lane?.lane_q ?? lane?.laneQ ?? lane?.q;
    const match = String(laneValue || '').match(/\d+/);
    const laneNumber = match ? Number(match[0]) : Number(laneValue);
    return Number.isFinite(laneNumber) && laneNumber > 0 ? laneNumber : index + 1;
  }

  getPeerDetailProvisionLanes(peer: any): any[] {
    const provision = this.getPeerProvision(peer);
    const lanes = this.getPeerLanes(peer).filter(lane => !lane?.default);
    const provisionLanes = this.getPeerDetailProvisionFecQLanes(provision);

    if (!lanes.length) {
      return provisionLanes;
    }

    if (!provisionLanes.length) {
      return lanes;
    }

    const laneMap = lanes.reduce((mappedLanes, lane, index) => ({
      ...mappedLanes,
      [this.getPeerFecLaneNumber(lane, index)]: lane
    }), {} as { [key: number]: any });

    provisionLanes.forEach((lane, index) => {
      const laneNumber = this.getPeerFecLaneNumber(lane, index);
      laneMap[laneNumber] = {
        ...(laneMap[laneNumber] || {}),
        ...lane,
        lane: laneNumber
      };
    });

    return Object.keys(laneMap)
      .map(laneNumber => laneMap[Number(laneNumber)])
      .sort((firstLane, secondLane) => this.getPeerFecLaneNumber(firstLane, 0) - this.getPeerFecLaneNumber(secondLane, 0));
  }

  getPeerDetailProvisionFecQLanes(provision: any): any[] {
    const provisionLanes = [];
    for (let laneIndex = 1; laneIndex <= 16; laneIndex++) {
      const origDupes = this.getFirstValue(provision[`fec_q${laneIndex}`]);
      if (!origDupes) {
        continue;
      }
      provisionLanes.push({
        lane: laneIndex,
        orig_dupes: origDupes,
        mode: this.getFirstValue(provision[`fec_q${laneIndex}_mode`]) || 'raw',
        steer: this.getFirstValue(provision[`fec_q${laneIndex}_steer`]) || laneIndex
      });
    }
    return provisionLanes;
  }

  savePeerDetail(applyConfirmTemplate?: TemplateRef<any>) {
    if (this.overlayPlusCrudLoading) {
      return;
    }

    if (!this.editingPeerId) {
      this.dialog.closeAll();
      return;
    }

    const peer = this.getPeerFromDetailModel();
    const submitPayload = this.getPeerDetailSubmitPayload(peer);
    const peerMac = this.peerDetailModel.mac || peer?.mac || '';
    const applyContext = this.getPeerDetailApplyContext(peerMac, submitPayload);

    if (this.currentDeviceMac && peerMac) {
      this.dialog.closeAll();
      this.startPeerCrudProcess('edit');
      this.vceVhubService.editMidlayVhubPeer(this.currentDeviceMac, peerMac, this.getApplyEditPeerPayload(applyContext)).subscribe(() => {
        this.stopPeerCrudProcess();
        applyContext.peerSavedOnVhub = true;
        this.completePeerDetailSave(peer);

        if (applyConfirmTemplate) {
          this.openApplyActivationConfirm(applyConfirmTemplate, applyContext);
          return;
        }

        this.startApplyProcess(applyContext);
      }, error => {
        this.stopPeerCrudProcess();
        this.restorePeerFormModal();
        this.showOverlayPlusError('Unable to update peer.', error);
      });
      return;
    }

    this.completePeerDetailSave(peer);

    if (applyConfirmTemplate) {
      this.openApplyActivationConfirm(applyConfirmTemplate, applyContext);
      return;
    }

    this.startApplyProcess(applyContext);
  }

  applyPeerDetailToDevice(peerMac: string, submitPayload: any) {
    this.startApplyProcess(this.getPeerDetailApplyContext(peerMac, submitPayload));
  }

  getPeerDetailApplyContext(peerMac: string, submitPayload: any): ApplyProcessContext {
    return {
      type: 'editPeer',
      vhubMac: this.currentDeviceMac,
      peerMac,
      vceMac: peerMac,
      team: this.getPeerTeam(this.editingPeerData || submitPayload),
      dpc: submitPayload?.dpc_allowed || '',
      submitPayload,
      existingVceProvision: this.getExistingVceProvision(peerMac)
    };
  }

  completePeerDetailSave(peer: any) {
    const peerRows = this.getPeerDisplayRows(peer, this.tableRows.length)
      .map(row => ({ ...row, id: this.editingPeerId, peerGroupId: this.editingPeerId }));
    const firstPeerIndex = this.tableRows.findIndex(row => (row.peerGroupId || row.id) === this.editingPeerId);
    const remainingRows = this.tableRows.filter(row => (row.peerGroupId || row.id) !== this.editingPeerId);
    const insertIndex = firstPeerIndex > -1 ? firstPeerIndex : remainingRows.length;
    this.tableRows = [
      ...remainingRows.slice(0, insertIndex),
      ...peerRows,
      ...remainingRows.slice(insertIndex)
    ];
    this.editingPeerId = '';
    this.editingPeerData = null;
    this.dialog.closeAll();
    this.toastr.success('Peer updated successfully.');
  }

  openApplyActivationConfirm(templateRef: TemplateRef<any>, applyContext: ApplyProcessContext) {
    this.pendingApplyActivation = applyContext;
    this.dialog.open(templateRef, {
      width: '420px',
      panelClass: 'overlay-plus-confirm-dialog',
      autoFocus: false
    });
  }

  confirmApplyActivation(processTemplate?: TemplateRef<any>) {
    const applyContext = this.pendingApplyActivation;
    this.pendingApplyActivation = null;

    if (applyContext) {
      if (processTemplate) {
        this.openApplyAutomaticProcess(processTemplate);
      }
      this.startApplyProcess(applyContext);
      return;
    }

    this.dialog.closeAll();
  }

  openApplyAutomaticProcess(templateRef: TemplateRef<any>) {
    this.dialog.closeAll();
    this.dialog.open(templateRef, {
      width: '620px',
      panelClass: 'overlay-plus-apply-process-dialog',
      autoFocus: false,
      disableClose: true
    });
  }

  startApplyProcess(context: ApplyProcessContext | null) {
    if (!context) {
      this.dialog.closeAll();
      return;
    }

    this.applyProcessContext = context;
    this.applyProcessSteps = this.getDefaultApplyProcessSteps();
    this.applyProcessBusy = false;
    this.applyProcessComplete = false;
    if (context.peerSavedOnVhub) {
      this.markApplyProcessStepsCompleted(['collect', 'validate']);
      this.runApplyProcessFrom('authorize');
      return;
    }

    this.runApplyProcessFrom('collect');
  }

  getDefaultApplyProcessSteps(): ApplyProcessStep[] {
    return [
      {
        key: 'collect',
        title: 'Preview Peer Payload',
        description: 'Preview the peer payload before writing it to the vHUB.',
        status: 'pending'
      },
      {
        key: 'validate',
        title: 'Add Peer on vHUB',
        description: 'Save the peer settings to the selected vHUB.',
        status: 'pending'
      },
      {
        key: 'authorize',
        title: 'Generate Provisioning',
        description: 'Generate the provisioning preview for the selected peer and DPC.',
        status: 'pending'
      },
      {
        key: 'vceProvision',
        title: 'Validate on vCE',
        description: 'Validate the provisioning on the target vCE.',
        status: 'pending'
      },
      {
        key: 'assertOk',
        title: 'Apply on vCE',
        description: 'Apply provisioning on the target vCE with validation enabled.',
        status: 'pending'
      },
      {
        key: 'saveDevice',
        title: 'Refresh State',
        description: 'Refresh vHUB and vCE Overlay Plus state after apply.',
        status: 'pending'
      }
    ];
  }

  runApplyProcessFrom(stepKey: ApplyProcessStepKey) {
    if (!this.applyProcessContext || this.applyProcessBusy) {
      return;
    }

    const orderedSteps: ApplyProcessStepKey[] = ['collect', 'validate', 'authorize', 'vceProvision', 'assertOk', 'saveDevice'];
    const startIndex = orderedSteps.indexOf(stepKey);
    if (startIndex < 0) {
      return;
    }

    this.resetApplyProcessStepsFrom(startIndex);
    this.runApplyProcessStep(orderedSteps, startIndex);
  }

  resetApplyProcessStepsFrom(startIndex: number) {
    this.applyProcessSteps = this.applyProcessSteps.map((step, index) => {
      if (index < startIndex && step.status === 'completed') {
        return step;
      }
      if (index >= startIndex) {
        return { ...step, status: 'pending', error: '' };
      }
      return step;
    });
    this.applyProcessComplete = false;
  }

  markApplyProcessStepsCompleted(stepKeys: ApplyProcessStepKey[]) {
    this.applyProcessSteps = this.applyProcessSteps.map(step => stepKeys.indexOf(step.key) > -1
      ? { ...step, status: 'completed', error: '' }
      : step);
  }

  runApplyProcessStep(orderedSteps: ApplyProcessStepKey[], index: number) {
    if (!this.applyProcessContext || index >= orderedSteps.length) {
      this.applyProcessBusy = false;
      this.applyProcessComplete = true;
      this.toastr.success(this.applyProcessContext?.type === 'editPeer'
        ? 'Peer update applied to device successfully.'
        : 'Peer provisioning applied to device successfully.');
      return;
    }

    const stepKey = orderedSteps[index];
    this.setApplyProcessStepStatus(stepKey, 'active');
    this.applyProcessBusy = true;

    const finishStep = () => {
      this.setApplyProcessStepStatus(stepKey, 'completed');
      this.applyProcessBusy = false;
      setTimeout(() => this.runApplyProcessStep(orderedSteps, index + 1), 260);
    };
    const failStep = (error: any) => {
      this.setApplyProcessStepStatus(stepKey, 'failed', this.getApplyErrorMessage(error));
      this.applyProcessBusy = false;
      this.showOverlayPlusError('Overlay Plus operation failed.', error);
    };

    if (stepKey === 'collect') {
      const error = this.validateApplyCollectStep() || this.validateApplyPayloadStep();
      if (error) {
        failStep(error);
        return;
      }
    }

    this.callApplyProcessStepApi(stepKey).subscribe(res => {
      this.applyProcessContext.result = res;
      this.applyProcessContext.responses = {
        ...(this.applyProcessContext.responses || {}),
        [stepKey]: res
      };
      if ((stepKey === 'vceProvision' || stepKey === 'assertOk') && !this.isApplyResultOk(res)) {
        failStep(stepKey === 'vceProvision'
          ? 'VCE validation result was not OK. Retry validation.'
          : 'VCE apply result was not OK. Retry apply.');
        return;
      }
      finishStep();
    }, failStep);
  }

  callApplyProcessStepApi(stepKey: ApplyProcessStepKey): Observable<any> {
    const context = this.applyProcessContext;
    const peerMac = context.peerMac;
    const vceMac = context.vceMac || context.peerMac;
    const dpcPayload = this.getApplyDpcPayload(context);

    if (stepKey === 'collect') {
      return this.vceVhubService.previewMidlayVhubPeer(context.vhubMac, this.getApplyPreviewPayload(context));
    }

    if (stepKey === 'validate') {
      return context.type === 'editPeer'
        ? this.vceVhubService.editMidlayVhubPeer(context.vhubMac, peerMac, this.getApplyEditPeerPayload(context))
        : this.vceVhubService.addMidlayVhubPeer(context.vhubMac, this.getApplyAddPeerPayload(context));
    }

    if (stepKey === 'authorize') {
      return this.vceVhubService.generateMidlayVhubPeerProvisioning(context.vhubMac, peerMac, dpcPayload);
    }

    if (stepKey === 'vceProvision') {
      return this.vceVhubService.validateMidlayVhubPeerVce(context.vhubMac, peerMac, vceMac, dpcPayload);
    }

    if (stepKey === 'assertOk') {
      return this.vceVhubService.applyMidlayVhubPeerVce(
        context.vhubMac,
        peerMac,
        vceMac,
        this.getApplyVcePayload(context, dpcPayload)
      );
    }

    return forkJoin({
      vhub: this.vceVhubService.getMidlayVhubState(context.vhubMac),
      vce: this.vceVhubService.getMidlayVceState(vceMac, this.getApplyTeam(context) || 'team2')
    });
  }

  getApplyPreviewPayload(context: ApplyProcessContext) {
    return {
      generate_psk: false,
      peer: this.getApplyPeerPayload(context)
    };
  }

  getApplyAddPeerPayload(context: ApplyProcessContext) {
    return {
      vce_mac: context.vceMac || context.peerMac,
      peer: this.getApplyPeerPayload(context)
    };
  }

  getApplyEditPeerPayload(context: ApplyProcessContext) {
    const peer = this.getApplyPeerPayload(context);
    delete peer.mac;
    return {
      team: this.getApplyTeam(context),
      peer
    };
  }

  getApplyTeam(context: ApplyProcessContext) {
    return context.team || this.getPeerTeam(context.submitPayload) || '';
  }

  getPeerTeam(peer: any) {
    const team = this.getFirstValue(
      peer?.team,
      peer?.team_name,
      peer?.teamName,
      peer?.team_config?.team,
      peer?.teamConfig?.team,
      peer?.vce_provision?.team
    );
    const normalizedTeam = String(team || '').trim().toLowerCase();
    return /^team[1-9]\d*$/.test(normalizedTeam) ? normalizedTeam : '';
  }

  getApplyDpcPayload(context: ApplyProcessContext) {
    const payload: any = {
      dpc: context.dpc || context.submitPayload?.dpc_allowed || this.getSelectedDpcName(this.addPeerModel.dpcService)
    };
    const team = context.type === 'editPeer' ? this.getApplyTeam(context) : '';
    if (team) {
      payload.team = team;
    }
    return payload;
  }

  getApplyVcePayload(context: ApplyProcessContext, dpcPayload = this.getApplyDpcPayload(context)) {
    const payload: any = {
      ...dpcPayload,
      validate_first: false
    };
    const team = context.type === 'editPeer' ? this.getApplyTeam(context) : '';
    if (team) {
      payload.replace_team = team;
    }
    return payload;
  }

  getApplyPeerPayload(context: ApplyProcessContext) {
    const payload = context.submitPayload || {};
    const savedConfiguration = { ...payload };
    delete savedConfiguration.vce_mac;
    const peer: any = {
      ...savedConfiguration,
      mac: context.peerMac,
      dpc_allowed: payload.dpc_allowed || context.dpc || ''
    };

    Object.keys(peer).forEach(key => {
      if (peer[key] === undefined || peer[key] === null ||
        (typeof peer[key] === 'string' && peer[key].trim() === '')) {
        delete peer[key];
      }
    });

    return peer;
  }

  retryApplyProcessStep(stepKey: ApplyProcessStepKey) {
    const retryStep = stepKey === 'assertOk' ? 'vceProvision' : stepKey;
    this.runApplyProcessFrom(retryStep);
  }

  chooseExistingVceProvision(updateExisting: boolean) {
    if (!this.applyProcessContext) {
      return;
    }

    this.applyProcessContext.updateExistingVceProvision = updateExisting;
    this.runApplyProcessFrom('vceProvision');
  }

  validateApplyCollectStep() {
    if (!this.applyProcessContext?.vhubMac) {
      return 'Missing vHUB device MAC.';
    }

    if (!this.applyProcessContext?.peerMac) {
      return 'Missing VCE device MAC.';
    }

    if (this.applyProcessContext.type === 'editPeer' && !this.getApplyTeam(this.applyProcessContext)) {
      return 'Missing existing VCE team for peer update.';
    }

    return '';
  }

  validateApplyPayloadStep() {
    const payload = this.applyProcessContext?.submitPayload;
    if (!payload) {
      return 'Missing saved configuration payload.';
    }

    if (!payload.vce_provision && this.applyProcessContext?.type === 'addPeer') {
      return 'Missing VCE provisioning payload.';
    }

    return '';
  }

  shouldAskForExistingVceProvision() {
    return !!this.applyProcessContext?.existingVceProvision &&
      this.applyProcessContext.updateExistingVceProvision === undefined;
  }

  getExistingVceProvision(peerMac: string) {
    const normalizedPeerMac = String(peerMac || '').toLowerCase();
    if (!normalizedPeerMac) {
      return null;
    }

    const row = this.tableRows.find(peerRow => String(peerRow?.mac || '').toLowerCase() === normalizedPeerMac && peerRow?.peerData?.vce_provision);
    return row?.peerData?.vce_provision || null;
  }

  setApplyProcessStepStatus(stepKey: ApplyProcessStepKey, status: ApplyProcessStepStatus, error = '') {
    this.applyProcessSteps = this.applyProcessSteps.map(step => step.key === stepKey
      ? { ...step, status, error }
      : step);
  }

  getApplyErrorMessage(error: any) {
    if (typeof error === 'string') {
      return error;
    }

    return error?.error?.message || error?.message || 'API request failed. Please retry.';
  }

  showOverlayPlusError(fallbackMessage: string, error?: any) {
    const message = typeof error === 'string'
      ? error
      : error?.error?.error || error?.error?.message || error?.message || fallbackMessage;
    this.toastr.error(message);
  }

  isApplyResultOk(res: any) {
    const status = String(res?.status || res?.result || res?.data?.status || res?.data?.result || '').toLowerCase();
    return !status || status === 'ok' || status === 'success' || status === 'completed';
  }

  closeApplyProcess() {
    this.dialog.closeAll();
    this.applyProcessContext = null;
    this.applyProcessComplete = false;
    this.applyProcessBusy = false;
  }

  abortApplyProcess() {
    this.closeApplyProcess();
  }

  cancelApplyActivation() {
    this.pendingApplyActivation = null;
  }

  getPeerDetailSubmitPayload(peer: any) {
    const dpc = this.dpcServiceRows.find(row => row.name === peer?.dpc_allowed) || this.dpcServiceRows[0] || null;
    const submitPayload = this.getAddPeerSubmitPayload(peer, dpc);
    const { vce_mac, ...editPayload } = submitPayload;
    return editPayload;
  }

  getPeerFromDetailModel() {
    const existingPeer = this.editingPeerData || {};
    return {
      ...existingPeer,
      mac: this.peerDetailModel.mac,
      hostname: this.peerDetailModel.hostname,
      client_model: this.peerDetailModel.clientDeviceType,
      dpc_allowed: this.peerDetailModel.dpcAllowed,
      encrypt: this.peerDetailModel.encrypt,
      psk: this.peerDetailModel.psk,
      dpc_ip: this.peerDetailModel.dpcIp,
      wan_id: this.peerDetailModel.wanId,
      wan_subnet: this.peerDetailModel.wanSubnet,
      dmz_ip: this.peerDetailModel.dmzIps,
      dmz_subnet: this.peerDetailModel.dmzSubnets,
      links: this.peerDetailModel.links
        .filter(link => link.name && link.name !== '[not set]')
        .map(link => ({
          name: link.name,
          path: link.path,
          weight: link.tx,
          rx_weight: link.rx,
          timeout: link.timeout,
          flapcheck: link.flapcheck,
          transport: link.transport,
          state: 'PENDING'
        })),
      vce_provision: {
        ...(existingPeer?.vce_provision || {}),
        link_names: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => link.name)
          .join(','),
        ulay_mode: this.getUlayModeToken(this.peerDetailModel.mode),
        weights: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => `${this.toNumber(link.tx)}:${this.toNumber(link.rx)}`)
          .join(','),
        timeouts: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => String(this.toNumber(link.timeout)))
          .join(','),
        flapcheck_period: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => String(this.toNumber(link.flapcheck)))
          .join(','),
        route_tables: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => link.path || link.name)
          .join(','),
        distribution: this.peerDetailModel.distribution,
        transport: this.peerDetailModel.links
          .filter(link => link.name && link.name !== '[not set]')
          .map(link => this.getTransportToken(link.transport))
          .join(','),
        maxpacketsize: this.peerDetailModel.maxPacketSize,
        fec: this.peerDetailModel.defaultFec,
        rseq_hold_ms: this.peerDetailModel.rseqHoldMs,
        rseq_think_acks: this.peerDetailModel.rseqThinkAcks,
        rseq_thin_acks: this.peerDetailModel.rseqThinkAcks,
        maxblocks: this.peerDetailModel.fecMaxBlocks,
        fec_group_hold: this.peerDetailModel.fecGroupHold,
        fec_group_hold_depth: this.peerDetailModel.fecGroupHoldDepth,
        fec_lanes: this.peerFecLanes.map(lane => ({
          lane: lane.laneNumber || this.getPeerFecLaneNumber(lane, 0),
          orig_dupes: lane.origDupes,
          mode: lane.mode,
          mode_str: lane.mode,
          steer: lane.steer
        })),
        ...this.getPeerDetailFecLanePayload()
      }
    };
  }

  getPeerDetailFecLanePayload() {
    return this.peerFecLanes.reduce((payload, lane, index) => ({
      ...payload,
      [`fec_q${lane.laneNumber || this.getPeerFecLaneNumber(lane, index)}`]: lane.origDupes,
      [`fec_q${lane.laneNumber || this.getPeerFecLaneNumber(lane, index)}_mode`]: lane.mode,
      [`fec_q${lane.laneNumber || this.getPeerFecLaneNumber(lane, index)}_steer`]: lane.steer
    }), {});
  }

  regeneratePeerPsk() {
    this.peerDetailModel.psk = this.generateBase64UrlPsk();
    this.showPeerPsk = true;
    this.peerPskLoading = false;
  }

  togglePeerPsk() {
    if (this.peerPskLoading) {
      return;
    }

    if (this.showPeerPsk) {
      this.showPeerPsk = false;
      return;
    }

    if (this.peerDetailModel.psk) {
      this.showPeerPsk = true;
      return;
    }

    this.loadPeerPsk(true);
  }

  loadPeerPsk(reveal: boolean) {
    const peerMac = this.peerDetailModel.mac || this.editingPeerData?.mac || '';
    if (!this.currentDeviceMac || !peerMac) {
      this.showPeerPsk = reveal;
      return;
    }

    this.peerPskLoading = true;
    this.vceVhubService.getMidlayVhubPeerPsk(this.currentDeviceMac, peerMac).subscribe(res => {
      const psk = this.getPskFromResponse(res);
      if (psk) {
        this.peerDetailModel.psk = psk;
      }
      this.showPeerPsk = reveal;
      this.peerPskLoading = false;
    }, () => {
      this.showPeerPsk = reveal;
      this.peerPskLoading = false;
    });
  }

  getPskFromResponse(res: any) {
    if (typeof res === 'string') {
      return res;
    }

    const psk = res?.peer_psk || res?.psk || res?.data?.peer_psk || res?.data?.psk || res?.peer?.psk || res?.result?.psk;
    if (psk) {
      return psk;
    }

    if (typeof res?.data === 'string') {
      return res.data;
    }

    if (typeof res?.result === 'string' && res.result !== 'ok') {
      return res.result;
    }

    return '';
  }

  addPeerFecLane() {
    const nextLaneNumber = this.getNextPeerFecLaneNumber();
    this.peerFecLanes = [
      ...this.peerFecLanes,
      { lane: `Lane ${nextLaneNumber}`, laneNumber: nextLaneNumber, origDupes: '0:0', mode: 'raw', steer: String(nextLaneNumber) }
    ];
  }

  getNextPeerFecLaneNumber() {
    const laneNumbers = this.peerFecLanes.map((lane, index) => lane.laneNumber || this.getPeerFecLaneNumber(lane, index));
    return laneNumbers.length ? Math.max(...laneNumbers) + 1 : 1;
  }

  removePeerFecLane(index: number) {
    this.peerFecLanes = this.peerFecLanes.filter((_, laneIndex) => laneIndex !== index);
  }

  getCsvParts(value: any) {
    return String(value || '')
      .split(',')
      .map(item => item.trim())
      .filter(Boolean);
  }

  getWeightPair(value: any) {
    if (!value) {
      return {};
    }
    const [tx, rx] = String(value || '')
      .split(':')
      .map(part => this.toNumber(part));
    return {
      weight: tx,
      rx_weight: rx
    };
  }

  getOptionalNumber(value: any): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numberValue = Number(value);
    return Number.isNaN(numberValue) ? null : numberValue;
  }

  getTransportLabel(value: any) {
    return String(value || '').toLowerCase().startsWith('tcp') ? TransportProtocol.Tcp : TransportProtocol.Udp;
  }

  getPeerDetailTransport(value: any) {
    return value ? this.getTransportLabel(value) : '';
  }

  getEncryptLabel(value: any) {
    const normalized = String(value || '').toLowerCase();
    if (normalized === 'on' || normalized === 'true' || normalized === '1' || normalized === 'yes') {
      return 'On';
    }
    if (normalized === 'off' || normalized === 'false' || normalized === '0' || normalized === 'no') {
      return 'Off';
    }
    return '';
  }

  getEncryptToken(value: any) {
    return this.getEncryptLabel(value).toLowerCase() || 'on';
  }

  getDistributionLabel(value: any) {
    const normalized = String(value || '').toLowerCase();
    if (normalized.includes('block')) {
      return DistributionMode.ByBlocks;
    }
    if (normalized.includes('flow')) {
      return DistributionMode.ByFlows;
    }
    if (normalized) {
      return DistributionMode.PerPacket;
    }
    return '';
  }

  getPeerDetailMode(value: any) {
    return String(value || '').toLowerCase().includes('source') ? 'Bridge' : 'Route';
  }

  getAssignedValue(row: PeerTableRow, label: string) {
    return row?.assignedIpLines?.find(line => line.label === label)?.value || '';
  }

  getDpcs(midlay: any) {
    return Array.isArray(midlay?.dpcs) ? midlay.dpcs : [];
  }

  getPeers(midlay: any) {
    return Array.isArray(midlay?.peers) ? midlay.peers : [];
  }

  getLegacyMidlayEntries(midlay: any): OverlayPlusLegacyEntry[] {
    if (Array.isArray(midlay?.dpcs) || Array.isArray(midlay?.peers)) {
      return [];
    }

    return Object.entries(midlay).map(([key, team]) => ({
      key,
      team
    }));
  }

  getDpcServiceRow(dpc: any, fallbackName?: string): DpcServiceRow {
    const config = this.getTeamConfig(dpc);

    return {
      name: dpc?.name || fallbackName || config?.iface || '-',
      listen_addr: dpc?.listen_addr || dpc?.listen || '-',
      server_url: dpc?.server_url || config?.remote_addr || '-',
      dpc_ip: dpc?.dpc_ip || config?.dpc_ip || '-',
      tunnel_ip: dpc?.tunnel_ip || this.stripCidr(dpc?.dpc_ip) || '-',
      dpc_gateway: dpc?.dpc_gateway || config?.dpc_gateway || '-',
      ip4table: dpc?.ip4table || config?.route_tables || '-',
      mtu: dpc?.mtu || config?.mtu || '-',
      maxpacketsize: dpc?.maxpacketsize || config?.maxpacketsize || '-',
      fec_timeout: dpc?.fec_timeout || '-',
      sdwan_link: dpc?.sdwan_link || config?.sdwan_link || '-',
      qos_rate: dpc?.qos_rate || '-',
      enabled: dpc?.enabled === 1 || dpc?.enabled === '1' || dpc?.enabled === true ||
        ['running', 'ok', 'up'].includes(String(dpc?.state || dpc?.status || '').toLowerCase())
    };
  }

  getPeerRows(peers: any[]): PeerTableRow[] {
    return peers.reduce((rows, peer, peerIndex) => [
      ...rows,
      ...this.getPeerDisplayRows(peer, peerIndex)
    ], [] as PeerTableRow[]);
  }

  getPeerDisplayRows(peer: any, peerIndex = 0): PeerTableRow[] {
    const links: OverlayPlusLink[] = (Array.isArray(peer?.links) ? peer.links : [])
      .filter((link: OverlayPlusLink) => String(link?.name || '').trim());

    if (!links.length) {
      return [
        this.getPeerSummaryRow(peer, peerIndex),
        ...this.getPeerLaneRows(peer, peerIndex)
      ];
    }

    const linkRows = links.map((link: OverlayPlusLink, linkIndex: number) => ({
      ...this.getPeerSummaryRow(peer, peerIndex),
      showPeerSummary: linkIndex === 0,
      linkLane: link?.name || '-',
      assuranceLines: this.getPeerLinkAssurance(peer, link, linkIndex),
      weight: `${this.toNumber(link?.weight)}/${this.toNumber(link?.rx_weight)}`,
      state: link?.state || this.getPeerState(peer)
    }));

    return [
      ...linkRows,
      ...this.getPeerLaneRows(peer, peerIndex)
    ];
  }

  getPeerSummaryRow(peer: any, peerIndex = 0): PeerTableRow {
    const id = `${peer?.mac || 'peer'}-${peer?.wan_id || peerIndex}-${peerIndex}`;
    return {
      id,
      peerGroupId: id,
      peerData: peer,
      showPeerSummary: true,
      mac: peer?.mac || '-',
      hostname: peer?.local ? '-' : peer?.hostname || peer?.server_name || '-',
      dpc: peer?.local ? 'local' : peer?.dpc_allowed || '-',
      local: peer?.local === true,
      assignedIpLines: this.getAssignedIpLines(peer),
      linkLane: '-',
      assuranceLines: ['-'],
      weight: '-',
      wanId: peer?.wan_id || '-',
      uptime: this.formatDuration(peer?.connected_sec),
      state: this.getPeerState(peer)
    };
  }

  getLegacyPeerRows(entries: any[]): PeerTableRow[] {
    return entries.reduce((rows, entry) => {
      const team = entry?.team || {};
      const clients: any[] = Array.isArray(team?.clients) ? team.clients : [];

      if (!clients.length) {
        return rows;
      }

      return [
        ...rows,
        ...this.getPeerRows(clients.map((client: any) => ({
          ...client,
          dpc_allowed: team?.name || entry?.key
        })))
      ];
    }, [] as PeerTableRow[]);
  }

  getPeerLaneRows(peer: any, peerIndex = 0): PeerTableRow[] {
    return this.getPeerLanes(peer).map((lane: OverlayPlusLane) => ({
      ...this.getPeerSummaryRow(peer, peerIndex),
      showPeerSummary: false,
      linkLane: this.getLaneLabel(lane),
      assuranceLines: this.getLaneAssurance(lane),
      weight: lane?.tx_weight != null || lane?.rx_weight != null
        ? `${this.toNumber(lane?.tx_weight)}/${this.toNumber(lane?.rx_weight)}`
        : '',
      state: lane?.state || ''
    }));
  }

  getLaneAnalyticsData() {
    if (this.laneAnalyticsData) {
      return this.laneAnalyticsData;
    }

    const statistics = this.currentDevice?.statistics || {};
    return statistics?.rrt_midlay_lane_analytics?.data || statistics?.lane_analytics?.data ||
      statistics?.rrt_midlay?.lane_analytics?.data || this.currentDevice?.lane_analytics?.data ||
      statistics?.rrt_midlay_lane_analytics || statistics?.lane_analytics ||
      statistics?.rrt_midlay?.lane_analytics || this.currentDevice?.lane_analytics || null;
  }

  getSelectedLaneAnalyticsTimeline() {
    const analytics = this.getLaneAnalyticsData();
    const selectedRange = this.laneAnalyticsRanges[this.selectedLaneAnalyticsRange] || this.laneAnalyticsRanges[0];
    return analytics?.timelines?.[selectedRange.key] || analytics?.[selectedRange.key] || null;
  }

  getLaneAnalyticsRows() {
    if (!this.isLaneAnalyticsSelectionReady()) {
      return [];
    }

    const sampleRows = this.getLaneAnalyticsSampleRows();
    if (sampleRows.length) {
      return this.vCEMode ? this.mergeVceLaneAnalyticsRows(sampleRows) : sampleRows;
    }

    const timeline = this.getSelectedLaneAnalyticsTimeline();
    const analytics = this.getLaneAnalyticsData();
    const tableRows = timeline?.table
      ? Object.keys(timeline.table).map(key => timeline.table[key])
      : [];
    const rows = tableRows.length ? tableRows : (Array.isArray(analytics?.lists) ? analytics.lists : []);

    const displayRows = rows.map((row: any) => {
      return this.getLaneAnalyticsDisplayRow(row);
    });
    if (this.vCEMode) {
      return this.mergeVceLaneAnalyticsRows(displayRows);
    }

    const selectedDevices = this.getLaneAnalyticsSelectedPeerValues();
    return displayRows.filter(row => selectedDevices.indexOf(row.peerValue) > -1);
  }

  getLaneAnalyticsSampleRows() {
    const samples = this.vHubMode
      ? [
        ...this.getFilteredLaneAnalyticsPeerSamples(),
        ...this.getLaneAnalyticsFlatSamples().filter(sample => this.isLaneAnalyticsPeerIncluded(sample))
      ]
      : [
        ...this.getLaneAnalyticsPeerSamples(),
        ...this.getLaneAnalyticsFlatSamples()
      ];
    const grouped = samples.reduce((groups, sample) => {
      const key = `${sample.peerMac || sample.mac || '-'}-${sample.lane ?? '-'}`;
      groups[key] = groups[key] || [];
      groups[key].push(sample);
      return groups;
    }, {} as { [key: string]: any[] });

    return Object.keys(grouped).map(key => {
      const group = grouped[key];
      const first = group[0] || {};
      return this.getLaneAnalyticsDisplayRow({
        ...first,
        mac: first.peerMac || first.mac,
        tx_packets: this.sumLaneAnalyticsMetric(group, 'tx_packets'),
        tx_bytes: this.sumLaneAnalyticsMetric(group, 'tx_bytes'),
        rx_packets: this.sumLaneAnalyticsMetric(group, 'rx_packets'),
        rx_bytes: this.sumLaneAnalyticsMetric(group, 'rx_bytes'),
        tx_mbps: this.averageLaneAnalyticsRate(group, 'tx_bytes'),
        rx_mbps: this.averageLaneAnalyticsRate(group, 'rx_bytes'),
        loss_pct: this.averageLaneAnalyticsMetric(group, 'loss_pct'),
        decode_latency_avg_ms: this.averageLaneAnalyticsMetric(group, 'decode_latency_avg_ms'),
        decode_latency_p90_ms: Math.max(...group.map(sample => this.toNumber(sample.decode_latency_p90_ms))),
        jitter_ms: this.averageLaneAnalyticsMetric(group, 'jitter_ms'),
        mos: this.averageLaneAnalyticsMetric(group, 'mos')
      });
    });
  }

  getLaneAnalyticsDisplayRow(row: any) {
    const lane = row?.Lane ?? row?.lane ?? '-';
    const mode = row?.mode_str || row?.Mode || row?.mode || '-';
    const isDefault = this.isDefaultLaneAnalyticsMode(mode);
    return {
      peer: this.getLaneAnalyticsPeerLabel(row),
      peerValue: this.getLaneAnalyticsPeerFilterValue(row),
      lane,
      laneLabel: isDefault ? `${lane} (default)` : lane,
      ratio: this.getAnalyticsLaneRatio(row),
      mode,
      qClass: row?.q_class ?? row?.['Q Class'] ?? '-',
      txPackets: this.toNumber(row?.['TX Packets'] ?? row?.tx_packets),
      txBytes: this.toNumber(row?.['TX Bytes'] ?? row?.tx_bytes),
      rxPackets: this.toNumber(row?.['RX Packets'] ?? row?.rx_packets),
      rxBytes: this.toNumber(row?.['RX Bytes'] ?? row?.rx_bytes),
      txMbps: row?.tx_mbps != null
        ? this.toNumber(row.tx_mbps)
        : this.bytesToMbps(row?.['TX Bytes'] ?? row?.tx_bytes, this.getLaneAnalyticsSampleDurationSeconds(row)),
      rxMbps: row?.rx_mbps != null
        ? this.toNumber(row.rx_mbps)
        : this.bytesToMbps(row?.['RX Bytes'] ?? row?.rx_bytes, this.getLaneAnalyticsSampleDurationSeconds(row)),
      lossPct: this.toNumber(row?.['Average Loss %'] ?? row?.loss_pct),
      decodeLatencyAvg: this.toNumber(row?.['Average Decode Latency'] ?? row?.decode_latency_avg_ms),
      decodeLatencyP90: this.toNumber(row?.['Max Decode Latency P90'] ?? row?.decode_latency_p90_ms),
      jitter: this.toNumber(row?.['Average Jitter'] ?? row?.jitter_ms),
      mos: row?.['Average MOS'] ?? row?.mos ?? '-'
    };
  }

  mergeVceLaneAnalyticsRows(rows: any[]) {
    const grouped = (rows || []).reduce((groups, row) => {
      const key = this.getVceLaneAnalyticsMergeKey(row);
      groups[key] = groups[key] || [];
      groups[key].push(row);
      return groups;
    }, {} as { [key: string]: any[] });

    return Object.keys(grouped)
      .map(key => this.getMergedVceLaneAnalyticsRow(grouped[key]))
      .sort((a, b) => this.getLaneAnalyticsLaneSortValue(a?.laneLabel) - this.getLaneAnalyticsLaneSortValue(b?.laneLabel));
  }

  getVceLaneAnalyticsMergeKey(row: any) {
    return String(row?.laneLabel ?? row?.lane ?? '-').trim().toLowerCase();
  }

  getMergedVceLaneAnalyticsRow(rows: any[]) {
    const first = rows[0] || {};
    const txPackets = this.sumDisplayLaneAnalyticsMetric(rows, 'txPackets');
    const txBytes = this.sumDisplayLaneAnalyticsMetric(rows, 'txBytes');
    const rxPackets = this.sumDisplayLaneAnalyticsMetric(rows, 'rxPackets');
    const rxBytes = this.sumDisplayLaneAnalyticsMetric(rows, 'rxBytes');
    const txMbps = this.sumDisplayLaneAnalyticsMetric(rows, 'txMbps');
    const rxMbps = this.sumDisplayLaneAnalyticsMetric(rows, 'rxMbps');

    return {
      ...first,
      peer: '-',
      txPackets,
      txBytes,
      rxPackets,
      rxBytes,
      txMbps,
      rxMbps,
      lossPct: this.weightedLaneAnalyticsMetric(rows, 'lossPct'),
      decodeLatencyAvg: this.weightedLaneAnalyticsMetric(rows, 'decodeLatencyAvg'),
      decodeLatencyP90: Math.max(...rows.map(row => this.toNumber(row?.decodeLatencyP90))),
      jitter: this.weightedLaneAnalyticsMetric(rows, 'jitter'),
      mos: this.weightedLaneAnalyticsMetric(rows, 'mos')
    };
  }

  sumDisplayLaneAnalyticsMetric(rows: any[], key: string) {
    return (rows || []).reduce((total, row) => total + this.toNumber(row?.[key]), 0);
  }

  weightedLaneAnalyticsMetric(rows: any[], key: string) {
    const weighted = (rows || []).reduce((total, row) => {
      const weight = this.toNumber(row?.txPackets) + this.toNumber(row?.rxPackets);
      const value = this.toNumber(row?.[key]);
      return {
        value: total.value + (value * weight),
        weight: total.weight + weight,
        fallbackValue: total.fallbackValue + value,
        fallbackCount: total.fallbackCount + (row?.[key] === '-' || row?.[key] == null ? 0 : 1)
      };
    }, {
      value: 0,
      weight: 0,
      fallbackValue: 0,
      fallbackCount: 0
    });

    if (weighted.weight > 0) {
      return Number((weighted.value / weighted.weight).toFixed(2));
    }

    if (weighted.fallbackCount > 0) {
      return Number((weighted.fallbackValue / weighted.fallbackCount).toFixed(2));
    }

    return 0;
  }

  getLaneAnalyticsPeerSamples() {
    const analytics = this.getLaneAnalyticsData();
    if (Array.isArray(analytics?.lanes)) {
      return analytics.lanes;
    }
    const peers = Array.isArray(analytics?.peers) ? analytics.peers : [];
    return peers.reduce((samples, peer) => [
      ...samples,
      ...(Array.isArray(peer?.lists) ? peer.lists : []).map((sample: any) => ({
        ...sample,
        peerMac: peer?.mac || sample?.mac,
        peerName: peer?.hostname || peer?.name || peer?.server_name,
        peerModel: peer?.model || peer?.client_model || peer?.clientModel
      }))
    ], [] as any[]);
  }

  getLaneAnalyticsPeerLabel(row: any) {
    const mac = row?.peerMac || row?.mac || '';
    if (!mac) {
      return '-';
    }

    const device = this.getClientLookupDevice(mac);
    if (device) {
      return this.getLaneAnalyticsDeviceLabel(device);
    }

    const peer = this.getLaneAnalyticsPeerFromRows(mac);
    const isLinkSample = row?.record_type === 'link' || row?.record_type === 'underlay' ||
      (row?.name && (row?.rtt_avg_ms != null || row?.rtt_p90_ms != null || row?.rtt_jitter_ms != null));
    const name = this.getFirstValue(
      row?.hostname,
      row?.peerName,
      isLinkSample ? null : row?.name,
      peer?.hostname,
      peer?.name,
      peer?.server_name
    );
    const model = this.getFirstValue(
      row?.client_model,
      row?.clientModel,
      row?.model,
      row?.peerModel,
      peer?.client_model,
      peer?.clientModel,
      peer?.model
    );

    if (name && model) {
      return `${mac} - ${name} (${model})`;
    }
    if (name) {
      return `${mac} - ${name}`;
    }
    if (model) {
      return `${mac} (${model})`;
    }

    return mac;
  }

  getLaneAnalyticsDeviceLabel(device: any) {
    const name = device?.name || device?.hostname || 'Unnamed';
    const model = this.getClientLookupRawDeviceModel(device);
    return model ? `${device.mac} - ${name} (${model})` : `${device.mac} - ${name}`;
  }

  getLaneAnalyticsPeerFromRows(mac: string) {
    const normalizedMac = String(mac || '').toLowerCase();
    return this.tableRows
      .map(row => row?.peerData)
      .find(peer => String(peer?.mac || '').toLowerCase() === normalizedMac) || null;
  }

  sumLaneAnalyticsMetric(rows: any[], key: string) {
    return rows.reduce((total, row) => total + this.toNumber(row?.[key]), 0);
  }

  averageLaneAnalyticsMetric(rows: any[], key: string) {
    if (!rows.length) {
      return 0;
    }

    return Number((this.sumLaneAnalyticsMetric(rows, key) / rows.length).toFixed(2));
  }

  averageLaneAnalyticsRate(rows: any[], byteKey: string) {
    if (!rows.length) {
      return 0;
    }

    const rateKey = byteKey === 'tx_bytes' ? 'tx_mbps' : 'rx_mbps';
    const rates = rows
      .map(row => row?.[rateKey] != null
        ? this.toNumber(row[rateKey])
        : this.bytesToMbps(row?.[byteKey], this.getLaneAnalyticsSampleDurationSeconds(row)))
      .filter(value => Number.isFinite(value));
    return rates.length ? Number((rates.reduce((sum, value) => sum + value, 0) / rates.length).toFixed(3)) : 0;
  }

  getLaneAnalyticsTotal() {
    return this.getLaneAnalyticsRows().reduce((total, row) => ({
      txPackets: total.txPackets + this.toNumber(row.txPackets),
      txBytes: total.txBytes + this.toNumber(row.txBytes),
      rxPackets: total.rxPackets + this.toNumber(row.rxPackets),
      rxBytes: total.rxBytes + this.toNumber(row.rxBytes),
      txMbps: total.txMbps + this.toNumber(row.txMbps),
      rxMbps: total.rxMbps + this.toNumber(row.rxMbps)
    }), {
      txPackets: 0,
      txBytes: 0,
      rxPackets: 0,
      rxBytes: 0,
      txMbps: 0,
      rxMbps: 0
    });
  }

  getLaneAnalyticsPeerTotal(rows: any[], peer: string) {
    return (rows || [])
      .filter(row => row?.peer === peer)
      .reduce((total, row) => ({
        txPackets: total.txPackets + this.toNumber(row.txPackets),
        txBytes: total.txBytes + this.toNumber(row.txBytes),
        rxPackets: total.rxPackets + this.toNumber(row.rxPackets),
        rxBytes: total.rxBytes + this.toNumber(row.rxBytes),
        txMbps: total.txMbps + this.toNumber(row.txMbps),
        rxMbps: total.rxMbps + this.toNumber(row.rxMbps)
      }), {
        txPackets: 0,
        txBytes: 0,
        rxPackets: 0,
        rxBytes: 0,
        txMbps: 0,
        rxMbps: 0
      });
  }

  isFirstLaneAnalyticsPeerRow(rows: any[], row: any, index: number) {
    if (!row?.peer) {
      return true;
    }

    return rows.findIndex(item => item?.peer === row.peer) === index;
  }

  getLaneAnalyticsPeerRowspan(rows: any[], peer: string) {
    const laneCount = (rows || []).filter(row => row?.peer === peer).length || 1;
    return laneCount + this.getCachedLaneAnalyticsTableLinkRows(peer).length + 1;
  }

  isLastLaneAnalyticsPeerRow(rows: any[], row: any, index: number) {
    if (!row?.peer) {
      return true;
    }

    return (rows || []).map(item => item?.peer).lastIndexOf(row.peer) === index;
  }

  getLaneAnalyticsChartLabels() {
    const peerSamples = this.getFilteredLaneAnalyticsPeerSamples();
    if (peerSamples.length) {
      return this.getLaneAnalyticsTimezoneLabels(this.getLaneAnalyticsPeerSampleLabels(peerSamples));
    }

    const flatSamples = this.getLaneAnalyticsFlatSamples();
    if (flatSamples.length) {
      return this.getLaneAnalyticsTimezoneLabels(this.getLaneAnalyticsPeerSampleLabels(flatSamples));
    }

    const timelineLaneSamples = this.getLaneAnalyticsTimelineLaneSamples();
    if (timelineLaneSamples.length) {
      return this.getLaneAnalyticsTimezoneLabels(this.getLaneAnalyticsPeerSampleLabels(timelineLaneSamples));
    }

    const points = this.getLaneAnalyticsTimelinePoints();
    if (points.length) {
      return this.getLaneAnalyticsTimezoneLabels(points.map(point => point.datetime || point.timestamp || point.date || point.time || ''));
    }

    return [this.getLaneAnalyticsRangeLabel()];
  }

  getLaneAnalyticsDisplayIndices(sampleCount: number, expanded = false): number[] {
    // Match Analytics & Info: omit intermediate records and retain the final record.
    const intervals = expanded ? [1, 6, 36, 72, 72] : [3, 12, 72, 432, 432];
    const interval = intervals[this.selectedLaneAnalyticsRange] || intervals[0];
    return Array.from({ length: sampleCount }, (_, index) => index)
      .filter(index => index > 0 && (index % interval === 0 || index === sampleCount - 1));
  }

  buildLaneAnalyticsCharts() {
    this.refreshLaneAnalyticsDeviceOptions();
    if (!this.isLaneAnalyticsSelectionReady()) {
      this.laneAnalyticsChartLabels = [];
      this.laneAnalyticsChartDataByMetric = {};
      this.laneAnalyticsGraphs = [];
      this.laneAnalyticsRows = [];
      this.laneAnalyticsTableLinkRowsByPeer = {};
      this.scheduleAnalyticsChartRender();
      return;
    }

    this.laneAnalyticsChartLabels = this.getLaneAnalyticsChartLabels();
    this.laneAnalyticsChartDataByMetric = this.getLaneAnalyticsMetricsForCharts().reduce((charts, metric) => ({
      ...charts,
      [metric.valueKey]: this.getLaneAnalyticsChartDataForMetric(metric)
    }), {});
    // Plot interval records only; keep full telemetry for expanded sampling and summaries.
    const displayIndices = this.getLaneAnalyticsDisplayIndices(this.laneAnalyticsChartLabels.length);
    const displayLabels = displayIndices.map(index => this.laneAnalyticsChartLabels[index]);
    this.laneAnalyticsGraphs = this.getLaneAnalyticsGraphDefinitions().map(graph => {
      const fullData = this.getLaneAnalyticsGraphData(graph).map((series: any) => ({
        ...series,
        label: this.getSelectedDeviceChartSeriesLabel(series)
      }));
      const data = fullData.map(series => ({
        ...series,
        data: displayIndices.map(index => series.data[index])
      }));
      return {
        ...graph,
        data,
        labels: displayLabels,
        fullData,
        fullLabels: this.laneAnalyticsChartLabels,
        options: this.getLaneAnalyticsChartOptions(graph),
        legendGroups: this.getSelectedDeviceLaneAnalyticsLegendGroups(data)
      };
    });
    this.laneAnalyticsRows = this.getLaneAnalyticsRows();
    this.laneAnalyticsTableLinkRowsByPeer = this.laneAnalyticsRows.reduce((rows, row) => {
      const peer = String(row?.peer || '');
      if (peer && !rows[peer]) {
        rows[peer] = this.getLaneAnalyticsTableLinkRows(peer);
      }
      return rows;
    }, {} as { [peer: string]: any[] });
    this.scheduleAnalyticsChartRender();
  }

  getSelectedDeviceChartSeriesLabel(series: any) {
    const label = series?.laneLabel || this.getLaneAnalyticsLaneFromSeriesLabel(series?.label);
    return series?.seriesType === 'link'
      ? String(label || '').replace(/^Link\s+/i, '')
      : String(label || '');
  }

  getSelectedDeviceLaneAnalyticsLegendGroups(series: ChartDataSets[]) {
    const groups = this.getLaneAnalyticsLegendGroups(series);
    return [{
      peerLabel: '',
      linkItems: groups.reduce((items, group) => [...items, ...(group.linkItems || [])], []),
      laneItems: groups.reduce((items, group) => [...items, ...(group.laneItems || [])], [])
    }];
  }

  getLaneAnalyticsCachedChartData(metric: any) {
    return this.laneAnalyticsChartDataByMetric?.[metric?.valueKey] || [];
  }

  getLaneAnalyticsChartData(): ChartDataSets[] {
    return this.getLaneAnalyticsChartDataForMetric(this.laneAnalyticsMetrics[1]);
  }

  getLaneAnalyticsGraphMetrics() {
    const graphMetricKeys = ['lossPct', 'decodeLatencyAvg', 'jitter', 'mos'];
    return this.laneAnalyticsMetrics.filter(metric => graphMetricKeys.indexOf(metric.valueKey) > -1);
  }

  getLaneAnalyticsGraphDefinitions() {
    return [
      {
        title: 'Bandwidth',
        axis: 'Mbps',
        metrics: this.getLaneAnalyticsMetricsByKeys(['totalMbps'])
      },
      {
        title: 'Data Usage',
        axis: 'MB',
        valueDivisor: 1000000,
        metrics: this.getLaneAnalyticsMetricsByKeys(['totalBytes'])
      },
      ...this.getLaneAnalyticsGraphMetrics().map(metric => ({
        metric,
        title: this.getLaneAnalyticsGraphTitle(metric),
        axis: metric.axis || metric.label,
        metrics: [metric]
      }))
    ];
  }

  getLaneAnalyticsMetricsForCharts() {
    const metrics = this.getLaneAnalyticsGraphDefinitions().reduce((items, graph: any) => [
      ...items,
      ...(graph.metrics || [])
    ], [] as any[]);
    return metrics.filter((metric, index) =>
      metrics.findIndex(item => item?.valueKey === metric?.valueKey) === index);
  }

  getLaneAnalyticsMetricsByKeys(keys: string[]) {
    return keys
      .map(key => this.laneAnalyticsMetrics.find(metric => metric.valueKey === key))
      .filter(metric => !!metric);
  }

  getLaneAnalyticsGraphData(graph: any) {
    const metrics = graph?.metrics || (graph?.metric ? [graph.metric] : []);
    if (metrics.length <= 1) {
      const metric = metrics[0] || graph?.metric || graph;
      return this.getLaneAnalyticsMetricSeries(metric).map((series: any) => ({
        ...series,
        data: graph.valueDivisor
          ? series.data.map(value => value == null ? value : value / graph.valueDivisor)
          : series.data
      }));
    }

    return metrics.reduce((series, metric) => [
      ...series,
      ...this.getLaneAnalyticsMetricSeriesForCompositeGraph(metric)
    ], [] as ChartDataSets[]);
  }

  getLaneAnalyticsMetricSeriesForCompositeGraph(metric: any) {
    return this.getLaneAnalyticsMetricSeries(metric).map((series: any) => ({
      ...series,
      label: `${series.label} ${metric.label}`,
      metricLabel: metric.label,
      laneLabel: `${series.laneLabel || this.getLaneAnalyticsLaneFromSeriesLabel(series.label)} ${metric.label}`
    }));
  }

  getLaneAnalyticsMetricSeries(metric: any): ChartDataSets[] {
    return [
      ...this.getLaneAnalyticsLinkChartDataForMetric(metric),
      ...(this.laneAnalyticsChartDataByMetric?.[metric?.valueKey] || [])
    ];
  }

  getLaneAnalyticsLinkChartDataForMetric(metric: any): ChartDataSets[] {
    const linkMetricKey = this.getLaneAnalyticsLinkMetricKey(metric);
    const samples = this.getLaneAnalyticsLinkSourceSamples();
    if (!linkMetricKey || !samples.length) {
      return [];
    }

    const labels = this.getLaneAnalyticsPeerSampleLabels(samples);
    const grouped = this.getLaneAnalyticsLinkSamples(samples).reduce((groups, item) => {
      const peerLabel = this.vCEMode ? '' : this.getLaneAnalyticsPeerLabel(item.sample);
      const linkName = String(item.link?.name || '').trim();
      const key = `${peerLabel}||${linkName}`;
      const label = this.getLaneAnalyticsSampleLabel(item.sample);
      const labelIndex = labels.indexOf(label);
      groups[key] = groups[key] || {
        peerLabel,
        laneLabel: `Link ${linkName}`,
        data: labels.map(() => null)
      };
      if (labelIndex > -1) {
        const value = linkMetricKey === 'combined_bytes'
          ? this.toNumber(item.link?.tx_bytes) + this.toNumber(item.link?.rx_bytes)
          : this.toNumber(item.link?.[linkMetricKey]);
        const apiRate = linkMetricKey === 'combined_bytes'
          ? (item.link?.tx_mbps != null || item.link?.rx_mbps != null
            ? this.toNumber(item.link?.tx_mbps) + this.toNumber(item.link?.rx_mbps)
            : null)
          : item.link?.[linkMetricKey === 'tx_bytes' ? 'tx_mbps' : 'rx_mbps'];
        groups[key].data[labelIndex] = metric?.rateFromBytes
          ? (apiRate != null ? this.toNumber(apiRate) : this.bytesToMbps(value, this.getLaneAnalyticsSampleDurationSeconds(item.sample)))
          : value;
      }
      return groups;
    }, {} as { [key: string]: { peerLabel: string, laneLabel: string, data: Array<number | null> } });

    return Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => {
        const peerCompare = String(a.peerLabel).localeCompare(String(b.peerLabel));
        return peerCompare || a.laneLabel.localeCompare(b.laneLabel, undefined, { numeric: true });
      })
      .map(series => ({
        label: `${series.peerLabel ? `${series.peerLabel} ` : ''}${series.laneLabel}`,
        // Keep periods before link telemetry was available as gaps instead of
        // reporting a misleading zero measurement.
        data: series.data,
        peerLabel: series.peerLabel,
        laneLabel: series.laneLabel,
        seriesType: 'link'
      } as any));
  }

  getLaneAnalyticsLinkMetricKey(metric: any) {
    const metricKeys = {
      txPackets: 'tx_packets',
      txBytes: 'tx_bytes',
      rxPackets: 'rx_packets',
      rxBytes: 'rx_bytes',
      txMbps: 'tx_bytes',
      rxMbps: 'rx_bytes',
      totalMbps: 'combined_bytes',
      totalBytes: 'combined_bytes',
      lossPct: 'loss_pct',
      decodeLatencyAvg: 'rtt_avg_ms',
      decodeLatencyP90: 'rtt_p90_ms',
      jitter: 'rtt_jitter_ms',
      mos: 'mos'
    };
    return metricKeys[metric?.valueKey] || '';
  }

  getLaneAnalyticsLinkSourceSamples() {
    if (!this.isLaneAnalyticsSelectionReady()) {
      return [];
    }

    const directLinkSamples = this.getLaneAnalyticsDirectLinkSamples();
    if (directLinkSamples.length) {
      if (!this.isLaneAnalyticsDeviceFilterActive()) {
        return directLinkSamples;
      }

      return directLinkSamples.filter(sample => this.isLaneAnalyticsPeerIncluded(sample));
    }

    if (this.vCEMode) {
      return [
        ...this.getLaneAnalyticsPeerSamples(),
        ...this.getLaneAnalyticsFlatSamples(),
        ...this.getLaneAnalyticsTimelineLaneSamples()
      ];
    }

    const peerSamples = this.getFilteredLaneAnalyticsPeerSamples();
    if (peerSamples.length || this.isLaneAnalyticsDeviceFilterActive()) {
      return peerSamples;
    }

    const flatSamples = this.getLaneAnalyticsFlatSamples();
    return flatSamples.length ? flatSamples : this.getLaneAnalyticsTimelineLaneSamples();
  }

  getLaneAnalyticsDirectLinkSamples() {
    const analytics = this.getLaneAnalyticsData();
    if (Array.isArray(analytics?.underlays)) {
      return analytics.underlays;
    }
    const peers = Array.isArray(analytics?.peers) ? analytics.peers : [];
    const peerLinks = peers.reduce((samples, peer) => [
      ...samples,
      ...(Array.isArray(peer?.links) ? peer.links : []).map((link: any) => ({
        ...link,
        peerMac: peer?.mac || link?.mac,
        peerName: peer?.hostname || peer?.name || peer?.server_name,
        peerModel: peer?.model || peer?.client_model || peer?.clientModel
      }))
    ], [] as any[]);
    if (peerLinks.length) {
      return peerLinks;
    }

    if (Array.isArray(analytics?.links) && analytics.links.length) {
      return analytics.links;
    }

    const timelines = analytics?.link_timelines;
    if (!timelines || typeof timelines !== 'object') {
      return [];
    }

    return Object.keys(timelines).reduce((samples, key) => [
      ...samples,
      ...(Array.isArray(timelines[key]) ? timelines[key] : []).map((link: any) => ({
        ...link,
        name: String(link?.name || '').trim() || key
      }))
    ], [] as any[]);
  }

  getLaneAnalyticsLinkSamples(samples: any[]) {
    const unique = (samples || []).reduce((items, sample) => {
      const sampleLabel = this.getLaneAnalyticsSampleLabel(sample);
      const peer = String(sample?.peerMac || sample?.mac || '').toLowerCase();
      const links = sample?.record_type === 'link' || sample?.record_type === 'underlay' ||
        (!Array.isArray(sample?.links) && sample?.name && sample?.rtt_avg_ms != null)
        ? [sample]
        : (Array.isArray(sample?.links) ? sample.links : []);
      links
        .filter(link => String(link?.name || '').trim())
        .forEach(link => {
          const key = `${peer}||${sampleLabel}||${String(link.name).trim().toLowerCase()}`;
          items[key] = { sample, link };
        });
      return items;
    }, {} as { [key: string]: { sample: any, link: any } });

    return Object.keys(unique).map(key => unique[key]);
  }

  getLaneAnalyticsTableLinkRows(peer = '') {
    const grouped = this.getLaneAnalyticsLinkSamples(this.getLaneAnalyticsLinkSourceSamples())
      .reduce((groups, item) => {
        const peerLabel = this.vCEMode ? '' : this.getLaneAnalyticsPeerLabel(item.sample);
        if (peer && peerLabel !== peer) {
          return groups;
        }

        const linkName = String(item.link?.name || '').trim();
        const key = `${peerLabel}||${linkName.toLowerCase()}`;
        groups[key] = groups[key] || {
          peer: peerLabel,
          linkLabel: linkName,
          samples: []
        };
        groups[key].samples.push({
          ...item.link,
          window_minutes: item.sample?.window_minutes,
          window_start: item.sample?.window_start,
          window_end: item.sample?.window_end,
          generated_date: item.sample?.generated_date
        });
        return groups;
      }, {} as { [key: string]: { peer: string, linkLabel: string, samples: any[] } });

    return Object.keys(grouped)
      .map(key => {
        const group = grouped[key];
        const latest = group.samples[group.samples.length - 1] || {};
        return {
          peer: group.peer,
          linkLabel: group.linkLabel,
          mode: [latest.action, latest.state].filter(value => !!value).join(' / ') || '-',
          txPackets: this.sumLaneAnalyticsMetric(group.samples, 'tx_packets'),
          txBytes: this.sumLaneAnalyticsMetric(group.samples, 'tx_bytes'),
          rxPackets: this.sumLaneAnalyticsMetric(group.samples, 'rx_packets'),
          rxBytes: this.sumLaneAnalyticsMetric(group.samples, 'rx_bytes'),
          txMbps: this.averageLaneAnalyticsRate(group.samples, 'tx_bytes'),
          rxMbps: this.averageLaneAnalyticsRate(group.samples, 'rx_bytes'),
          lossPct: this.averageLaneAnalyticsMetric(group.samples, 'loss_pct'),
          decodeLatencyAvg: this.averageLaneAnalyticsMetric(group.samples, 'rtt_avg_ms'),
          decodeLatencyP90: Math.max(...group.samples.map(sample => this.toNumber(sample?.rtt_p90_ms))),
          jitter: this.averageLaneAnalyticsMetric(group.samples, 'rtt_jitter_ms'),
          mos: this.averageLaneAnalyticsMetric(group.samples, 'mos')
        };
      })
      .sort((a, b) => {
        const peerCompare = String(a.peer).localeCompare(String(b.peer));
        return peerCompare || a.linkLabel.localeCompare(b.linkLabel, undefined, { numeric: true });
      });
  }

  getLaneAnalyticsGraphTitle(metric: any) {
    if (metric?.valueKey === 'decodeLatencyAvg') {
      return 'Latency';
    }

    return metric?.label || '';
  }

  getLaneAnalyticsChartDataForMetric(metric: any): ChartDataSets[] {
    const vceSeries = this.getVceLaneAnalyticsChartDataForMetric(metric);
    if (vceSeries.length) {
      return vceSeries;
    }

    const peerSeries = this.getLaneAnalyticsChartDataFromPeerSamples(metric);
    if (peerSeries.length) {
      return peerSeries;
    }
    if (this.isLaneAnalyticsDeviceFilterActive()) {
      return [];
    }

    const flatSeries = this.getLaneAnalyticsChartDataFromFlatSamples(metric);
    if (flatSeries.length) {
      return flatSeries;
    }

    const timelineLaneSeries = this.getLaneAnalyticsChartDataFromTimelineLaneSamples(metric);
    if (timelineLaneSeries.length) {
      return timelineLaneSeries;
    }

    const pointSeries = this.getLaneAnalyticsChartDataFromPoints();
    if (pointSeries.length) {
      return this.getLaneAnalyticsChartDataFromPoints(metric);
    }

    const rows = this.getLaneAnalyticsRows();
    return rows.map(row => ({
      data: [this.getLaneAnalyticsDisplayMetricValue(row, metric)],
      label: `Lane ${row.laneLabel}`
    }));
  }

  getLaneAnalyticsDisplayMetricValue(row: any, metric: any) {
    if (metric?.valueKey === 'totalMbps') {
      return this.toNumber(row?.txMbps) + this.toNumber(row?.rxMbps);
    }
    if (metric?.valueKey === 'totalBytes') {
      return this.toNumber(row?.txBytes) + this.toNumber(row?.rxBytes);
    }

    return this.toNumber(row?.[metric?.valueKey]);
  }

  getLaneAnalyticsChartDataFromFlatSamples(metric: any): ChartDataSets[] {
    const samples = this.getLaneAnalyticsFlatSamples();
    if (!samples.length) {
      return [];
    }

    return this.getLaneAnalyticsChartDataFromSamples(samples, metric);
  }

  getLaneAnalyticsChartDataFromPeerSamples(metric: any): ChartDataSets[] {
    const samples = this.getFilteredLaneAnalyticsPeerSamples();
    if (!samples.length) {
      return [];
    }

    return this.getLaneAnalyticsChartDataFromSamples(samples, metric);
  }

  getLaneAnalyticsDeviceOptions() {
    return this.laneAnalyticsDeviceOptions;
  }

  refreshLaneAnalyticsDeviceOptions() {
    this.laneAnalyticsDeviceOptions = this.buildLaneAnalyticsDeviceOptions();
  }

  buildLaneAnalyticsDeviceOptions() {
    const runtimePeers = this.tableRows
      .filter(row => row?.showPeerSummary && row?.peerData && !row.peerData.local)
      .map(row => row.peerData);
    const analytics = this.getLaneAnalyticsData();
    const analyticsPeers = Array.isArray(analytics?.peers) ? analytics.peers : [];
    const apiPeers = this.laneAnalyticsDeviceMacs.map(mac => ({ mac }));
    const candidates = apiPeers.length ? [
      ...apiPeers.map(apiPeer => runtimePeers.find(peer =>
        this.normalizeLaneAnalyticsDeviceOptionValue(peer?.mac) === apiPeer.mac) || apiPeer),
      ...analyticsPeers
    ] : [...runtimePeers, ...analyticsPeers];
    const options = candidates.reduce((items, peer) => {
      const value = this.getLaneAnalyticsPeerFilterValue(peer);
      if (!value || items.some(item => item.value === value)) {
        return items;
      }

      return [
        ...items,
        {
          value,
          label: this.getLaneAnalyticsPeerLabel(peer)
        }
      ];
    }, [] as Array<{ value: string, label: string }>);

    return options.sort((a, b) => a.label.localeCompare(b.label));
  }

  getFilteredLaneAnalyticsDeviceOptions() {
    const search = String(this.laneAnalyticsDeviceSearchText || '').trim().toLowerCase();
    return this.laneAnalyticsDeviceOptions
      .filter(option => !search || option.label.toLowerCase().indexOf(search) > -1 || option.value.indexOf(search) > -1)
      .slice(0, 25);
  }

  getSelectedLaneAnalyticsDeviceOption() {
    const selectedDevice = this.getNormalizedLaneAnalyticsDeviceFilter();
    return this.laneAnalyticsDeviceOptions.find(option => option.value === selectedDevice) || null;
  }

  getSelectedLaneAnalyticsDeviceLabel() {
    return this.getSelectedLaneAnalyticsDeviceOption()?.label || this.selectedLaneAnalyticsDevice || '';
  }

  getCachedLaneAnalyticsTableLinkRows(peer = '') {
    return this.laneAnalyticsTableLinkRowsByPeer[peer] || [];
  }

  getFilteredLaneAnalyticsPeerSamples() {
    const samples = this.getLaneAnalyticsPeerSamples();
    if (!this.isLaneAnalyticsDeviceFilterActive()) {
      return samples;
    }

    return samples.filter(sample => this.isLaneAnalyticsPeerIncluded(sample));
  }

  isLaneAnalyticsDeviceFilterActive() {
    return this.vHubMode && this.isLaneAnalyticsSelectionReady();
  }

  isLaneAnalyticsDeviceSelected() {
    return !!this.getNormalizedLaneAnalyticsDeviceFilter();
  }

  isLaneAnalyticsSelectionReady() {
    return !this.vHubMode || this.isLaneAnalyticsDeviceSelected();
  }

  getLaneAnalyticsSelectedPeerValues() {
    if (!this.vHubMode) {
      return [];
    }

    const selectedDevice = this.getNormalizedLaneAnalyticsDeviceFilter();
    return selectedDevice ? [selectedDevice] : [];
  }

  isLaneAnalyticsPeerIncluded(row: any) {
    const selectedPeers = this.getLaneAnalyticsSelectedPeerValues();
    return selectedPeers.indexOf(this.getLaneAnalyticsPeerFilterValue(row)) > -1;
  }

  getLaneAnalyticsPeerFilterValue(row: any) {
    return this.normalizeLaneAnalyticsDeviceOptionValue(row?.peerMac || row?.mac || this.getLaneAnalyticsPeerLabel(row) || '');
  }

  getNormalizedLaneAnalyticsDeviceFilter() {
    return this.normalizeLaneAnalyticsDeviceOptionValue(this.selectedLaneAnalyticsDevice);
  }

  normalizeLaneAnalyticsDeviceOptionValue(value: any) {
    return String(value || '').trim().toLowerCase();
  }

  trackByLaneAnalyticsOption(index: number, option: any) {
    return option?.value || index;
  }

  trackByLaneAnalyticsRow(index: number, row: any) {
    return `${row?.peerValue || row?.peer || ''}-${row?.laneLabel || row?.lane || index}`;
  }

  trackByLaneAnalyticsLinkRow(index: number, row: any) {
    return `${row?.peer || ''}-${row?.linkLabel || index}`;
  }

  trackByLaneAnalyticsGraph(index: number, graph: any) {
    return graph?.title || index;
  }

  trackByLaneAnalyticsLegendGroup(index: number, group: any) {
    return group?.peerLabel || index;
  }

  trackByLaneAnalyticsLegendItem(index: number, item: any) {
    return item?.laneLabel || index;
  }

  getLaneAnalyticsChartDataFromSamples(samples: any[], metric: any): ChartDataSets[] {
    const labels = this.getLaneAnalyticsPeerSampleLabels(samples);
    const grouped = samples.reduce((groups, sample, sampleIndex) => {
      const lane = this.getLaneAnalyticsSampleLaneLabel(sample);
      const peerLabel = this.getLaneAnalyticsPeerLabel(sample);
      const key = `${peerLabel}||${lane}`;
      const label = this.getLaneAnalyticsSampleLabel(sample, sampleIndex);
      const labelIndex = labels.indexOf(label);
      groups[key] = groups[key] || {
        peerLabel,
        laneLabel: `Lane ${lane}`,
        data: labels.map(() => null)
      };
      if (labelIndex > -1) {
        groups[key].data[labelIndex] = this.getLaneAnalyticsMetricValue(sample, metric);
      }
      return groups;
    }, {} as { [key: string]: { peerLabel: string, laneLabel: string, data: Array<number | null> } });

    return Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => this.compareLaneAnalyticsSeries(a, b))
      .map(series => ({
        label: `${series.peerLabel} ${series.laneLabel}`,
        data: series.data.map(value => value == null ? 0 : value),
        peerLabel: series.peerLabel,
      laneLabel: series.laneLabel
    } as any));
  }

  getVceLaneAnalyticsChartDataForMetric(metric: any): ChartDataSets[] {
    if (!this.vCEMode) {
      return [];
    }

    const samples = [
      ...this.getLaneAnalyticsPeerSamples(),
      ...this.getLaneAnalyticsFlatSamples(),
      ...this.getLaneAnalyticsTimelineLaneSamples()
    ];
    if (!samples.length) {
      return [];
    }

    return this.getMergedVceLaneAnalyticsChartDataFromSamples(samples, metric);
  }

  getMergedVceLaneAnalyticsChartDataFromSamples(samples: any[], metric: any): ChartDataSets[] {
    const labels = this.getLaneAnalyticsPeerSampleLabels(samples);
    const grouped = samples.reduce((groups, sample, sampleIndex) => {
      const lane = this.getLaneAnalyticsSampleLaneLabel(sample);
      const label = this.getLaneAnalyticsSampleLabel(sample, sampleIndex);
      const labelIndex = labels.indexOf(label);
      const key = String(lane || '-').trim().toLowerCase();
      groups[key] = groups[key] || {
        laneLabel: `Lane ${lane}`,
        buckets: labels.map(() => [])
      };

      if (labelIndex > -1) {
        groups[key].buckets[labelIndex].push(sample);
      }

      return groups;
    }, {} as { [key: string]: { laneLabel: string, buckets: any[][] } });

    return Object.keys(grouped)
      .map(key => grouped[key])
      .sort((a, b) => this.getLaneAnalyticsLaneSortValue(a?.laneLabel) - this.getLaneAnalyticsLaneSortValue(b?.laneLabel))
      .map(series => ({
        label: series.laneLabel,
        data: series.buckets.map(bucket => this.getMergedVceLaneAnalyticsChartBucketValue(bucket, metric)),
        peerLabel: '',
        laneLabel: series.laneLabel
      } as any));
  }

  getMergedVceLaneAnalyticsChartBucketValue(samples: any[], metric: any) {
    if (!samples.length) {
      return 0;
    }

    if (['txPackets', 'txBytes', 'rxPackets', 'rxBytes', 'txMbps', 'rxMbps', 'totalMbps', 'totalBytes'].indexOf(metric?.valueKey) > -1) {
      return samples.reduce((total, sample) => total + this.getLaneAnalyticsMetricValue(sample, metric), 0);
    }

    if (metric?.valueKey === 'decodeLatencyP90') {
      return Math.max(...samples.map(sample => this.getLaneAnalyticsMetricValue(sample, metric)));
    }

    const weighted = samples.reduce((total, sample) => {
      const weight = this.toNumber(sample?.tx_packets ?? sample?.['TX Packets']) +
        this.toNumber(sample?.rx_packets ?? sample?.['RX Packets']);
      const value = this.getLaneAnalyticsMetricValue(sample, metric);
      return {
        value: total.value + (value * weight),
        weight: total.weight + weight,
        fallbackValue: total.fallbackValue + value
      };
    }, {
      value: 0,
      weight: 0,
      fallbackValue: 0
    });

    if (weighted.weight > 0) {
      return Number((weighted.value / weighted.weight).toFixed(2));
    }

    return Number((weighted.fallbackValue / samples.length).toFixed(2));
  }

  compareLaneAnalyticsSeries(a: any, b: any) {
    const peerCompare = String(a?.peerLabel || '').localeCompare(String(b?.peerLabel || ''));
    if (peerCompare) {
      return peerCompare;
    }

    return this.getLaneAnalyticsLaneSortValue(a?.laneLabel) - this.getLaneAnalyticsLaneSortValue(b?.laneLabel);
  }

  getLaneAnalyticsLaneSortValue(label: any) {
    const match = String(label || '').match(/(\d+)/);
    return match ? Number(match[1]) : 9999;
  }

  getLaneAnalyticsLegendGroups(series: ChartDataSets[]) {
    const grouped = (series || []).reduce((groups, item: any, index) => {
      // VCE series deliberately use an empty peerLabel so all underlays and lanes
      // share one legend. Only infer a group label when peerLabel is absent.
      const hasPeerLabel = item != null && Object.prototype.hasOwnProperty.call(item, 'peerLabel');
      const peerLabel = hasPeerLabel
        ? String(item.peerLabel || '').trim()
        : this.getLaneAnalyticsPeerFromSeriesLabel(item?.label);
      const groupKey = peerLabel || '-';
      const laneLabel = item?.laneLabel || this.getLaneAnalyticsLaneFromSeriesLabel(item?.label);
      groups[groupKey] = groups[groupKey] || {
        peerLabel,
        laneItems: [],
        linkItems: []
      };
      const legendItem = {
        datasetIndex: index,
        laneLabel: item?.seriesType === 'link'
          ? String(laneLabel || '').replace(/^Link\s+/i, '')
          : laneLabel,
        color: this.getLaneAnalyticsLegendColor(index),
        hidden: !!item?.hidden
      };
      if (item?.seriesType === 'link') {
        groups[groupKey].linkItems.push(legendItem);
      } else {
        groups[groupKey].laneItems.push(legendItem);
      }
      return groups;
    }, {} as { [peerLabel: string]: { peerLabel: string, laneItems: any[], linkItems: any[] } });

    return Object.keys(grouped).map(key => grouped[key]);
  }

  toggleLaneAnalyticsSeries(graph: any, item: any) {
    const graphIndex = this.laneAnalyticsGraphs.indexOf(graph);
    const datasetIndex = Number(item?.datasetIndex);
    const chart = this.laneAnalyticsChartInstances[graphIndex];
    const dataset = chart?.data?.datasets?.[datasetIndex] as any;
    if (!chart || !dataset || !Number.isFinite(datasetIndex)) {
      return;
    }

    item.hidden = !item.hidden;
    dataset.hidden = item.hidden;
    if (graph?.data?.[datasetIndex]) {
      graph.data[datasetIndex].hidden = item.hidden;
    }
    const metadata = chart.getDatasetMeta(datasetIndex);
    if (metadata) {
      metadata.hidden = item.hidden;
    }
    chart.update();
  }

  hasLaneAnalyticsLegendPeerLabel(group: any) {
    const label = String(group?.peerLabel || '').trim();
    return !!label && label !== '-';
  }

  getLaneAnalyticsPeerFromSeriesLabel(label: any) {
    const value = String(label || '');
    if (/^Lane\s+\d+/i.test(value)) {
      return '-';
    }

    return value.replace(/\s+Lane\s+\d+.*$/i, '').trim() || '-';
  }

  getLaneAnalyticsLaneFromSeriesLabel(label: any) {
    const match = String(label || '').match(/Lane\s+\d+.*$/i);
    return match ? match[0] : String(label || '-');
  }

  getLaneAnalyticsLegendColor(index: number) {
    const color: any = this.laneAnalyticsChartColors[index % this.laneAnalyticsChartColors.length] || {};
    return color.borderColor || '#6b7280';
  }

  getLaneAnalyticsFlatSamples() {
    const analytics = this.getLaneAnalyticsData();
    if (Array.isArray(analytics?.lanes)) {
      return [];
    }
    return Array.isArray(analytics?.lists) ? analytics.lists : [];
  }

  getLaneAnalyticsChartDataFromTimelineLaneSamples(metric: any): ChartDataSets[] {
    const samples = this.getLaneAnalyticsTimelineLaneSamples();
    if (!samples.length) {
      return [];
    }

    return this.getLaneAnalyticsChartDataFromSamples(samples, metric);
  }

  getLaneAnalyticsTimelineLaneSamples() {
    const analytics = this.getLaneAnalyticsData();
    const timelines = analytics?.timelines;
    if (!timelines || typeof timelines !== 'object') {
      return [];
    }

    return Object.keys(timelines).reduce((samples, key) => {
      const value = timelines[key];
      if (!Array.isArray(value)) {
        return samples;
      }

      const laneFromKey = this.getLaneAnalyticsLaneFromKey(key);
      return [
        ...samples,
        ...value.map((sample: any, sampleIndex: number) => ({
          ...sample,
          lane: sample?.lane ?? laneFromKey,
          timelineKey: key,
          timelineIndex: sampleIndex
        }))
      ];
    }, [] as any[]);
  }

  getLaneAnalyticsLaneFromKey(key: string) {
    const match = String(key || '').match(/lane[_-\s]*(\d+)/i);
    return match ? Number(match[1]) : key;
  }

  getLaneAnalyticsPeerSampleLabels(samples: any[]) {
    const labels = samples
      .map((sample, sampleIndex) => this.getLaneAnalyticsSampleLabel(sample, sampleIndex))
      .filter(label => !!label);
    return Array.from(new Set<string>(labels));
  }

  getLaneAnalyticsSampleLabel(sample: any, sampleIndex = 0) {
    const generatedAt = sample?.generated_at ? new Date(Number(sample.generated_at) * 1000).toISOString() : '';
    return sample?.generated_date || sample?.window_end || sample?.window_start ||
      sample?.datetime || sample?.timestamp || sample?.date || generatedAt ||
      this.getLaneAnalyticsRangeLabel() || `Sample ${sampleIndex + 1}`;
  }

  getLaneAnalyticsTimezoneLabels(labels: any[]) {
    return (labels || []).map(label => {
      // Telemetry timestamps are UTC; explicit offsets are honored when present.
      const timestamp = moment.utc(String(label || ''), [
        moment.ISO_8601, 'YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm',
        'YYYY-MM-DD h:mm:ss A', 'YYYY-MM-DD h:mm A'
      ], true);
      return timestamp.isValid()
        ? timestamp.tz(this.selectedLaneAnalyticsTimezone).format('YYYY-MM-DD h:mm A')
        : this.formatLaneAnalyticsAxisLabel(label);
    });
  }

  getLaneAnalyticsAxisLabels(labels: any[]) {
    return (labels || []).map(label => this.formatLaneAnalyticsAxisLabel(label));
  }

  formatLaneAnalyticsAxisLabel(label: any) {
    const value = String(label || '');
    const match = value.match(/^(\d{4}-\d{2}-\d{2})[T\s]+(\d{2}):(\d{2})(?::\d{2}(?:\.\d+)?)?\s*(AM|PM)?/i);
    if (match) {
      const hour = Number(match[2]);
      const period = match[4] ? match[4].toUpperCase() : (hour >= 12 ? 'PM' : 'AM');
      return match[1] + ' ' + (hour % 12 || 12) + ':' + match[3] + ' ' + period;
    }

    return value;
  }

  getLaneAnalyticsSampleLaneLabel(sample: any) {
    const lane = sample?.Lane ?? sample?.lane ?? this.getLaneAnalyticsLaneFromKey(sample?.timelineKey) ?? '-';
    const mode = sample?.mode_str || sample?.Mode || sample?.mode || '';
    return this.isDefaultLaneAnalyticsMode(mode) ? `${lane} (default)` : lane;
  }

  getLaneAnalyticsChartDataFromPoints(metric = this.laneAnalyticsMetrics[1]): ChartDataSets[] {
    const points = this.getLaneAnalyticsTimelinePoints();
    if (!points.length) {
      return [];
    }

    const laneKeys = this.getLaneAnalyticsPointKeys(points);
    return laneKeys.map(key => ({
      data: points.map(point => this.getLaneAnalyticsPointValue(point, key, metric)),
      label: this.getLaneAnalyticsSeriesLabel(key)
    }));
  }

  getLaneAnalyticsTimelinePoints() {
    const analytics = this.getLaneAnalyticsData();
    const timeline = this.getSelectedLaneAnalyticsTimeline();
    const pointSources = [
      timeline?.points,
      timeline?.graph,
      timeline?.chart,
      timeline?.series,
      timeline?.items,
      analytics?.points,
      analytics?.graph,
      analytics?.chart,
      analytics?.series
    ];
    return pointSources.find(source => Array.isArray(source)) || [];
  }

  getLaneAnalyticsPointKeys(points: any[]) {
    const dateKeys = ['datetime', 'timestamp', 'date', 'time'];
    const keys = points.reduce((allKeys, point) => {
      if (point?.lanes && typeof point.lanes === 'object') {
        return [
          ...allKeys,
          ...Object.keys(point.lanes)
        ];
      }

      return [
        ...allKeys,
        ...Object.keys(point || {}).filter(key => dateKeys.indexOf(key) === -1 && typeof point[key] !== 'object')
      ];
    }, [] as string[]);

    return Array.from(new Set<string>(keys)).filter((key: string) => key);
  }

  getLaneAnalyticsPointValue(point: any, key: string, metric: any) {
    const lanePoint = point?.lanes?.[key];
    if (lanePoint && typeof lanePoint === 'object') {
      return this.getLaneAnalyticsMetricValue(lanePoint, metric);
    }

    const metricKey = metric.apiKeys.find(apiKey => point?.[`${key} ${apiKey}`] != null || point?.[`${key}_${apiKey}`] != null);
    if (metricKey) {
      const value = this.toNumber(point?.[`${key} ${metricKey}`] ?? point?.[`${key}_${metricKey}`]);
      return metric?.rateFromBytes
        ? this.bytesToMbps(value, this.getLaneAnalyticsSampleDurationSeconds(point))
        : value;
    }

    return this.toNumber(point?.[key]);
  }

  getLaneAnalyticsMetricValue(row: any, metric: any) {
    if (metric?.combinedTraffic) {
      if (metric?.rateFromBytes && (row?.tx_mbps != null || row?.rx_mbps != null)) {
        return this.toNumber(row?.tx_mbps) + this.toNumber(row?.rx_mbps);
      }
      const combinedBytes = this.toNumber(row?.tx_bytes ?? row?.['TX Bytes']) +
        this.toNumber(row?.rx_bytes ?? row?.['RX Bytes']);
      return metric?.rateFromBytes
        ? this.bytesToMbps(combinedBytes, this.getLaneAnalyticsSampleDurationSeconds(row))
        : combinedBytes;
    }

    if (metric?.valueKey === 'txMbps' && row?.tx_mbps != null) {
      return this.toNumber(row.tx_mbps);
    }
    if (metric?.valueKey === 'rxMbps' && row?.rx_mbps != null) {
      return this.toNumber(row.rx_mbps);
    }
    const key = metric.apiKeys.find(apiKey => row?.[apiKey] != null);
    const value = this.toNumber(key ? row?.[key] : row?.[metric.valueKey]);
    return metric?.rateFromBytes
      ? this.bytesToMbps(value, this.getLaneAnalyticsSampleDurationSeconds(row))
      : value;
  }

  getLaneAnalyticsSeriesLabel(key: string) {
    return String(key || '')
      .replace(/\s*Bandwidth$/i, '')
      .replace(/[_-]+/g, ' ')
      .replace(/\blane\s*/i, 'lane')
      .trim();
  }

  getLaneAnalyticsRangeLabel() {
    const selectedRange = this.laneAnalyticsRanges[this.selectedLaneAnalyticsRange] || this.laneAnalyticsRanges[0];
    return selectedRange?.label || '';
  }

  getLaneAnalyticsChartOptions(metric: any = this.laneAnalyticsMetrics[1]) {
    return {
      ...this.laneAnalyticsChartOptions,
      // Match Analytics & Info: inspect the individual point under the pointer.
      tooltips: { mode: 'nearest', intersect: true },
      hover: { mode: 'nearest', intersect: true },
      scales: {
        yAxes: [{
          display: true,
          scaleLabel: { display: true, labelString: metric.axis || metric.label },
          ticks: { beginAtZero: true }
        }]
      }
    };
  }

  getLaneAnalyticsDurationSeconds() {
    const analytics = this.getLaneAnalyticsData();
    const timeline = this.getSelectedLaneAnalyticsTimeline();
    const dateFrom = new Date(timeline?.date_from || analytics?.date_from || '').getTime();
    const dateTo = new Date(timeline?.date_to || analytics?.date_to || '').getTime();
    if (!dateFrom || !dateTo || dateTo <= dateFrom) {
      return this.getLaneAnalyticsBucketSeconds();
    }

    return Math.max((dateTo - dateFrom) / 1000, 1);
  }

  getLaneAnalyticsBucketSeconds() {
    const range = this.getSelectedLaneAnalyticsRange();
    if (range === 'last4hrs') {
      return 4 * 60 * 60;
    }
    if (range === 'last24hrs') {
      return 24 * 60 * 60;
    }
    if (range === 'last7days') {
      return 7 * 24 * 60 * 60;
    }
    return 30 * 24 * 60 * 60;
  }

  bytesToMbps(bytes: any, seconds: number) {
    const value = this.toNumber(bytes);
    const duration = Math.max(Number(seconds) || 1, 1);
    return Number(((value * 8) / duration / 1000000).toFixed(3));
  }

  getLaneAnalyticsSampleDurationSeconds(sample: any) {
    const windowMinutes = this.toNumber(sample?.window_minutes);
    if (windowMinutes > 0) {
      return windowMinutes * 60;
    }

    const windowStart = new Date(sample?.window_start || '').getTime();
    const windowEnd = new Date(sample?.window_end || sample?.generated_date || '').getTime();
    if (windowStart && windowEnd > windowStart) {
      return (windowEnd - windowStart) / 1000;
    }

    return this.getLaneAnalyticsDurationSeconds();
  }

  getAnalyticsLaneRatio(row: any) {
    const k = this.toNumber(row?.k);
    const n = this.toNumber(row?.n);
    if (!k && !n) {
      return '-';
    }

    return `${k}:${Math.max(n - k, 0)}`;
  }

  isDefaultLaneAnalyticsMode(mode: any) {
    return String(mode || '').trim().toLowerCase() === 'default';
  }

  getPeerLanes(peer: any): OverlayPlusLane[] {
    const provision = this.getPeerProvision(peer);
    const runtimeLanes = [
      peer?.qfec_lanes,
      peer?.fec_lanes,
      peer?.lanes,
      provision?.qfec_lanes
    ].find(value => Array.isArray(value));

    if (Array.isArray(runtimeLanes) && runtimeLanes.length) {
      return runtimeLanes;
    }

    const provisionLanes = provision?.fec_lanes || provision?.lanes;
    if (Array.isArray(provisionLanes) && provisionLanes.length) {
      return provisionLanes.map((lane: OverlayPlusLane, index: number) => ({
        ...lane,
        lane: lane?.lane ?? index + 1,
        k: this.getLaneK(lane),
        n: this.getLaneN(lane),
        mode_str: lane?.mode_str || lane?.mode || lane?.class || '-'
      }));
    }

    const qfec = provision?.qfec || provision?.default_qfec;
    if (qfec) {
      return [{
        lane: 'Default',
        default: true,
        orig_dupes: qfec,
        mode_str: 'default',
        k: String(qfec).split(':')[0],
        n: this.getTotalFromOrigDupes(qfec)
      }];
    }

    return [];
  }

  getPeerState(peer: any) {
    const links: OverlayPlusLink[] = Array.isArray(peer?.links) ? peer.links : [];
    if (links.some((link: OverlayPlusLink) => String(link?.state || '').toLowerCase() === 'up')) {
      return 'UP';
    }

    return peer?.connected_sec ? 'UP' : '-';
  }

  getAssignedIpLines(peer: any): AssignedIpLine[] {
    const lines: AssignedIpLine[] = [];
    const dpcIp = this.getPeerDpcIpDisplayValue(peer);
    const dmzValues = [
      peer?.dmz_ip || peer?.dmz_ips,
      peer?.dmz_subnet || peer?.dmz_subnet_ips
    ].filter(Boolean).join(' / ');
    const wanSubnet = peer?.wan_subnet;

    lines.push({
      label: '',
      value: dpcIp,
      primary: true
    });

    if (dmzValues) {
      lines.push({
        label: 'dmz:',
        value: dmzValues,
        primary: false
      });
    }

    if (wanSubnet) {
      lines.push({
        label: 'wan:',
        value: wanSubnet,
        primary: false
      });
    }

    return lines;
  }

  getPeerConfiguredDpcIp(peer: any) {
    const configuredDpcIp = String(peer?.dpc_ip || '').trim();
    if (configuredDpcIp) {
      return configuredDpcIp;
    }

    return peer?.live_dpc_ip ? 'auto' : '';
  }

  getPeerDpcIpDisplayValue(peer: any) {
    const configuredDpcIp = this.getPeerConfiguredDpcIp(peer) || 'auto';
    const liveDpcIp = String(peer?.live_dpc_ip || '').trim();
    if (configuredDpcIp.toLowerCase() === 'auto' && liveDpcIp) {
      return `auto (${liveDpcIp})`;
    }

    return configuredDpcIp;
  }

  normalizePeerDpcIpForEdit(value: any) {
    const dpcIp = String(value || '').trim();
    return /^auto(?:\s*\(|$)/i.test(dpcIp) ? 'auto' : dpcIp;
  }

  getPeerLinkAssurance(peer: any, link: any, index: number) {
    const routeTable = this.getCsvValue(peer?.route_tables || peer?.vce_provision?.route_tables, index);
    const action = link?.action ? ` (${link.action})` : '';
    const latency = this.toNumber(link?.rtt_avg_ms);
    const jitter = this.toNumber(link?.rtt_jitter_ms);
    const loss = this.toNumber(link?.loss_pct ?? link?.post_fec_loss_pct ?? link?.pre_fec_loss_pct);
    const mos = link?.mos || '-';
    const tx = this.formatBytes(link?.tx_bytes);
    const rx = this.formatBytes(link?.rx_bytes);

    return [
      `${routeTable || link?.name || '-'}${action}`,
      `Latency: ${latency}ms | Jitter: ${jitter}ms | Loss: ${loss}% | MOS: ${mos}`,
      `TX: ${tx} RX: ${rx}`
    ];
  }

  buildDeviceOverlayView(teamOrTeams: any | any[], interfaces: any[] = []) {
    const teams = (Array.isArray(teamOrTeams) ? teamOrTeams : [teamOrTeams]).filter(team => !!team);
    this.dpcServiceRows = [];
    this.tableRows = [];
    this.interfaceRows = teams.map(team => this.getVceInterfaceRow(team));
    this.interfaceRow = this.interfaceRows[0] || null;
    this.connectionRows = teams.reduce((rows, team) => [
      ...rows,
      ...this.getVceConnectionRows(team, interfaces)
    ], [] as ConnectionRow[]);
  }

  getVceInterfaceRow(team: any): InterfaceRow {
    const teamConfig = this.getTeamConfig(team);
    const links: OverlayPlusLink[] = Array.isArray(team?.links) ? team.links : [];
    const endpoint = String(teamConfig.remote_addr || this.getPrimaryServer(links) || '').trim().toLowerCase();
    const peer = this.associatedVhubPeers.find(item =>
      endpoint && String(item?.remote_addr || '').trim().toLowerCase() === endpoint
    );
    return {
      iface: teamConfig.iface || team?.name || '-',
      sdwan_link: teamConfig.sdwan_link || '-',
      tunnel_ip: team?.dpc_gateway || teamConfig.dpc_gateway || '-',
      dmz_ip: this.getFirstValue(peer?.dmz_ip, peer?.dmz_ips),
      server_url: teamConfig.remote_addr || this.getPrimaryServer(links),
      dpc_gateway: teamConfig.dpc_gateway || '-',
      ip4_table: teamConfig.iface || this.getIp4Table(teamConfig),
      mtu: teamConfig.mtu || '-',
      max_packet_size: teamConfig.maxpacketsize || '-',
      distribution: teamConfig.distribution || team?.distribution || '-'
    };
  }

  getVceConnectionRows(team: any, interfaces: any[] = []): ConnectionRow[] {
    const teamConfig = this.getTeamConfig(team);
    const links: OverlayPlusLink[] = Array.isArray(team?.links) ? team.links : [];
    const lanes: OverlayPlusLane[] = Array.isArray(team?.qfec_lanes) ? team.qfec_lanes : [];
    const teamName = team?.name || teamConfig?.iface || '-';
    const assignedIp = this.getVceAssignedIpDisplayValue(team, interfaces);
    return [
      ...links.map((link: OverlayPlusLink, index: number) => ({
        link_lane: link?.name || `link${index + 1}`,
        assuranceLines: this.getLinkAssurance(index, teamConfig, link),
        assigned_ips: index === 0 ? assignedIp : '',
        endpoint: index === 0 ? teamConfig?.remote_addr || '-' : '',
        sdwan_link: index === 0 ? teamConfig?.sdwan_link || '-' : '',
        weight: `${this.toNumber(link?.weight)}/${this.toNumber(link?.rx_weight)}`,
        wan_id: index === 0 ? team?.wan_id || '-' : '',
        uptime: index === 0 ? this.formatTotalUptime(team?.uptime_sec, team?.total_uptime_sec) : '',
        state: link?.state || ''
      })),
      ...(!links.length ? [{
        link_lane: teamName,
        assuranceLines: this.getSummaryDetailRow(team).assuranceLines,
        assigned_ips: assignedIp,
        endpoint: teamConfig?.remote_addr || '-',
        sdwan_link: teamConfig?.sdwan_link || '-',
        weight: this.joinCsv(teamConfig?.weights) || '-',
        wan_id: team?.wan_id || '-',
        uptime: this.formatTotalUptime(team?.uptime_sec, team?.total_uptime_sec),
        state: team?.state || team?.status || '-'
      }] : []),
      ...lanes.map((lane: OverlayPlusLane) => ({
        link_lane: this.getLaneLabel(lane),
        assuranceLines: this.getLaneAssurance(lane, teamConfig),
        assigned_ips: '',
        endpoint: '',
        sdwan_link: '',
        weight: lane?.tx_weight != null || lane?.rx_weight != null
          ? `${this.toNumber(lane?.tx_weight)}/${this.toNumber(lane?.rx_weight)}`
          : '',
        wan_id: '',
        uptime: '',
        state: lane?.state || ''
      }))
    ];
  }

  getTeamConfig(team: any) {
    return team?.team_config || team?.teamConfig || team?.vce_provision || {};
  }

  getVceAssignedIpDisplayValue(team: any, interfaces: any[] = []) {
    const teamConfig = this.getTeamConfig(team);
    const configuredDpcIp = this.cleanIpValue(team?.dpc_ip || teamConfig?.dpc_ip) || 'auto';
    const iface = String(team?.iface || teamConfig?.iface || '').trim();
    const runtimeInterface = interfaces.find(item =>
      iface && [item?.ifname, item?.network, item?.iface]
        .some(value => String(value || '').trim() === iface)
    );
    const liveDpcIp = this.cleanIpValue(runtimeInterface?.address);

    if (configuredDpcIp.toLowerCase() === 'auto' && liveDpcIp) {
      return `auto (${liveDpcIp})`;
    }

    return configuredDpcIp;
  }

  stripCidr(value: any) {
    return FormatterHelper.stripCidr(value);
  }

  getPrimaryServer(links: OverlayPlusLink[]) {
    const primaryLink = links.find((link: OverlayPlusLink) => String(link?.action).toLowerCase() === 'primary');
    return primaryLink?.server || links[0]?.server || '-';
  }

  getIp4Table(teamConfig: any) {
    const routeTables = String(teamConfig?.route_tables || '')
      .split(',')
      .map(value => value.trim())
      .filter(Boolean);
    return routeTables[0] || teamConfig?.iface || '-';
  }

  getLinkAssurance(index: number, teamConfig: any, link: OverlayPlusLink) {
    const transport = teamConfig?.transport?.split(',') ?? [];
    const routeTable = this.getCsvValue(teamConfig?.route_tables, index) || link?.name || '-';
    const action = link?.action ? `${link.action}` : 'active';
    const latency = this.toNumber(link?.rtt_avg_ms);
    const jitter = this.toNumber(link?.rtt_jitter_ms);
    const loss = this.toNumber(link?.loss_pct ?? link?.post_fec_loss_pct ?? link?.pre_fec_loss_pct);
    const mos = link?.mos || '-';
    const traffic = this.getTrafficAssuranceLine(link?.tx_bytes, link?.rx_bytes, true);

    return [
      `${routeTable} (${action}) [${transport[index] || '-'}]`,
      `Latency: ${latency}ms | Jitter: ${jitter}ms | Loss: ${loss}% | MOS: ${mos}`,
      traffic
    ].filter(line => !!line);
  }

  getLaneAssurance(lane: OverlayPlusLane, teamConfig?: any) {
    const k = this.getLaneK(lane);
    const n = this.getLaneN(lane);
    const parity = Math.max(this.toNumber(n) - this.toNumber(k), 0);
    const latency = this.toNumber(lane?.decode_latency_avg_ms);
    const jitter = this.toNumber(lane?.jitter_ms);
    const loss = this.toNumber(lane?.loss_pct);
    const mos = lane?.mos || '-';
    const qClass = this.getLaneQClass(lane, teamConfig);
    const qClassLabel = qClass ? ` Q-Class: ${qClass}` : '';
    const traffic = this.getTrafficAssuranceLine(lane?.tx_bytes, lane?.rx_bytes);

    return [
      `${this.toNumber(k)}:${parity} mode=${lane?.mode_str || lane?.mode || '-'}${qClassLabel}`,
      `Latency: ${latency}ms | Jitter: ${jitter}ms | Loss: ${loss}% | MOS: ${mos}`,
      traffic
    ].filter(line => !!line);
  }

  getLaneQClass(lane: OverlayPlusLane, teamConfig: any) {
    if (lane?.default || String(lane?.mode_str || '').toLowerCase() === 'default') {
      return '';
    }
    if (lane?.class != null && String(lane.class).trim()) {
      return String(lane.class).trim();
    }
    if (!teamConfig) {
      return String(lane?.lane || '').trim();
    }
    const laneNumber = String(lane?.lane || '').trim();
    const steer = String(teamConfig?.[`fec_q${laneNumber}_steer`] || '');
    return steer.split(':')[1]?.trim() || '';
  }

  getTrafficAssuranceLine(txBytes: any, rxBytes: any, alwaysShowTx = false) {
    const tx = this.toNumber(txBytes);
    const rx = this.toNumber(rxBytes);
    if (!alwaysShowTx && tx <= 0 && rx <= 0) {
      return '';
    }
    return `TX: ${this.formatMegabytes(tx)}${rx > 0 ? ` RX: ${this.formatMegabytes(rx)}` : ''}`;
  }

  formatMegabytes(bytes: any) {
    return `${(this.toNumber(bytes) / (1024 * 1024)).toFixed(2)} MB`;
  }

  getLaneK(lane: OverlayPlusLane) {
    return lane?.k ?? String(lane?.orig_dupes || lane?.origDupes || '0:0').split(':')[0];
  }

  getLaneN(lane: OverlayPlusLane) {
    return lane?.n ?? this.getTotalFromOrigDupes(lane?.orig_dupes || lane?.origDupes || '0:0');
  }

  getTotalFromOrigDupes(value: any) {
    return FormatterHelper.getTotalFromOrigDupes(value);
  }

  getLaneLabel(lane: OverlayPlusLane) {
    if (lane?.default || String(lane?.mode_str || '').toLowerCase() === 'default') {
      return 'Lane (Default)';
    }
    return `Lane ${lane?.lane ?? '-'}`;
  }

  getSummaryDetailRow(team: any): SummaryDetailRow {
    const teamConfig = this.getTeamConfig(team);
    const teamName = team?.name || teamConfig?.iface || '-';
    const wanLabel = teamConfig?.sdwan_link || team?.active_link || 'local';
    const modeLabel = team?.mode || '-';
    const statusLabel = team?.status || '-';
    const versionLabel = team?.version || '-';
    const uptimeLabel = this.formatDuration(team?.uptime_sec);
    const transport = this.joinCsv(teamConfig?.transport) || '-';
    const weight = this.joinCsv(teamConfig?.weights) || '-';
    const latency = this.toNumber(team?.fec_dec?.decode_latency_avg_ms);
    const jitter = this.toNumber(team?.fec_dec?.jitter_ms);
    const loss = this.toNumber(team?.fec_dec?.loss_pct);
    const mos = team?.fec_dec?.mos || '-';

    return {
      linkLane: teamName,
      assuranceLines: [
        `${wanLabel} (${modeLabel})`,
        `Status: ${statusLabel} | Version: ${versionLabel} | Uptime: ${uptimeLabel}`,
        `Latency: ${latency}ms | Jitter: ${jitter}ms | Loss: ${loss}% | MOS: ${mos}`,
        `Transport: ${transport} | Weight: ${weight}`
      ],
      weight,
      state: team?.state || team?.status || '-'
    };
  }

  cleanIpValue(value: any) {
    return FormatterHelper.cleanIpValue(value);
  }

  joinCsv(value: any) {
    return FormatterHelper.joinCsv(value);
  }

  getCsvValue(value: any, index: number) {
    return FormatterHelper.getCsvValue(value, index);
  }

  formatBytes(bytes: number | string | undefined, decimals = 2) {
    return FormatterHelper.formatBytesShort(bytes, decimals);
  }

  formatDuration(seconds: number) {
    return FormatterHelper.formatDuration(seconds);
  }

  formatTotalUptime(uptimeSeconds: number, totalUptimeSeconds: number) {
    return FormatterHelper.formatTotalUptime(uptimeSeconds, totalUptimeSeconds);
  }

  toNumber(value: any) {
    return FormatterHelper.toNumber(value);
  }

  getStateClass(state: string) {
    return FormatterHelper.getStateClass(state);
  }

  getStateLabel(state: string) {
    return FormatterHelper.getStateLabel(state);
  }
}
